import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { decryptFinancePayload, isAnalyticsAmountApiUrl } from "../utils/financeAmountCrypto";
import { amountInWordsInr, formatInr, formatInrDecimal, formatInrWithWords, formatPaiseAsInr } from "../utils/financeFormat";
import { Alert, Button, Form, Spinner, Table, Modal } from "react-bootstrap";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { Context } from "../App";
import AdminLayout from "../components/AdminLayout";
import AdminDateInput from "../components/AdminDateInput";
import { RichLineChart, RichGroupedBarChart } from "../components/RichChartWidgets";
import * as Constants from "../api/constants";
import "../adminConsoleTheme.css";
import "../bookingAnalyticsLounge.css";
import { useAnalyticsSessionGuard } from "../hooks/useAnalyticsSessionGuard";
import MonthlyCostTabShell, { MonthlyMetricTrend } from "../components/analytics/MonthlyCostTabShell";
import AnalyticsStatHero from "../components/analytics/AnalyticsStatHero";
import ProfitAnalyticsPanel from "../components/analytics/ProfitAnalyticsPanel";
import BusinessTrendPanel from "../components/analytics/BusinessTrendPanel";
import AnalyticsLoungeDashboard from "../components/analytics/AnalyticsLoungeDashboard";
import VendorPayableMonthlyTab from "../components/analytics/VendorPayableMonthlyTab";
import MeatMonthlyTab from "../components/analytics/MeatMonthlyTab";
import MonthlyCashPaymentPanel from "../components/analytics/MonthlyCashPaymentPanel";
import MonthlyCashIncomeTab from "../components/analytics/MonthlyCashIncomeTab";
import MonthlyCashSummaryTab from "../components/analytics/MonthlyCashSummaryTab";
import MonthlyBankCashHero from "../components/analytics/MonthlyBankCashHero";
import TableTotalAmount, { ANALYTICS_TABLE_TOTAL_ROW_CLASS } from "../components/analytics/TableTotalAmount";
import AnalyticsSubsectionLayout, {
  AnalyticsSubsectionPanel,
} from "../components/analytics/AnalyticsSubsectionLayout";
import SiteThemePicker from "../components/SiteThemePicker";
import { useSiteTheme } from "../context/SiteThemeContext";

const ROLE_HEADER = "X-Admin-Role";
const SUPER_ADMIN = "SUPER_ADMIN";

const ANALYTICS_SESSION_STORAGE_TOKEN = "bookingAnalyticsSessionToken";
const ANALYTICS_SESSION_STORAGE_EMAIL = "bookingAnalyticsSessionEmail";
function sumSalaryPaidRupee(salaryRows, monthlyOverview) {
  const fromOverview = Number(monthlyOverview?.salaryUploadRupee);
  if (Number.isFinite(fromOverview) && fromOverview > 0) return fromOverview;
  return (salaryRows || []).reduce((s, r) => s + (Number(r.paidSalaryRupee) || 0), 0);
}

function analyticsPrivacyClass(privacyBlur) {
  return privacyBlur ? "analytics-privacy-scope analytics-privacy-blur" : "analytics-privacy-scope";
}

function axiosErrorMessage(e, fallback) {
  const body = e?.response?.data;
  if (body?.message) return body.message;
  if (typeof body === "string" && body.trim()) return body.trim();
  if (body?.detail) return body.detail;
  return e?.message || fallback;
}

function MonthlyCostCashPanel({
  slotId,
  label,
  hint,
  cashBySlot,
  cashLinesBySlot,
  monthlyCashBusy,
  onAddCashLine,
  onDeleteCashLine,
}) {
  return (
    <MonthlyCashPaymentPanel
      slotId={slotId}
      label={label}
      hint={hint}
      lines={cashLinesBySlot[slotId] || []}
      totalRupee={cashBySlot[slotId] || 0}
      saving={monthlyCashBusy}
      onAddLine={onAddCashLine}
      onDeleteLine={onDeleteCashLine}
    />
  );
}

function formatCancelledAtIst(ms) {
  try {
    return new Intl.DateTimeFormat("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: "Asia/Kolkata",
    }).format(new Date(Number(ms)));
  } catch {
    return "—";
  }
}

function rowAmountRupee(r) {
  return Number(r?.amountRupee ?? r?.debitRupee ?? r?.creditRupee) || 0;
}

function bankPlusCash(bank, cash) {
  return (Number(bank) || 0) + (Number(cash) || 0);
}

function filterFinanceRows(rows, { categoryField, categoryValue, minRupee, searchText }) {
  const min = Number(minRupee) || 0;
  const q = String(searchText || "").trim().toLowerCase();
  return (rows || []).filter((r) => {
    if (categoryValue && categoryValue !== "ALL") {
      const cat = r[categoryField] || r.category || r.vendorLabel || "";
      if (cat !== categoryValue) return false;
    }
    const amt = rowAmountRupee(r);
    if (min > 0 && amt < min) return false;
    if (q) {
      const hay = `${r.category || ""} ${r.vendorLabel || ""} ${r.particulars || ""} ${r.empName || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

function formatRupeeMaybe(n) {
  if (n === null || n === undefined) return "—";
  return formatInr(n);
}

/** KPI card: large ₹ figure with smaller in-words line underneath. */
function AmountWithWords({ amount, valueClassName = "" }) {
  const words = amountInWordsInr(amount);
  return (
    <>
      <div className={`fs-5 fw-bold ${valueClassName}`.trim()}>{formatInr(amount)}</div>
      {words && words !== "Zero" ? <div className="analytics-amount-words">{words}</div> : null}
    </>
  );
}

/** ISO date yyyy-mm-dd → dd-MM-yyyy for display */
function formatIsoDateIn(d) {
  if (!d || typeof d !== "string") return "—";
  const parts = d.split("-");
  if (parts.length !== 3) return d;
  const [y, m, day] = parts;
  return `${day}-${m}-${y}`;
}

/** Prepare edit form defaults from booking line API payload */
function banquetLineToEditDraft(ln) {
  const iso =
    ln?.eventDate == null
      ? ""
      : typeof ln.eventDate === "string"
        ? ln.eventDate.slice(0, 10)
        : "";
  const nstr = (v) => (v != null && v !== "" ? String(v) : "");
  return {
    id: ln?.id,
    eventDate: iso,
    serialNo: nstr(ln?.serialNo),
    packageName: ln?.packageName || "",
    slotDesc: ln?.slotDesc || "",
    numPax: nstr(ln?.numPax),
    advanceRupee: nstr(ln?.advanceRupee),
    balanceRupee: nstr(ln?.balanceRupee),
    totalAmountRupee: nstr(ln?.totalAmountRupee),
    bankPaymentRupee: nstr(ln?.bankPaymentRupee),
    cashPaymentRupee: nstr(ln?.cashPaymentRupee),
    status: ln?.status || "",
    remarks: ln?.remarks || "",
    decorExpensesRupee: nstr(ln?.decorExpensesRupee),
    decorCostRupee: nstr(ln?.decorCostRupee),
    prePlateCostRupee: nstr(ln?.prePlateCostRupee),
    customerName: ln?.customerName || "",
    customerEmail: ln?.customerEmail || "",
    customerPhone: ln?.customerPhone || "",
    cancelled: Boolean(ln?.cancelled),
  };
}

function formatSignedBookingDelta(n) {
  if (n === null || n === undefined) return "—";
  const v = Number(n);
  if (v >= 0) return `+${v}`;
  return String(v);
}

function formatSignedRevenueDelta(n) {
  if (n === null || n === undefined) return "—";
  const v = Number(n);
  const prefix = v >= 0 ? "+" : "−";
  return `${prefix}${formatInr(Math.abs(v))}`;
}

function yyyyMmToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function sumRentMaint(overview) {
  return (Number(overview?.rent1Rupee) || 0) + (Number(overview?.rent2Rupee) || 0) + (Number(overview?.maintenanceRupee) || 0);
}

function BankCreditChannelTabBody({
  title,
  narrationHint,
  totalRupee,
  rows,
  comparison,
  trendLabel,
  valueFn,
  periodMonth,
}) {
  const list = rows || [];
  return (
    <>
      <MonthlyMetricTrend metricLabel={trendLabel || title} valueFn={valueFn} comparison={comparison} />
      <AnalyticsStatHero
        label={periodMonth ? `${title} — ${periodMonth}` : title}
        amountRupee={totalRupee}
        valueClassName="text-info"
        meta={
          <>
            <div>{narrationHint}</div>
            {list.length ? (
              <div>
                Credits: <strong className="text-light">{list.length}</strong>
              </div>
            ) : null}
          </>
        }
      />
      <div className="finance-recon-scroll">
        <Table striped bordered hover size="sm" variant="dark" responsive className="mb-0">
          <thead>
            <tr>
              <th>Date</th>
              <th>Bank</th>
              <th>Particulars</th>
              <th className="text-end">Credit ₹</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-secondary small">
                  No matching bank credits this month — upload ICICI/IDFC statements.
                </td>
              </tr>
            ) : (
              <>
                {list.map((r, idx) => (
                  <tr key={`${title}-cr-${r.txnDateIso}-${idx}`}>
                    <td>{r.txnDateIso}</td>
                    <td>{r.bank}</td>
                    <td className="small">{r.particulars}</td>
                    <td className="text-end text-success">{formatInr(r.creditRupee)}</td>
                  </tr>
                ))}
                <tr className={ANALYTICS_TABLE_TOTAL_ROW_CLASS}>
                  <td colSpan={3}>Total ({list.length} credits)</td>
                  <td className="text-end fs-6">
                    <TableTotalAmount amount={totalRupee} toneClass="text-success" />
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </Table>
      </div>
    </>
  );
}

function lastDayOfMonth(y, mZeroBased) {
  return new Date(y, mZeroBased + 1, 0).getDate();
}

function isoMonthToRange(monthStr) {
  if (!monthStr || !/^\d{4}-\d{2}$/.test(monthStr)) {
    const t = yyyyMmToday();
    return isoMonthToRange(t);
  }
  const [y, m] = monthStr.split("-").map(Number);
  const ld = lastDayOfMonth(y, m - 1);
  return {
    fromIso: `${monthStr}-01`,
    toIso: `${monthStr}-${String(ld).padStart(2, "0")}`,
  };
}

function sortPeriodKeysAscending(rows) {
  return [...rows].sort((a, b) => String(a.periodKey).localeCompare(String(b.periodKey)));
}

/** Past/current months newest-first, then any future months ascending (scheduled events). */
function filterMonthlyRows(rows, fromKey, toKey) {
  if (!rows?.length || !fromKey || !toKey) return [];
  const todayKey = yyyyMmToday();
  const filtered = rows.filter((r) => r.periodKey >= fromKey && r.periodKey <= toKey);
  const pastAndCurrent = filtered
    .filter((r) => r.periodKey <= todayKey)
    .sort((a, b) => b.periodKey.localeCompare(a.periodKey));
  const future = filtered
    .filter((r) => r.periodKey > todayKey)
    .sort((a, b) => a.periodKey.localeCompare(b.periodKey));
  return [...pastAndCurrent, ...future];
}

/** SUPER_ADMIN: booking / cancellation / revenue trends (Asia/Kolkata buckets by event date). */
export default function AdminBookingAnalytics() {
  const navigate = useNavigate();
  const { context } = useContext(Context);
  const { themeId, setThemeId } = useSiteTheme();
  const role = String(context?.adminRole || "").toUpperCase();

  const [granularity, setGranularity] = useState("monthly");
  const [tableMonths, setTableMonths] = useState(36);
  const [tableFromKey, setTableFromKey] = useState("");
  const [tableToKey, setTableToKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);

  const [analyticsToken, setAnalyticsToken] = useState(
    () => sessionStorage.getItem(ANALYTICS_SESSION_STORAGE_TOKEN) ?? ""
  );

  const { privacyBlur } = useAnalyticsSessionGuard({
    enabled: role === SUPER_ADMIN && Boolean(analyticsToken),
    inactivityMs: 180000,
    onExpire: () => {
      setAnalyticsToken("");
      setVerifiedEmailLabel("");
      setFinanceErr("Analytics session expired — verify OTP again.");
    },
  });

  const [otpEmailInput, setOtpEmailInput] = useState(
    () => sessionStorage.getItem(ANALYTICS_SESSION_STORAGE_EMAIL) ?? ""
  );
  const [verifiedEmailLabel, setVerifiedEmailLabel] = useState(
    () => sessionStorage.getItem(ANALYTICS_SESSION_STORAGE_EMAIL) ?? ""
  );
  const [otpCodeInput, setOtpCodeInput] = useState("");
  const [otpBusy, setOtpBusy] = useState(false);
  const [otpFlash, setOtpFlash] = useState("");
  const [otpRegisteredEmails, setOtpRegisteredEmails] = useState([]);
  const [otpTargetEmail, setOtpTargetEmail] = useState("");
  const [accessEmails, setAccessEmails] = useState([]);
  const [registerEmailDraft, setRegisterEmailDraft] = useState("");
  const [accessBusy, setAccessBusy] = useState(false);

  const [bizTab, setBizTab] = useState("partyboss");
  const [partybossView, setPartybossView] = useState("overview");
  const [banquetView, setBanquetView] = useState("rollup");
  const [profitView, setProfitView] = useState("summary");
  const [financeErr, setFinanceErr] = useState("");
  const [financeMsg, setFinanceMsg] = useState("");

  const [banquetRows, setBanquetRows] = useState([]);
  const [banquetUploadBusy, setBanquetUploadBusy] = useState(false);
  const [banquetLinesMonth, setBanquetLinesMonth] = useState(() => yyyyMmToday());
  const [banquetBookingLines, setBanquetBookingLines] = useState([]);
  const [banquetLinesBusy, setBanquetLinesBusy] = useState(false);
  const [banquetEditOpen, setBanquetEditOpen] = useState(false);
  const [banquetEditBusy, setBanquetEditBusy] = useState(false);
  const [banquetEditDraft, setBanquetEditDraft] = useState(null);

  const [expensePickMonth, setExpensePickMonth] = useState(() => yyyyMmToday());
  const [expensePresets, setExpensePresets] = useState([]);
  const [dailyRows, setDailyRows] = useState([]);
  const [dailyBusy, setDailyBusy] = useState(false);
  const [dexDate, setDexDate] = useState(() => isoMonthToRange(yyyyMmToday()).fromIso);
  const [dexCategory, setDexCategory] = useState("");
  const [dexAmount, setDexAmount] = useState("");
  const [dexEnteredBy, setDexEnteredBy] = useState("");
  const [dexNotes, setDexNotes] = useState("");
  const [dexSaveBusy, setDexSaveBusy] = useState(false);

  const [maintPickMonth, setMaintPickMonth] = useState(() => yyyyMmToday());

  const [profitWindowMonths, setProfitWindowMonths] = useState(36);
  const [profitPack, setProfitPack] = useState(null);
  const [executiveSummary, setExecutiveSummary] = useState(null);
  const [executiveSummaryBusy, setExecutiveSummaryBusy] = useState(false);
  const [cashflowChart, setCashflowChart] = useState(null);
  const [profitBusy, setProfitBusy] = useState(false);

  // Uploads & reconciliation tab state
  const [uploadBusyKey, setUploadBusyKey] = useState("");
  const [uploadLastResult, setUploadLastResult] = useState(null);
  const [reconPeriod, setReconPeriod] = useState(() => yyyyMmToday());
  const [reconBank, setReconBank] = useState("");
  const [reconLedger, setReconLedger] = useState([]);
  const [reconLedgerBusy, setReconLedgerBusy] = useState(false);
  const [reconSummary, setReconSummary] = useState(null);
  const [reconRules, setReconRules] = useState([]);
  const [reconRuleDraft, setReconRuleDraft] = useState({
    ruleKind: "LAND_OWNER_RENT",
    bank: "",
    direction: "DR",
    expectedAmountRupee: "",
    amountToleranceRupee: "",
    particularsRegex: "",
    category: "",
    priority: 100,
    enabled: true,
    notes: "",
  });
  const [reconRuleBusy, setReconRuleBusy] = useState(false);
  const [stockBundle, setStockBundle] = useState(null);
  const [stockBusy, setStockBusy] = useState(false);
  const [stockDrillCategory, setStockDrillCategory] = useState("");
  const [stockDrillRows, setStockDrillRows] = useState([]);

  const [vendorIncomeRows, setVendorIncomeRows] = useState([]);
  const [vendorIncomeBusy, setVendorIncomeBusy] = useState(false);
  const [dayendMatchRows, setDayendMatchRows] = useState([]);
  const [dayendMatchBusy, setDayendMatchBusy] = useState(false);
  const [salaryMonths, setSalaryMonths] = useState([]);
  const [salaryPick, setSalaryPick] = useState("");
  const [salaryRows, setSalaryRows] = useState([]);
  const [salaryBusy, setSalaryBusy] = useState(false);
  const [salaryAdvanceSummary, setSalaryAdvanceSummary] = useState(null);
  const [partnerShare, setPartnerShare] = useState(null);
  const [partnerGranularity, setPartnerGranularity] = useState("QUARTERLY");
  const [partnerShareBusy, setPartnerShareBusy] = useState(false);
  const [partnerSettings, setPartnerSettings] = useState({ partner1SharePercent: 30, partner2SharePercent: 30, businessRetentionPercent: 40, partner1Label: "Partner 1", partner2Label: "Partner 2" });
  const [partnerSettingsBusy, setPartnerSettingsBusy] = useState(false);
  const [hyperItems, setHyperItems] = useState([]);
  const [hyperItemsBusy, setHyperItemsBusy] = useState(false);
  const [hyperOrders, setHyperOrders] = useState([]);
  const [hyperOrdersBusy, setHyperOrdersBusy] = useState(false);
  const [selectedHyperOrderId, setSelectedHyperOrderId] = useState("");
  const [hyperOrderLines, setHyperOrderLines] = useState([]);
  const [hyperOrderLinesBusy, setHyperOrderLinesBusy] = useState(false);
  const [hyperOrdersTableOpen, setHyperOrdersTableOpen] = useState(true);
  const [hyperLinesTableOpen, setHyperLinesTableOpen] = useState(false);
  const [hyperCompare, setHyperCompare] = useState(null);
  const [hyperCompareMonths, setHyperCompareMonths] = useState(6);
  const [hyperCompareBusy, setHyperCompareBusy] = useState(false);
  const [plasticLedger, setPlasticLedger] = useState(null);
  const [plasticLedgerBusy, setPlasticLedgerBusy] = useState(false);
  const [plasticExpandedOrderIds, setPlasticExpandedOrderIds] = useState(() => new Set());
  const [plasticSettings, setPlasticSettings] = useState({ openingBalanceRupee: 10383, openingBalanceDateIso: "2026-05-09" });
  const [plasticSettingsBusy, setPlasticSettingsBusy] = useState(false);
  const [plasticReplaceAll, setPlasticReplaceAll] = useState(false);
  const [coalLedger, setCoalLedger] = useState(null);
  const [coalLedgerBusy, setCoalLedgerBusy] = useState(false);
  const [coalSettings, setCoalSettings] = useState({ openingBalanceRupee: 0, openingBalanceDateIso: "" });
  const [coalSettingsBusy, setCoalSettingsBusy] = useState(false);
  const [dairyLedger, setDairyLedger] = useState(null);
  const [dairyLedgerBusy, setDairyLedgerBusy] = useState(false);
  const [dairySettings, setDairySettings] = useState({ openingBalanceRupee: 0, openingBalanceDateIso: "" });
  const [dairySettingsBusy, setDairySettingsBusy] = useState(false);
  const [iceCreamLedger, setIceCreamLedger] = useState(null);
  const [iceCreamLedgerBusy, setIceCreamLedgerBusy] = useState(false);
  const [iceCreamSettings, setIceCreamSettings] = useState({ openingBalanceRupee: 0, openingBalanceDateIso: "" });
  const [iceCreamSettingsBusy, setIceCreamSettingsBusy] = useState(false);
  const [vendorDebtSummary, setVendorDebtSummary] = useState(null);
  const [vendorDebtBusy, setVendorDebtBusy] = useState(false);
  const [monthlyCash, setMonthlyCash] = useState(null);
  const [monthlyCashBusy, setMonthlyCashBusy] = useState(false);
  const [cashIncome, setCashIncome] = useState(null);
  const [cashIncomeBusy, setCashIncomeBusy] = useState(false);
  const [advEntryName, setAdvEntryName] = useState("");
  const [advEntryAmount, setAdvEntryAmount] = useState("");
  const [advRecoveryAmount, setAdvRecoveryAmount] = useState("");
  const [advEntryBusy, setAdvEntryBusy] = useState(false);
  const [petpoojaMonthly, setPetpoojaMonthly] = useState([]);
  const [petpoojaMonthlyBusy, setPetpoojaMonthlyBusy] = useState(false);
  /** Sub-view inside Uploads & Recon: all-uploads | ledger | rules | stock | hyper | plastic | vendors | dayend | petpooja | salary */
  const [uploadReconView, setUploadReconView] = useState("all-uploads");
  const [monthlyCostsView, setMonthlyCostsView] = useState("rent");
  const [monthlyOverview, setMonthlyOverview] = useState(null);
  const [monthlyOverviewBusy, setMonthlyOverviewBusy] = useState(false);
  const [monthlyComparison, setMonthlyComparison] = useState(null);
  const [comparisonMonths, setComparisonMonths] = useState(6);
  const [comparisonBusy, setComparisonBusy] = useState(false);
  const [hyperLookbackMonths, setHyperLookbackMonths] = useState(12);
  const [hyperGmailStatus, setHyperGmailStatus] = useState(null);
  const [bankOutFilterCat, setBankOutFilterCat] = useState("ALL");
  const [bankOutFilterMin, setBankOutFilterMin] = useState("");
  const [bankOutSearch, setBankOutSearch] = useState("");
  const [creditFilterVendor, setCreditFilterVendor] = useState("ALL");
  const [creditFilterMin, setCreditFilterMin] = useState("");
  const [creditSearch, setCreditSearch] = useState("");

  const adminOnlyHeaders = useMemo(
    () => ({
      [ROLE_HEADER]: role,
      "X-Admin-User": context?.adminUserName || "",
    }),
    [role, context?.adminUserName]
  );

  const analyticsHeaders = useMemo(
    () => ({
      ...adminOnlyHeaders,
      ...(analyticsToken ? { [Constants.ANALYTICS_SESSION_HEADER]: analyticsToken } : {}),
    }),
    [adminOnlyHeaders, analyticsToken]
  );

  useEffect(() => {
    if (!analyticsToken) return undefined;
    const interceptorId = axios.interceptors.response.use(async (response) => {
      const url = response?.config?.url || "";
      if (isAnalyticsAmountApiUrl(url) && response?.data != null) {
        response.data = await decryptFinancePayload(response.data, analyticsToken);
      }
      return response;
    });
    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, [analyticsToken]);

  useEffect(() => {
    if (!context.isAdminLoggedIn) {
      navigate("/admin", { replace: true });
      return;
    }
    if (role !== SUPER_ADMIN) {
      navigate("/admin/dashboard", { replace: true });
    }
  }, [context.isAdminLoggedIn, role, navigate]);

  const refreshAccessEmails = useCallback(async () => {
    if (role !== SUPER_ADMIN) return;
    try {
      const { data } = await axios.get(Constants.ADMIN_BOOKING_ANALYTICS_ACCESS_EMAILS, {
        headers: adminOnlyHeaders,
      });
      setAccessEmails(Array.isArray(data) ? data : []);
    } catch {
      setAccessEmails([]);
    }
  }, [role, adminOnlyHeaders]);

  useEffect(() => {
    if (role !== SUPER_ADMIN || analyticsToken) return undefined;
    (async () => {
      try {
        const { data } = await axios.get(Constants.ADMIN_BOOKING_ANALYTICS_OTP_CONTEXT, {
          headers: adminOnlyHeaders,
        });
        const registered = Array.isArray(data?.registeredEmails) ? data.registeredEmails : [];
        setOtpRegisteredEmails(registered);
        if (data?.defaultEmail && !sessionStorage.getItem(ANALYTICS_SESSION_STORAGE_EMAIL)) {
          setOtpEmailInput(String(data.defaultEmail));
        } else if (registered.length === 1) {
          setOtpEmailInput((prev) => prev.trim() || String(registered[0]));
        }
      } catch {
        setOtpRegisteredEmails([]);
      }
    })();
    return undefined;
  }, [role, analyticsToken, adminOnlyHeaders]);

  const resolveOtpEmail = useCallback(() => {
    return (otpTargetEmail || otpEmailInput || sessionStorage.getItem(ANALYTICS_SESSION_STORAGE_EMAIL) || "").trim();
  }, [otpEmailInput, otpTargetEmail]);

  const sendAnalyticsOtp = useCallback(async () => {
    setOtpBusy(true);
    setError("");
    setOtpFlash("");
    try {
      await axios.post(Constants.ADMIN_BOOKING_ANALYTICS_OTP_SEND, {}, { headers: adminOnlyHeaders });
      setOtpTargetEmail("");
      setOtpFlash(
        otpRegisteredEmails.length > 1
          ? `The same code was emailed to all ${otpRegisteredEmails.length} registered inboxes — check yours (and spam).`
          : "If your inbox is registered, a code was sent — check email and spam."
      );
    } catch (e) {
      setError(axiosErrorMessage(e, "Send failed."));
    } finally {
      setOtpBusy(false);
    }
  }, [adminOnlyHeaders, otpRegisteredEmails.length]);

  useEffect(() => {
    if (role !== SUPER_ADMIN || !analyticsToken || bizTab !== "register-otp") return undefined;
    refreshAccessEmails();
    return undefined;
  }, [role, analyticsToken, bizTab, refreshAccessEmails]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(Constants.ADMIN_BOOKING_ANALYTICS, {
        headers: analyticsHeaders,
        params: { granularity, tableMonths },
      });
      setPayload(data);
    } catch (e) {
      setPayload(null);
      if (e?.response?.status === 401) {
        sessionStorage.removeItem(ANALYTICS_SESSION_STORAGE_TOKEN);
        setAnalyticsToken("");
        setVerifiedEmailLabel("");
        setError("Analytics session expired — verify OTP again.");
      } else if (e?.response?.status === 403) {
        setError("Access denied — SUPER_ADMIN only.");
      } else {
        setError(e?.response?.data?.message || e?.message || "Could not load analytics.");
      }
    } finally {
      setLoading(false);
    }
  }, [analyticsHeaders, granularity, tableMonths]);

  useEffect(() => {
    if (role !== SUPER_ADMIN || !analyticsToken) return undefined;
    load();
    return undefined;
  }, [role, analyticsToken, load]);

  /** Default table range: last 12 calendar months through current month (not future-only window). */
  useEffect(() => {
    const rows = payload?.monthlyBookingTable;
    if (!rows?.length) {
      setTableFromKey("");
      setTableToKey("");
      return;
    }
    const todayKey = yyyyMmToday();
    const keys = [...new Set(rows.map((r) => r.periodKey))].sort();
    const throughKey = keys.includes(todayKey)
      ? todayKey
      : keys.filter((k) => k <= todayKey).pop() || todayKey;
    const pastKeys = keys.filter((k) => k <= throughKey);
    const fromIdx = Math.max(0, pastKeys.length - 12);
    setTableFromKey(pastKeys[fromIdx] || pastKeys[0] || keys[0]);
    setTableToKey(throughKey);
  }, [payload]);

  const monthlyBookingTable = payload?.monthlyBookingTable || [];
  const activeFutureMonthlyTable = payload?.activeFutureMonthlyTable || [];
  const theatresByCalendarMonth = payload?.theatresByCalendarMonth || [];

  const minPeriodKey =
    monthlyBookingTable.length > 0
      ? [...monthlyBookingTable].sort((a, b) => a.periodKey.localeCompare(b.periodKey))[0]?.periodKey
      : "";
  const maxPeriodKey =
    monthlyBookingTable.length > 0
      ? [...monthlyBookingTable].sort((a, b) => b.periodKey.localeCompare(a.periodKey))[0]?.periodKey
      : "";

  const filteredMonthlyDesc = filterMonthlyRows(monthlyBookingTable, tableFromKey, tableToKey);

  const todayPeriodKey = yyyyMmToday();
  const filteredActiveFutureDesc = [...activeFutureMonthlyTable].sort((a, b) =>
    a.periodKey.localeCompare(b.periodKey)
  );

  const filteredTheatreBundlesNewestFirst = theatresByCalendarMonth
    .filter((b) => tableFromKey && tableToKey && b.periodKey >= tableFromKey && b.periodKey <= tableToKey)
    .sort((a, b) => {
      const todayKey = yyyyMmToday();
      const aFuture = a.periodKey > todayKey;
      const bFuture = b.periodKey > todayKey;
      if (aFuture !== bFuture) return aFuture ? 1 : -1;
      if (aFuture) return a.periodKey.localeCompare(b.periodKey);
      return b.periodKey.localeCompare(a.periodKey);
    });

  const filteredCancellationDetails = useMemo(() => {
    if (!tableFromKey || !tableToKey) return [];
    const cancellationRowsRaw = payload?.cancellationDetailRows || [];
    return cancellationRowsRaw
      .filter(
        (r) => r.calendarMonthPeriodKey >= tableFromKey && r.calendarMonthPeriodKey <= tableToKey
      )
      .sort((a, b) => b.cancellationTimeMillis - a.cancellationTimeMillis);
  }, [payload?.cancellationDetailRows, tableFromKey, tableToKey]);

  const selectedHyperOrderSummary = useMemo(
    () => hyperOrders.find((r) => r.orderId === selectedHyperOrderId) || null,
    [hyperOrders, selectedHyperOrderId]
  );

  const hyperOrderLinesTotalRupee = useMemo(
    () => (hyperOrderLines || []).reduce((sum, ln) => sum + (Number(ln.amountRupee) || 0), 0),
    [hyperOrderLines]
  );

  const hyperOrderLinesTotalGstRupee = useMemo(
    () =>
      (hyperOrderLines || []).reduce((sum, ln) => sum + (Number(ln.gstAmountRupee) || 0), 0),
    [hyperOrderLines]
  );

  const series = payload?.series || [];
  const labels = series.map((b) => b.label);
  const bookingCounts = series.map((b) => b.bookings);
  const cancelledCounts = series.map((b) => b.cancelled);
  const revenues = series.map((b) => Number(b.revenue) || 0);

  const mom = payload?.monthOverMonth;

  const handleFinanceUnauthorized = useCallback(() => {
    sessionStorage.removeItem(ANALYTICS_SESSION_STORAGE_TOKEN);
    setAnalyticsToken("");
    setVerifiedEmailLabel("");
    setError("Analytics session expired — verify OTP again.");
    setFinanceErr("");
  }, []);

  const loadBanquetMonthly = useCallback(async () => {
    if (role !== SUPER_ADMIN || !analyticsToken) return;
    setFinanceErr("");
    try {
      const { data } = await axios.get(Constants.ADMIN_FINANCE_BANQUET_MONTHLY, { headers: analyticsHeaders });
      setBanquetRows(Array.isArray(data) ? data : []);
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
      else setFinanceErr(e?.response?.data?.message || e?.message || "Could not load banquet totals.");
      setBanquetRows([]);
    }
  }, [role, analyticsToken, analyticsHeaders, handleFinanceUnauthorized]);

  const loadBanquetBookingLinesForMonth = useCallback(
    async (monthYyyyMm) => {
      if (role !== SUPER_ADMIN || !analyticsToken) return;
      const m = typeof monthYyyyMm === "string" ? monthYyyyMm.trim() : "";
      if (!/^\d{4}-\d{2}$/.test(m)) return;
      setBanquetLinesBusy(true);
      setFinanceErr("");
      try {
        const { data } = await axios.get(Constants.ADMIN_FINANCE_BANQUET_BOOKING_LINES, {
          headers: analyticsHeaders,
          params: { calendarMonth: m },
        });
        setBanquetBookingLines(Array.isArray(data) ? data : []);
      } catch (e) {
        if (e?.response?.status === 401) handleFinanceUnauthorized();
        else setFinanceErr(e?.response?.data?.message || e?.message || "Could not load banquet booking lines.");
        setBanquetBookingLines([]);
      } finally {
        setBanquetLinesBusy(false);
      }
    },
    [role, analyticsToken, analyticsHeaders, handleFinanceUnauthorized]
  );

  const banquetAnalyticsLineColumnDefs = useMemo(
    () => [
      { headerName: "S.No", field: "serialNo", width: 72 },
      {
        headerName: "Date",
        field: "eventDate",
        width: 110,
        valueFormatter: (p) => formatIsoDateIn(p.value),
      },
      {
        headerName: "Src",
        field: "source",
        width: 90,
        valueFormatter: (p) => {
          const src = p.value || "—";
          return p.data?.cancelled ? `${src} (CX)` : src;
        },
      },
      { headerName: "Package / function", field: "packageName", width: 180, wrapText: true, autoHeight: true },
      { headerName: "Slot", field: "slotDesc", width: 120, wrapText: true, autoHeight: true },
      { headerName: "Pax", field: "numPax", width: 72, type: "numericColumn" },
      {
        headerName: "Advance",
        field: "advanceRupee",
        width: 100,
        type: "numericColumn",
        valueFormatter: (p) => formatRupeeMaybe(p.value),
        cellClass: "admin-console-grid-cell-advance",
      },
      {
        headerName: "Balance",
        field: "balanceRupee",
        width: 100,
        type: "numericColumn",
        valueFormatter: (p) => formatRupeeMaybe(p.value),
        cellClass: "admin-console-grid-cell-amount",
      },
      {
        headerName: "Total",
        field: "totalAmountRupee",
        width: 100,
        type: "numericColumn",
        valueFormatter: (p) => formatRupeeMaybe(p.value),
        cellClass: "admin-console-grid-cell-amount",
      },
      {
        headerName: "Bank",
        field: "bankPaymentRupee",
        width: 100,
        type: "numericColumn",
        valueFormatter: (p) => formatRupeeMaybe(p.value),
      },
      {
        headerName: "Cash",
        field: "cashPaymentRupee",
        width: 100,
        type: "numericColumn",
        valueFormatter: (p) => formatRupeeMaybe(p.value),
      },
      { headerName: "Status", field: "status", width: 100 },
      { headerName: "Remarks", field: "remarks", width: 200, wrapText: true, autoHeight: true },
      {
        headerName: "Décor exp",
        field: "decorExpensesRupee",
        width: 110,
        type: "numericColumn",
        valueFormatter: (p) => formatRupeeMaybe(p.value),
      },
      {
        headerName: "Décor cost",
        field: "decorCostRupee",
        width: 110,
        type: "numericColumn",
        valueFormatter: (p) => formatRupeeMaybe(p.value),
      },
      {
        headerName: "Pre-plate",
        field: "prePlateCostRupee",
        width: 110,
        type: "numericColumn",
        valueFormatter: (p) => formatRupeeMaybe(p.value),
      },
      { headerName: "Ref", field: "bookingReferenceId", width: 120 },
      {
        headerName: "Customer",
        width: 160,
        wrapText: true,
        autoHeight: true,
        valueGetter: (p) => {
          const d = p.data || {};
          const parts = [d.customerName, d.customerPhone].filter(Boolean);
          return parts.join(" · ") || "—";
        },
      },
      { headerName: "Import file", field: "importedFromFile", width: 140 },
      {
        headerName: "Edit",
        width: 88,
        pinned: "right",
        sortable: false,
        filter: false,
        cellRenderer: (params) => {
          const ln = params.data;
          return (
            <button
              type="button"
              className="btn btn-sm admin-console-grid-btn-gold"
              disabled={!ln?.id || banquetLinesBusy}
              onClick={() => {
                setBanquetEditDraft(banquetLineToEditDraft(ln));
                setBanquetEditOpen(true);
              }}
            >
              Edit
            </button>
          );
        },
        cellClass: "admin-console-grid-actions-cell",
      },
    ],
    [banquetLinesBusy]
  );

  const banquetAnalyticsLineDefaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: false,
      resizable: true,
      suppressMovable: true,
    }),
    []
  );

  /** API returns yyyy-MM newest-first; aligns line grid month with sheet tab names after upload. */
  const banquetImportPrimaryMonthKey = useCallback((importPayload) => {
    const raw = Array.isArray(importPayload?.calendarMonthsTouched) ? importPayload.calendarMonthsTouched : [];
    const ok = raw.map((x) => String(x).trim()).filter((x) => /^\d{4}-\d{2}$/.test(x));
    return ok.length ? ok[0] : null;
  }, []);

  const uploadBanquetWorkbookFile = useCallback(
    async (file) => {
      if (role !== SUPER_ADMIN || !analyticsToken || !file) return;
      setFinanceErr("");
      setFinanceMsg("");
      setBanquetUploadBusy(true);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await axios.post(Constants.ADMIN_FINANCE_BANQUET_UPLOAD, fd, {
          headers: { ...analyticsHeaders },
        });
        const msgs = Array.isArray(data?.messages) ? data.messages : [];
        const fromImport = banquetImportPrimaryMonthKey(data);
        const monthForLines = fromImport || banquetLinesMonth;
        if (fromImport) {
          setBanquetLinesMonth(fromImport);
        }
        const dupNote = data?.duplicateFile
          ? "Duplicate banquet workbook — no rows were changed.\n"
          : "";
        setFinanceMsg(
          dupNote +
            (msgs.length
              ? msgs.join("\n")
              : `Imported ${Number(data?.sheetsRecognized ?? 0)} sheet(s); ${Number(
                  data?.monthsUpdated ?? 0
                )} month total(s).`)
        );
        await loadBanquetMonthly();
        await loadBanquetBookingLinesForMonth(monthForLines);
      } catch (e) {
        if (e?.response?.status === 401) handleFinanceUnauthorized();
        else setFinanceErr(e?.response?.data?.message || e?.message || "Upload failed.");
      } finally {
        setBanquetUploadBusy(false);
      }
    },
    [
      role,
      analyticsToken,
      analyticsHeaders,
      banquetImportPrimaryMonthKey,
      banquetLinesMonth,
      loadBanquetMonthly,
      loadBanquetBookingLinesForMonth,
      handleFinanceUnauthorized,
    ]
  );

  /** If picker is stuck on today's month but roll-up rows only contain other yyyy-MM keys (typical Excel tabs). */
  useEffect(() => {
    if (bizTab !== "banquet" || role !== SUPER_ADMIN || !analyticsToken) return undefined;
    if (!(banquetRows || []).length) return undefined;
    if (banquetLinesMonth !== yyyyMmToday()) return undefined;
    const hasRollup = (banquetRows || []).some((r) => r.periodKey === banquetLinesMonth);
    if (hasRollup) return undefined;
    const latest = [...banquetRows]
      .map((r) => r.periodKey)
      .filter(Boolean)
      .sort((a, b) => String(b).localeCompare(String(a)))[0];
    if (latest) setBanquetLinesMonth(latest);
    return undefined;
  }, [bizTab, role, analyticsToken, banquetRows, banquetLinesMonth]);

  const loadDailyExpensesForMonthPick = useCallback(async () => {
    if (role !== SUPER_ADMIN || !analyticsToken) return;
    setDailyBusy(true);
    setFinanceErr("");
    try {
      const rng = isoMonthToRange(expensePickMonth);
      const { data } = await axios.get(Constants.ADMIN_FINANCE_DAILY_EXPENSES, {
        headers: analyticsHeaders,
        params: { fromDate: rng.fromIso, toDate: rng.toIso },
      });
      setDailyRows(Array.isArray(data) ? data : []);
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
      else setFinanceErr(e?.response?.data?.message || e?.message || "Could not load daily expense rows.");
      setDailyRows([]);
    } finally {
      setDailyBusy(false);
    }
  }, [role, analyticsToken, analyticsHeaders, expensePickMonth, handleFinanceUnauthorized]);

  const loadMonthlyOverview = useCallback(async (periodKey) => {
    const pk = periodKey || maintPickMonth || expensePickMonth;
    if (role !== SUPER_ADMIN || !analyticsToken || !pk) return;
    setMonthlyOverviewBusy(true);
    try {
      const { data } = await axios.get(Constants.ADMIN_FINANCE_MONTHLY_OVERVIEW, {
        headers: analyticsHeaders,
        params: { periodKey: pk },
      });
      setMonthlyOverview(data);
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
      else setFinanceErr(e?.response?.data?.message || e?.message || "Monthly overview load failed.");
      setMonthlyOverview(null);
    } finally {
      setMonthlyOverviewBusy(false);
    }
  }, [role, analyticsToken, analyticsHeaders, maintPickMonth, expensePickMonth, handleFinanceUnauthorized]);

  const loadMonthlyComparison = useCallback(async (periodKey, trailing) => {
    const endPk = periodKey || maintPickMonth || yyyyMmToday();
    const n = Number(trailing) || comparisonMonths;
    if (role !== SUPER_ADMIN || !analyticsToken || !endPk) return;
    setComparisonBusy(true);
    try {
      const { data } = await axios.get(Constants.ADMIN_FINANCE_MONTHLY_COMPARISON, {
        headers: analyticsHeaders,
        params: { months: n, endPeriodKey: endPk },
      });
      setMonthlyComparison(data || { months: [], trailingMonths: n });
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
      setMonthlyComparison(null);
    } finally {
      setComparisonBusy(false);
    }
  }, [role, analyticsToken, analyticsHeaders, maintPickMonth, comparisonMonths, handleFinanceUnauthorized]);

  const loadHyperGmailStatus = useCallback(async () => {
    if (role !== SUPER_ADMIN || !analyticsToken) return;
    try {
      const { data } = await axios.get(Constants.ADMIN_FINANCE_GMAIL_HYPER_STATUS, { headers: analyticsHeaders });
      setHyperGmailStatus(data || null);
      if (data?.defaultLookbackMonths != null) {
        setHyperLookbackMonths((prev) => (prev === 12 ? data.defaultLookbackMonths : prev));
      }
    } catch {
      setHyperGmailStatus(null);
    }
  }, [role, analyticsToken, analyticsHeaders]);

  const loadExpensePresetsOnce = useCallback(async () => {
    if (role !== SUPER_ADMIN || !analyticsToken) return;
    try {
      const { data } = await axios.get(Constants.ADMIN_FINANCE_EXPENSE_PRESETS, { headers: analyticsHeaders });
      const arr = Array.isArray(data?.categories) ? data.categories : [];
      setExpensePresets(arr);
      setDexCategory((prev) => ((typeof prev === "string" ? prev.trim() : "") ? prev : arr[0] || ""));
    } catch {
      setExpensePresets([]);
    }
  }, [role, analyticsToken, analyticsHeaders]);

  const loadProfit = useCallback(async () => {
    if (role !== SUPER_ADMIN || !analyticsToken) return;
    setProfitBusy(true);
    setFinanceErr("");
    try {
      const [profitRes, chartRes] = await Promise.all([
        axios.get(Constants.ADMIN_FINANCE_PROFIT_SUMMARY, {
          headers: analyticsHeaders,
          params: { months: profitWindowMonths },
        }),
        axios.get(Constants.ADMIN_FINANCE_CASHFLOW_CHART, {
          headers: analyticsHeaders,
          params: { months: profitWindowMonths },
        }),
      ]);
      setProfitPack(profitRes.data || { months: [], insightBullets: [] });
      setCashflowChart(chartRes.data || null);
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
      else setFinanceErr(e?.response?.data?.message || e?.message || "Could not calculate profit workbook.");
      setProfitPack(null);
      setCashflowChart(null);
    } finally {
      setProfitBusy(false);
    }
  }, [role, analyticsToken, analyticsHeaders, profitWindowMonths, handleFinanceUnauthorized]);

  const loadExecutiveSummary = useCallback(async () => {
    if (role !== SUPER_ADMIN || !analyticsToken) return;
    setExecutiveSummaryBusy(true);
    setFinanceErr("");
    try {
      const { data } = await axios.get(Constants.ADMIN_FINANCE_EXECUTIVE_SUMMARY, { headers: analyticsHeaders });
      setExecutiveSummary(data || null);
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
      else setFinanceErr(e?.response?.data?.message || e?.message || "Executive summary load failed.");
      setExecutiveSummary(null);
    } finally {
      setExecutiveSummaryBusy(false);
    }
  }, [role, analyticsToken, analyticsHeaders, handleFinanceUnauthorized]);

  const uploadFinanceFile = useCallback(
    async (key, url, file, successLabel) => {
      if (!file) {
        setFinanceErr("Pick a file first.");
        return;
      }
      setFinanceErr("");
      setFinanceMsg("");
      setUploadBusyKey(key);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await axios.post(url, fd, {
          headers: { ...analyticsHeaders, "Content-Type": "multipart/form-data" },
        });
        setUploadLastResult({ key, ...data });
        const dupNote = data?.duplicateFile
          ? "Duplicate file — nothing was inserted or updated.\n"
          : "";
        setFinanceMsg(
          dupNote +
            `${successLabel}: inserted ${data?.insertedCount ?? 0}, updated ${data?.updatedCount ?? 0}, skipped ${data?.skippedCount ?? 0}.\n` +
            (Array.isArray(data?.messages) ? data.messages.join("\n") : "")
        );
      } catch (e) {
        if (e?.response?.status === 401) handleFinanceUnauthorized();
        else setFinanceErr(e?.response?.data?.message || e?.message || "Upload failed.");
      } finally {
        setUploadBusyKey("");
      }
    },
    [analyticsHeaders, handleFinanceUnauthorized]
  );

  const pollHyperGmail = useCallback(async () => {
    setFinanceErr("");
    setFinanceMsg("");
    const force = Boolean(hyperGmailStatus?.backfillActive);
    setUploadBusyKey("hyper-gmail");
    try {
      const url = Constants.adminFinanceGmailHyperPollUrl(hyperLookbackMonths, force);
      const { data } = await axios.post(url, null, { headers: analyticsHeaders });
      setUploadLastResult({ key: "hyper-gmail", ...data });
      setFinanceMsg(
        `Hyper Gmail pull: inserted ${data?.insertedCount ?? 0}, skipped ${data?.skippedCount ?? 0}.\n` +
          (Array.isArray(data?.messages) ? data.messages.join("\n") : "")
      );
      await loadHyperGmailStatus();
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
      else setFinanceErr(e?.response?.data?.message || e?.message || "Gmail poll failed.");
    } finally {
      setUploadBusyKey("");
    }
  }, [analyticsHeaders, handleFinanceUnauthorized, hyperLookbackMonths, hyperGmailStatus?.backfillActive, loadHyperGmailStatus]);

  const resetHyperCursor = useCallback(async () => {
    setFinanceErr("");
    setUploadBusyKey("hyper-reset");
    try {
      const { data } = await axios.post(Constants.ADMIN_FINANCE_GMAIL_HYPER_RESET, null, {
        headers: analyticsHeaders,
      });
      setFinanceMsg(Array.isArray(data?.messages) ? data.messages.join("\n") : "Cursor reset.");
      await loadHyperGmailStatus();
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
      else setFinanceErr(e?.response?.data?.message || e?.message || "Cursor reset failed.");
    } finally {
      setUploadBusyKey("");
    }
  }, [analyticsHeaders, handleFinanceUnauthorized, loadHyperGmailStatus]);

  const loadReconLedger = useCallback(async () => {
    if (role !== SUPER_ADMIN || !analyticsToken) return;
    const { fromIso, toIso } = isoMonthToRange(reconPeriod);
    setReconLedgerBusy(true);
    setFinanceErr("");
    try {
      const params = { fromDate: fromIso, toDate: toIso };
      if (reconBank) params.bank = reconBank;
      const { data } = await axios.get(Constants.ADMIN_FINANCE_BANK_LEDGER, {
        headers: analyticsHeaders,
        params,
      });
      setReconLedger(Array.isArray(data) ? data : []);
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
      else setFinanceErr(e?.response?.data?.message || e?.message || "Could not load bank ledger.");
      setReconLedger([]);
    } finally {
      setReconLedgerBusy(false);
    }
  }, [role, analyticsToken, analyticsHeaders, reconPeriod, reconBank, handleFinanceUnauthorized]);

  const runReconciliation = useCallback(async () => {
    const { fromIso, toIso } = isoMonthToRange(reconPeriod);
    setFinanceErr("");
    try {
      const { data } = await axios.post(Constants.ADMIN_FINANCE_RECONCILIATION_RUN, null, {
        headers: analyticsHeaders,
        params: { fromDate: fromIso, toDate: toIso },
      });
      setReconSummary(data || null);
      setFinanceMsg(
        `Reconciliation: total ${data?.totalRows ?? 0}, already ${data?.alreadyClassified ?? 0}, ` +
          `newly ${data?.newlyClassified ?? 0}, unmatched ${data?.unmatched ?? 0}.`
      );
      await loadReconLedger();
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
      else setFinanceErr(e?.response?.data?.message || e?.message || "Reconciliation failed.");
    }
  }, [reconPeriod, analyticsHeaders, handleFinanceUnauthorized, loadReconLedger]);

  const loadReconRules = useCallback(async () => {
    if (role !== SUPER_ADMIN || !analyticsToken) return;
    try {
      const { data } = await axios.get(Constants.ADMIN_FINANCE_RECONCILIATION_RULES, {
        headers: analyticsHeaders,
      });
      setReconRules(Array.isArray(data) ? data : []);
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
      else setFinanceErr(e?.response?.data?.message || e?.message || "Could not load rules.");
    }
  }, [role, analyticsToken, analyticsHeaders, handleFinanceUnauthorized]);

  const saveReconRule = useCallback(async () => {
    setReconRuleBusy(true);
    setFinanceErr("");
    setFinanceMsg("");
    try {
      const payload = {
        ...reconRuleDraft,
        expectedAmountRupee:
          reconRuleDraft.expectedAmountRupee === "" || reconRuleDraft.expectedAmountRupee == null
            ? null
            : Math.round(Number(reconRuleDraft.expectedAmountRupee)),
        amountToleranceRupee:
          reconRuleDraft.amountToleranceRupee === "" || reconRuleDraft.amountToleranceRupee == null
            ? null
            : Math.round(Number(reconRuleDraft.amountToleranceRupee)),
        priority: Number(reconRuleDraft.priority || 100),
      };
      await axios.post(Constants.ADMIN_FINANCE_RECONCILIATION_RULES, payload, {
        headers: analyticsHeaders,
      });
      setFinanceMsg("Rule saved.");
      await loadReconRules();
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
      else setFinanceErr(e?.response?.data?.message || e?.message || "Save rule failed.");
    } finally {
      setReconRuleBusy(false);
    }
  }, [reconRuleDraft, analyticsHeaders, handleFinanceUnauthorized, loadReconRules]);

  const deleteReconRule = useCallback(
    async (id) => {
      try {
        await axios.delete(Constants.adminFinanceReconciliationRuleDelete(id), {
          headers: analyticsHeaders,
        });
        setFinanceMsg("Rule deleted.");
        await loadReconRules();
      } catch (e) {
        if (e?.response?.status === 401) handleFinanceUnauthorized();
        else setFinanceErr(e?.response?.data?.message || e?.message || "Delete rule failed.");
      }
    },
    [analyticsHeaders, handleFinanceUnauthorized, loadReconRules]
  );

  const loadStockBundle = useCallback(async () => {
    if (role !== SUPER_ADMIN || !analyticsToken) return;
    setStockBusy(true);
    setFinanceErr("");
    try {
      const { data } = await axios.get(Constants.ADMIN_FINANCE_STOCK_MONTHLY, {
        headers: analyticsHeaders,
        params: { periodKey: reconPeriod },
      });
      setStockBundle(data || null);
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
      else setFinanceErr(e?.response?.data?.message || e?.message || "Stock report failed.");
      setStockBundle(null);
    } finally {
      setStockBusy(false);
    }
  }, [role, analyticsToken, analyticsHeaders, reconPeriod, handleFinanceUnauthorized]);

  const loadStockDrill = useCallback(
    async (cat) => {
      const category = cat || stockDrillCategory;
      if (!category) {
        setStockDrillRows([]);
        return;
      }
      try {
        const { data } = await axios.get(Constants.ADMIN_FINANCE_STOCK_DRILLDOWN, {
          headers: analyticsHeaders,
          params: { periodKey: reconPeriod, category },
        });
        setStockDrillRows(Array.isArray(data) ? data : []);
      } catch (e) {
        if (e?.response?.status === 401) handleFinanceUnauthorized();
        else setFinanceErr(e?.response?.data?.message || e?.message || "Drill-down failed.");
      }
    },
    [stockDrillCategory, analyticsHeaders, reconPeriod, handleFinanceUnauthorized]
  );

  const reconRangeIso = useMemo(() => isoMonthToRange(reconPeriod), [reconPeriod]);

  const loadVendorIncome = useCallback(async () => {
    if (role !== SUPER_ADMIN || !analyticsToken) return;
    setVendorIncomeBusy(true);
    try {
      const { data } = await axios.get(Constants.ADMIN_FINANCE_BANK_VENDOR_INCOME, {
        headers: analyticsHeaders,
        params: { fromDate: reconRangeIso.fromIso, toDate: reconRangeIso.toIso },
      });
      setVendorIncomeRows(Array.isArray(data) ? data : []);
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
      else setFinanceErr(e?.response?.data?.message || e?.message || "Vendor income load failed.");
      setVendorIncomeRows([]);
    } finally {
      setVendorIncomeBusy(false);
    }
  }, [role, analyticsToken, analyticsHeaders, reconRangeIso, handleFinanceUnauthorized]);

  const loadDayendMatch = useCallback(async () => {
    if (role !== SUPER_ADMIN || !analyticsToken) return;
    setDayendMatchBusy(true);
    try {
      const { data } = await axios.get(Constants.ADMIN_FINANCE_PETPOOJA_DAYEND_VS_ICICI, {
        headers: analyticsHeaders,
        params: { fromDate: reconRangeIso.fromIso, toDate: reconRangeIso.toIso },
      });
      setDayendMatchRows(Array.isArray(data) ? data : []);
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
      else setFinanceErr(e?.response?.data?.message || e?.message || "Day-end match load failed.");
      setDayendMatchRows([]);
    } finally {
      setDayendMatchBusy(false);
    }
  }, [role, analyticsToken, analyticsHeaders, reconRangeIso, handleFinanceUnauthorized]);

  const loadSalaryMonths = useCallback(async () => {
    if (role !== SUPER_ADMIN || !analyticsToken) return;
    try {
      const { data } = await axios.get(Constants.ADMIN_FINANCE_EMP_SALARY_MONTHS, {
        headers: analyticsHeaders,
      });
      const list = Array.isArray(data?.months) ? data.months : [];
      setSalaryMonths(list);
      if (list.length && !list.includes(salaryPick)) setSalaryPick(list[0]);
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
    }
  }, [role, analyticsToken, analyticsHeaders, handleFinanceUnauthorized, salaryPick]);

  const loadSalaryRows = useCallback(
    async (periodKey) => {
      const pk = periodKey || salaryPick;
      if (!pk || role !== SUPER_ADMIN || !analyticsToken) return;
      setSalaryBusy(true);
      try {
        const { data } = await axios.get(Constants.ADMIN_FINANCE_EMP_SALARY, {
          headers: analyticsHeaders,
          params: { periodKey: pk },
        });
        setSalaryRows(Array.isArray(data) ? data : []);
      } catch (e) {
        if (e?.response?.status === 401) handleFinanceUnauthorized();
        else setFinanceErr(e?.response?.data?.message || e?.message || "Salary load failed.");
        setSalaryRows([]);
      } finally {
        setSalaryBusy(false);
      }
    },
    [role, analyticsToken, analyticsHeaders, salaryPick, handleFinanceUnauthorized]
  );

  const loadSalaryAdvanceSummary = useCallback(async (periodKey) => {
    const pk = periodKey || maintPickMonth || salaryPick;
    if (!pk || role !== SUPER_ADMIN || !analyticsToken) return;
    try {
      const { data } = await axios.get(Constants.ADMIN_FINANCE_EMP_SALARY_ADVANCE, {
        headers: analyticsHeaders,
        params: { periodKey: pk },
      });
      setSalaryAdvanceSummary(data || null);
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
      else setSalaryAdvanceSummary(null);
    }
  }, [role, analyticsToken, analyticsHeaders, maintPickMonth, salaryPick, handleFinanceUnauthorized]);

  const loadPartnerShare = useCallback(async () => {
    if (role !== SUPER_ADMIN || !analyticsToken) return;
    setPartnerShareBusy(true);
    try {
      const { data } = await axios.get(Constants.ADMIN_FINANCE_PARTNER_SHARE, {
        headers: analyticsHeaders,
        params: { granularity: partnerGranularity, periods: partnerGranularity === "MONTHLY" ? 12 : 8 },
      });
      setPartnerShare(data || null);
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
      else setFinanceErr(e?.response?.data?.message || e?.message || "Partner share load failed.");
      setPartnerShare(null);
    } finally {
      setPartnerShareBusy(false);
    }
  }, [role, analyticsToken, analyticsHeaders, partnerGranularity, handleFinanceUnauthorized]);

  const loadPartnerSettings = useCallback(async () => {
    if (role !== SUPER_ADMIN || !analyticsToken) return;
    try {
      const { data } = await axios.get(Constants.ADMIN_FINANCE_PARTNER_SHARE_SETTINGS, { headers: analyticsHeaders });
      if (data) setPartnerSettings(data);
    } catch {
      /* keep defaults */
    }
  }, [role, analyticsToken, analyticsHeaders]);

  const savePartnerSettings = useCallback(async () => {
    if (role !== SUPER_ADMIN || !analyticsToken) return;
    setPartnerSettingsBusy(true);
    setFinanceErr("");
    try {
      const { data } = await axios.put(Constants.ADMIN_FINANCE_PARTNER_SHARE_SETTINGS, partnerSettings, {
        headers: analyticsHeaders,
      });
      setPartnerSettings(data);
      setFinanceMsg("Partner share percentages saved.");
      await loadPartnerShare();
    } catch (e) {
      setFinanceErr(e?.response?.data?.message || e?.message || "Could not save partner settings.");
    } finally {
      setPartnerSettingsBusy(false);
    }
  }, [role, analyticsToken, analyticsHeaders, partnerSettings, loadPartnerShare]);

  const loadHyperCompare = useCallback(async () => {
    if (role !== SUPER_ADMIN || !analyticsToken) return;
    setHyperCompareBusy(true);
    try {
      const { data } = await axios.get(Constants.ADMIN_FINANCE_HYPER_KITCHEN_COMPARE, {
        headers: analyticsHeaders,
        params: { periodKey: reconPeriod, months: hyperCompareMonths },
      });
      setHyperCompare(data || null);
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
      setHyperCompare(null);
    } finally {
      setHyperCompareBusy(false);
    }
  }, [role, analyticsToken, analyticsHeaders, reconPeriod, hyperCompareMonths, handleFinanceUnauthorized]);

  const loadHyperItems = useCallback(async () => {
    if (role !== SUPER_ADMIN || !analyticsToken) return;
    setHyperItemsBusy(true);
    try {
      const pk = bizTab === "expenses" ? maintPickMonth : reconPeriod;
      const { data } = await axios.get(Constants.ADMIN_FINANCE_HYPER_ITEMS, {
        headers: analyticsHeaders,
        params: { periodKey: pk },
      });
      setHyperItems(Array.isArray(data) ? data : []);
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
      else setFinanceErr(e?.response?.data?.message || e?.message || "Hyper items load failed.");
      setHyperItems([]);
    } finally {
      setHyperItemsBusy(false);
    }
  }, [role, analyticsToken, analyticsHeaders, reconPeriod, maintPickMonth, bizTab, handleFinanceUnauthorized]);

  const loadHyperOrders = useCallback(async () => {
    if (role !== SUPER_ADMIN || !analyticsToken) return;
    setHyperOrdersBusy(true);
    try {
      const { data } = await axios.get(Constants.ADMIN_FINANCE_HYPER_ORDERS, {
        headers: analyticsHeaders,
        params: { periodKey: reconPeriod },
      });
      setHyperOrders(Array.isArray(data) ? data : []);
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
      else setFinanceErr(e?.response?.data?.message || e?.message || "Hyper orders load failed.");
      setHyperOrders([]);
    } finally {
      setHyperOrdersBusy(false);
    }
  }, [role, analyticsToken, analyticsHeaders, reconPeriod, handleFinanceUnauthorized]);

  const loadHyperOrderLines = useCallback(
    async (orderRef) => {
      if (role !== SUPER_ADMIN || !analyticsToken || !orderRef) return;
      setHyperOrderLinesBusy(true);
      try {
        const { data } = await axios.get(Constants.ADMIN_FINANCE_HYPER_ORDER_LINES, {
          headers: analyticsHeaders,
          params: { orderRef },
        });
        setHyperOrderLines(Array.isArray(data) ? data : []);
      } catch (e) {
        if (e?.response?.status === 401) handleFinanceUnauthorized();
        else setFinanceErr(e?.response?.data?.message || e?.message || "Hyper order lines load failed.");
        setHyperOrderLines([]);
      } finally {
        setHyperOrderLinesBusy(false);
      }
    },
    [role, analyticsToken, analyticsHeaders, handleFinanceUnauthorized]
  );

  const loadPlasticLedger = useCallback(async (periodKeyOverride) => {
    if (role !== SUPER_ADMIN || !analyticsToken) return;
    setPlasticLedgerBusy(true);
    try {
      const { data } = await axios.get(Constants.ADMIN_FINANCE_PLASTIC_LEDGER, {
        headers: analyticsHeaders,
        params: { periodKey: periodKeyOverride || reconPeriod },
      });
      setPlasticLedger(data || null);
      if (data?.openingBalanceRupee != null || data?.openingBalanceDateIso) {
        setPlasticSettings({
          openingBalanceRupee: Number(data.openingBalanceRupee) || 0,
          openingBalanceDateIso: data.openingBalanceDateIso || "2026-05-09",
        });
      }
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
      else setFinanceErr(e?.response?.data?.message || e?.message || "Plastic ledger load failed.");
      setPlasticLedger(null);
    } finally {
      setPlasticLedgerBusy(false);
    }
  }, [role, analyticsToken, analyticsHeaders, reconPeriod, handleFinanceUnauthorized]);

  const togglePlasticOrderExpand = useCallback((orderId) => {
    const key = String(orderId);
    setPlasticExpandedOrderIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const loadPlasticSettings = useCallback(async () => {
    if (role !== SUPER_ADMIN || !analyticsToken) return;
    try {
      const { data } = await axios.get(Constants.ADMIN_FINANCE_PLASTIC_SETTINGS, { headers: analyticsHeaders });
      if (data) {
        setPlasticSettings({
          openingBalanceRupee: Number(data.openingBalanceRupee) || 0,
          openingBalanceDateIso: data.openingBalanceDateIso || "2026-05-09",
        });
      }
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
    }
  }, [role, analyticsToken, analyticsHeaders, handleFinanceUnauthorized]);

  const savePlasticSettings = useCallback(async () => {
    if (role !== SUPER_ADMIN || !analyticsToken) return;
    setPlasticSettingsBusy(true);
    try {
      await axios.put(Constants.ADMIN_FINANCE_PLASTIC_SETTINGS, plasticSettings, { headers: analyticsHeaders });
      await loadPlasticLedger();
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
      else setFinanceErr(e?.response?.data?.message || e?.message || "Plastic settings save failed.");
    } finally {
      setPlasticSettingsBusy(false);
    }
  }, [role, analyticsToken, analyticsHeaders, plasticSettings, loadPlasticLedger, handleFinanceUnauthorized]);

  const loadCoalLedger = useCallback(async () => {
    if (role !== SUPER_ADMIN || !analyticsToken) return;
    setCoalLedgerBusy(true);
    try {
      const { data } = await axios.get(Constants.adminFinanceVendorPayableLedgerUrl("coal", maintPickMonth), {
        headers: analyticsHeaders,
      });
      setCoalLedger(data || null);
      if (data?.openingBalanceRupee != null || data?.openingBalanceDateIso) {
        setCoalSettings({
          openingBalanceRupee: Number(data.openingBalanceRupee) || 0,
          openingBalanceDateIso: data.openingBalanceDateIso || "",
        });
      }
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
      else setFinanceErr(e?.response?.data?.message || e?.message || "Coal ledger load failed.");
      setCoalLedger(null);
    } finally {
      setCoalLedgerBusy(false);
    }
  }, [role, analyticsToken, analyticsHeaders, maintPickMonth, handleFinanceUnauthorized]);

  const loadCoalSettings = useCallback(async () => {
    if (role !== SUPER_ADMIN || !analyticsToken) return;
    try {
      const { data } = await axios.get(Constants.adminFinanceVendorPayableSettingsUrl("coal"), { headers: analyticsHeaders });
      if (data) {
        setCoalSettings({
          openingBalanceRupee: Number(data.openingBalanceRupee) || 0,
          openingBalanceDateIso: data.openingBalanceDateIso || "",
        });
      }
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
    }
  }, [role, analyticsToken, analyticsHeaders, handleFinanceUnauthorized]);

  const loadVendorDebts = useCallback(async () => {
    if (role !== SUPER_ADMIN || !analyticsToken) return;
    setVendorDebtBusy(true);
    try {
      const { data } = await axios.get(Constants.adminFinanceVendorDebtsUrl(maintPickMonth), { headers: analyticsHeaders });
      setVendorDebtSummary(data || null);
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
      else setFinanceErr(e?.response?.data?.message || e?.message || "Vendor debt summary failed.");
      setVendorDebtSummary(null);
    } finally {
      setVendorDebtBusy(false);
    }
  }, [role, analyticsToken, analyticsHeaders, maintPickMonth, handleFinanceUnauthorized]);

  const saveCoalSettings = useCallback(async () => {
    if (role !== SUPER_ADMIN || !analyticsToken) return;
    setCoalSettingsBusy(true);
    try {
      await axios.put(Constants.adminFinanceVendorPayableSettingsUrl("coal"), coalSettings, { headers: analyticsHeaders });
      await loadCoalLedger();
      await loadVendorDebts();
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
      else setFinanceErr(e?.response?.data?.message || e?.message || "Coal settings save failed.");
    } finally {
      setCoalSettingsBusy(false);
    }
  }, [role, analyticsToken, analyticsHeaders, coalSettings, loadCoalLedger, loadVendorDebts, handleFinanceUnauthorized]);

  const loadDairyLedger = useCallback(async () => {
    if (role !== SUPER_ADMIN || !analyticsToken) return;
    setDairyLedgerBusy(true);
    try {
      const { data } = await axios.get(Constants.adminFinanceVendorPayableLedgerUrl("dairy", maintPickMonth), {
        headers: analyticsHeaders,
      });
      setDairyLedger(data || null);
      if (data?.openingBalanceRupee != null || data?.openingBalanceDateIso) {
        setDairySettings({
          openingBalanceRupee: Number(data.openingBalanceRupee) || 0,
          openingBalanceDateIso: data.openingBalanceDateIso || "",
        });
      }
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
      else setFinanceErr(e?.response?.data?.message || e?.message || "Dairy ledger load failed.");
      setDairyLedger(null);
    } finally {
      setDairyLedgerBusy(false);
    }
  }, [role, analyticsToken, analyticsHeaders, maintPickMonth, handleFinanceUnauthorized]);

  const loadDairySettings = useCallback(async () => {
    if (role !== SUPER_ADMIN || !analyticsToken) return;
    try {
      const { data } = await axios.get(Constants.adminFinanceVendorPayableSettingsUrl("dairy"), { headers: analyticsHeaders });
      if (data) {
        setDairySettings({
          openingBalanceRupee: Number(data.openingBalanceRupee) || 0,
          openingBalanceDateIso: data.openingBalanceDateIso || "",
        });
      }
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
    }
  }, [role, analyticsToken, analyticsHeaders, handleFinanceUnauthorized]);

  const saveDairySettings = useCallback(async () => {
    if (role !== SUPER_ADMIN || !analyticsToken) return;
    setDairySettingsBusy(true);
    try {
      await axios.put(Constants.adminFinanceVendorPayableSettingsUrl("dairy"), dairySettings, { headers: analyticsHeaders });
      await loadDairyLedger();
      await loadVendorDebts();
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
      else setFinanceErr(e?.response?.data?.message || e?.message || "Dairy settings save failed.");
    } finally {
      setDairySettingsBusy(false);
    }
  }, [role, analyticsToken, analyticsHeaders, dairySettings, loadDairyLedger, loadVendorDebts, handleFinanceUnauthorized]);

  const loadIceCreamLedger = useCallback(async () => {
    if (role !== SUPER_ADMIN || !analyticsToken) return;
    setIceCreamLedgerBusy(true);
    try {
      const { data } = await axios.get(Constants.adminFinanceVendorPayableLedgerUrl("ice-cream", maintPickMonth), {
        headers: analyticsHeaders,
      });
      setIceCreamLedger(data || null);
      if (data?.openingBalanceRupee != null || data?.openingBalanceDateIso) {
        setIceCreamSettings({
          openingBalanceRupee: Number(data.openingBalanceRupee) || 0,
          openingBalanceDateIso: data.openingBalanceDateIso || "",
        });
      }
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
      else setFinanceErr(e?.response?.data?.message || e?.message || "Ice Cream ledger load failed.");
      setIceCreamLedger(null);
    } finally {
      setIceCreamLedgerBusy(false);
    }
  }, [role, analyticsToken, analyticsHeaders, maintPickMonth, handleFinanceUnauthorized]);

  const loadIceCreamSettings = useCallback(async () => {
    if (role !== SUPER_ADMIN || !analyticsToken) return;
    try {
      const { data } = await axios.get(Constants.adminFinanceVendorPayableSettingsUrl("ice-cream"), {
        headers: analyticsHeaders,
      });
      if (data) {
        setIceCreamSettings({
          openingBalanceRupee: Number(data.openingBalanceRupee) || 0,
          openingBalanceDateIso: data.openingBalanceDateIso || "",
        });
      }
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
    }
  }, [role, analyticsToken, analyticsHeaders, handleFinanceUnauthorized]);

  const saveIceCreamSettings = useCallback(async () => {
    if (role !== SUPER_ADMIN || !analyticsToken) return;
    setIceCreamSettingsBusy(true);
    try {
      await axios.put(Constants.adminFinanceVendorPayableSettingsUrl("ice-cream"), iceCreamSettings, {
        headers: analyticsHeaders,
      });
      await loadIceCreamLedger();
      await loadVendorDebts();
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
      else setFinanceErr(e?.response?.data?.message || e?.message || "Ice Cream settings save failed.");
    } finally {
      setIceCreamSettingsBusy(false);
    }
  }, [role, analyticsToken, analyticsHeaders, iceCreamSettings, loadIceCreamLedger, loadVendorDebts, handleFinanceUnauthorized]);

  const loadMonthlyCash = useCallback(async (periodKey) => {
    const pk = periodKey || maintPickMonth;
    if (role !== SUPER_ADMIN || !analyticsToken || !pk) return;
    setMonthlyCashBusy(true);
    try {
      const { data } = await axios.get(Constants.adminFinanceMonthlyCashUrl(pk), { headers: analyticsHeaders });
      setMonthlyCash(data);
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
      else setMonthlyCash(null);
    } finally {
      setMonthlyCashBusy(false);
    }
  }, [role, analyticsToken, analyticsHeaders, maintPickMonth, handleFinanceUnauthorized]);

  const loadCashIncome = useCallback(async (periodKey) => {
    const pk = periodKey || maintPickMonth;
    if (role !== SUPER_ADMIN || !analyticsToken || !pk) return;
    setCashIncomeBusy(true);
    try {
      const { data } = await axios.get(Constants.adminFinanceCashIncomeUrl(pk), { headers: analyticsHeaders });
      setCashIncome(data);
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
      else setCashIncome(null);
    } finally {
      setCashIncomeBusy(false);
    }
  }, [role, analyticsToken, analyticsHeaders, maintPickMonth, handleFinanceUnauthorized]);

  const addCashPaymentLine = useCallback(
    async (slotId, line) => {
      const pk = maintPickMonth;
      if (role !== SUPER_ADMIN || !analyticsToken || !pk || !slotId) return;
      setMonthlyCashBusy(true);
      setFinanceErr("");
      try {
        const { data } = await axios.post(
          Constants.adminFinanceMonthlyCashAddLineUrl(pk),
          { slotId, ...line },
          { headers: analyticsHeaders }
        );
        setMonthlyCash(data);
        setFinanceMsg(`Cash payment added for ${slotId}.`);
        await loadMonthlyOverview(pk);
        await loadMonthlyComparison(pk, comparisonMonths);
        await loadProfit();
      } catch (e) {
        if (e?.response?.status === 401) handleFinanceUnauthorized();
        else setFinanceErr(e?.response?.data?.message || e?.message || "Could not add cash payment.");
      } finally {
        setMonthlyCashBusy(false);
      }
    },
    [
      role,
      analyticsToken,
      analyticsHeaders,
      maintPickMonth,
      comparisonMonths,
      loadMonthlyOverview,
      loadMonthlyComparison,
      loadProfit,
      handleFinanceUnauthorized,
    ]
  );

  const deleteCashPaymentLine = useCallback(
    async (slotId, lineId) => {
      const pk = maintPickMonth;
      if (role !== SUPER_ADMIN || !analyticsToken || !pk || !slotId || !lineId) return;
      setMonthlyCashBusy(true);
      setFinanceErr("");
      try {
        const { data } = await axios.delete(
          Constants.adminFinanceMonthlyCashDeleteLineUrl(pk, slotId, lineId),
          { headers: analyticsHeaders }
        );
        setMonthlyCash(data);
        setFinanceMsg(`Cash payment removed.`);
        await loadMonthlyOverview(pk);
        await loadMonthlyComparison(pk, comparisonMonths);
        await loadProfit();
      } catch (e) {
        if (e?.response?.status === 401) handleFinanceUnauthorized();
        else setFinanceErr(e?.response?.data?.message || e?.message || "Could not remove cash payment.");
      } finally {
        setMonthlyCashBusy(false);
      }
    },
    [
      role,
      analyticsToken,
      analyticsHeaders,
      maintPickMonth,
      comparisonMonths,
      loadMonthlyOverview,
      loadMonthlyComparison,
      loadProfit,
      handleFinanceUnauthorized,
    ]
  );

  const addCashIncomeLine = useCallback(
    async (line) => {
      const pk = maintPickMonth;
      if (role !== SUPER_ADMIN || !analyticsToken || !pk) return;
      setCashIncomeBusy(true);
      setFinanceErr("");
      try {
        const { data } = await axios.post(Constants.adminFinanceCashIncomeAddLineUrl(pk), line, {
          headers: analyticsHeaders,
        });
        setCashIncome(data);
        setFinanceMsg("Cash income entry added.");
        await loadProfit();
      } catch (e) {
        if (e?.response?.status === 401) handleFinanceUnauthorized();
        else setFinanceErr(e?.response?.data?.message || e?.message || "Could not add cash income.");
      } finally {
        setCashIncomeBusy(false);
      }
    },
    [role, analyticsToken, analyticsHeaders, maintPickMonth, loadProfit, handleFinanceUnauthorized]
  );

  const deleteCashIncomeLine = useCallback(
    async (source, lineId) => {
      const pk = maintPickMonth;
      if (role !== SUPER_ADMIN || !analyticsToken || !pk || !source || !lineId) return;
      setCashIncomeBusy(true);
      setFinanceErr("");
      try {
        const { data } = await axios.delete(
          Constants.adminFinanceCashIncomeDeleteLineUrl(pk, source, lineId),
          { headers: analyticsHeaders }
        );
        setCashIncome(data);
        setFinanceMsg("Cash income entry removed.");
        await loadProfit();
      } catch (e) {
        if (e?.response?.status === 401) handleFinanceUnauthorized();
        else setFinanceErr(e?.response?.data?.message || e?.message || "Could not remove cash income.");
      } finally {
        setCashIncomeBusy(false);
      }
    },
    [role, analyticsToken, analyticsHeaders, maintPickMonth, loadProfit, handleFinanceUnauthorized]
  );

  const cashBySlot = useMemo(() => {
    const m = {};
    (monthlyCash?.entries || []).forEach((e) => {
      m[e.slotId] = Number(e.cashRupee) || 0;
    });
    return m;
  }, [monthlyCash]);

  const cashLinesBySlot = useMemo(() => {
    const m = {};
    (monthlyCash?.entries || []).forEach((e) => {
      m[e.slotId] = e.lines || [];
    });
    return m;
  }, [monthlyCash]);

  const resetPlasticData = useCallback(async () => {
    if (role !== SUPER_ADMIN || !analyticsToken) return;
    if (!window.confirm("Delete ALL plastic vendor orders and line items? Opening balance settings are kept.")) return;
    setPlasticSettingsBusy(true);
    setFinanceErr("");
    try {
      const { data } = await axios.delete(Constants.ADMIN_FINANCE_PLASTIC_RESET, { headers: analyticsHeaders });
      setFinanceMsg(data?.message || "Plastic data cleared.");
      await loadPlasticLedger();
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
      else setFinanceErr(e?.response?.data?.message || e?.message || "Plastic reset failed.");
    } finally {
      setPlasticSettingsBusy(false);
    }
  }, [role, analyticsToken, analyticsHeaders, loadPlasticLedger, handleFinanceUnauthorized]);

  const uploadPlasticZip = useCallback(
    async (file) => {
      if (!file) {
        setFinanceErr("Pick a WhatsApp .zip file first.");
        return;
      }
      setFinanceErr("");
      setFinanceMsg("");
      setUploadBusyKey("whatsapp-plastic");
      try {
        const fd = new FormData();
        fd.append("file", file);
        const url = Constants.adminFinanceUploadWhatsAppPlasticUrl(plasticReplaceAll);
        const { data } = await axios.post(url, fd, {
          headers: { ...analyticsHeaders, "Content-Type": "multipart/form-data" },
        });
        setUploadLastResult({ key: "whatsapp-plastic", ...data });
        setFinanceMsg(
          `WhatsApp plastic chat: inserted ${data?.insertedCount ?? 0}, updated ${data?.updatedCount ?? 0}, skipped ${data?.skippedCount ?? 0}.\n` +
            (Array.isArray(data?.messages) ? data.messages.join("\n") : "")
        );
        await loadPlasticLedger();
      } catch (e) {
        if (e?.response?.status === 401) handleFinanceUnauthorized();
        else setFinanceErr(e?.response?.data?.message || e?.message || "Plastic upload failed.");
      } finally {
        setUploadBusyKey("");
      }
    },
    [analyticsHeaders, handleFinanceUnauthorized, plasticReplaceAll, loadPlasticLedger]
  );

  const loadPetpoojaMonthly = useCallback(async () => {
    if (role !== SUPER_ADMIN || !analyticsToken) return;
    setPetpoojaMonthlyBusy(true);
    try {
      const { data } = await axios.get(Constants.ADMIN_FINANCE_PETPOOJA_MONTHLY, {
        headers: analyticsHeaders,
      });
      setPetpoojaMonthly(Array.isArray(data) ? data : []);
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
      else setFinanceErr(e?.response?.data?.message || e?.message || "Petpooja monthly load failed.");
      setPetpoojaMonthly([]);
    } finally {
      setPetpoojaMonthlyBusy(false);
    }
  }, [role, analyticsToken, analyticsHeaders, handleFinanceUnauthorized]);

  useEffect(() => {
    if (role !== SUPER_ADMIN || !analyticsToken || bizTab !== "uploads") return undefined;
    loadReconLedger();
    loadReconRules();
    loadStockBundle();
    loadVendorIncome();
    loadDayendMatch();
    loadSalaryMonths();
    loadHyperItems();
    loadPlasticLedger();
    loadPetpoojaMonthly();
    return undefined;
  }, [
    role,
    analyticsToken,
    bizTab,
    loadReconLedger,
    loadReconRules,
    loadStockBundle,
    loadVendorIncome,
    loadDayendMatch,
    loadSalaryMonths,
    loadHyperItems,
    loadPlasticLedger,
    loadPetpoojaMonthly,
  ]);

  useEffect(() => {
    if (role !== SUPER_ADMIN || !analyticsToken || bizTab !== "uploads" || uploadReconView !== "plastic") return undefined;
    loadPlasticLedger();
    loadPlasticSettings();
    return undefined;
  }, [role, analyticsToken, bizTab, uploadReconView, reconPeriod, loadPlasticLedger, loadPlasticSettings]);

  useEffect(() => {
    if (role !== SUPER_ADMIN || !analyticsToken || bizTab !== "uploads" || !salaryPick) return undefined;
    loadSalaryRows(salaryPick);
    return undefined;
  }, [role, analyticsToken, bizTab, salaryPick, loadSalaryRows]);

  useEffect(() => {
    if (role !== SUPER_ADMIN || !analyticsToken || bizTab !== "banquet") return undefined;
    loadBanquetMonthly();
    return undefined;
  }, [role, analyticsToken, bizTab, loadBanquetMonthly]);

  useEffect(() => {
    if (role !== SUPER_ADMIN || !analyticsToken || bizTab !== "banquet") return undefined;
    loadBanquetBookingLinesForMonth(banquetLinesMonth);
    return undefined;
  }, [role, analyticsToken, bizTab, banquetLinesMonth, loadBanquetBookingLinesForMonth]);

  const latestBanquetMonthKey =
    (banquetRows || []).length > 0
      ? [...banquetRows]
          .map((r) => r.periodKey)
          .filter(Boolean)
          .sort((a, b) => String(b).localeCompare(String(a)))[0]
      : "";

  useEffect(() => {
    if (role !== SUPER_ADMIN || !analyticsToken || bizTab !== "expenses") return undefined;
    loadExpensePresetsOnce();
    loadMonthlyOverview(maintPickMonth);
    loadMonthlyComparison(maintPickMonth, comparisonMonths);
    loadMonthlyCash(maintPickMonth);
    loadCashIncome(maintPickMonth);
    loadSalaryRows(maintPickMonth);
    loadSalaryAdvanceSummary(maintPickMonth);
    return undefined;
  }, [role, analyticsToken, bizTab, loadExpensePresetsOnce, loadMonthlyOverview, loadMonthlyComparison, loadMonthlyCash, loadCashIncome, loadSalaryRows, loadSalaryAdvanceSummary, maintPickMonth, comparisonMonths]);

  useEffect(() => {
    if (role !== SUPER_ADMIN || !analyticsToken || bizTab !== "business-trend") return undefined;
    loadMonthlyComparison(maintPickMonth, comparisonMonths);
    return undefined;
  }, [role, analyticsToken, bizTab, maintPickMonth, comparisonMonths, loadMonthlyComparison]);

  useEffect(() => {
    if (role !== SUPER_ADMIN || !analyticsToken || bizTab !== "expenses" || monthlyCostsView !== "disposal") return undefined;
    loadPlasticLedger(maintPickMonth);
    return undefined;
  }, [role, analyticsToken, bizTab, monthlyCostsView, maintPickMonth, loadPlasticLedger]);

  useEffect(() => {
    if (role !== SUPER_ADMIN || !analyticsToken || bizTab !== "expenses" || monthlyCostsView !== "hyper") return undefined;
    loadHyperItems();
    return undefined;
  }, [role, analyticsToken, bizTab, monthlyCostsView, maintPickMonth, loadHyperItems]);

  useEffect(() => {
    if (role !== SUPER_ADMIN || !analyticsToken || bizTab !== "expenses" || monthlyCostsView !== "coal") return undefined;
    loadCoalLedger();
    loadCoalSettings();
    return undefined;
  }, [role, analyticsToken, bizTab, monthlyCostsView, maintPickMonth, loadCoalLedger, loadCoalSettings]);

  useEffect(() => {
    if (role !== SUPER_ADMIN || !analyticsToken || bizTab !== "expenses" || monthlyCostsView !== "dairy") return undefined;
    loadDairyLedger();
    loadDairySettings();
    return undefined;
  }, [role, analyticsToken, bizTab, monthlyCostsView, maintPickMonth, loadDairyLedger, loadDairySettings]);

  useEffect(() => {
    if (role !== SUPER_ADMIN || !analyticsToken || bizTab !== "expenses" || monthlyCostsView !== "ice-cream") return undefined;
    loadIceCreamLedger();
    loadIceCreamSettings();
    return undefined;
  }, [role, analyticsToken, bizTab, monthlyCostsView, maintPickMonth, loadIceCreamLedger, loadIceCreamSettings]);

  useEffect(() => {
    if (role !== SUPER_ADMIN || !analyticsToken || bizTab !== "expenses" || monthlyCostsView !== "debt") return undefined;
    loadVendorDebts();
    return undefined;
  }, [role, analyticsToken, bizTab, monthlyCostsView, maintPickMonth, loadVendorDebts]);

  useEffect(() => {
    if (uploadLastResult?.key === "whatsapp-plastic") {
      loadPlasticLedger();
    }
  }, [uploadLastResult, loadPlasticLedger]);

  useEffect(() => {
    if (role !== SUPER_ADMIN || !analyticsToken || bizTab !== "partner") return undefined;
    loadPartnerSettings();
    loadPartnerShare();
    return undefined;
  }, [role, analyticsToken, bizTab, loadPartnerSettings, loadPartnerShare]);

  useEffect(() => {
    if (role !== SUPER_ADMIN || !analyticsToken || bizTab !== "uploads" || uploadReconView !== "hyper") return undefined;
    loadHyperCompare();
    return undefined;
  }, [role, analyticsToken, bizTab, uploadReconView, reconPeriod, hyperCompareMonths, loadHyperCompare]);

  useEffect(() => {
    if (role !== SUPER_ADMIN || !analyticsToken || bizTab !== "uploads" || uploadReconView !== "hyper-lines") {
      return undefined;
    }
    loadHyperOrders();
    return undefined;
  }, [role, analyticsToken, bizTab, uploadReconView, reconPeriod, loadHyperOrders]);

  useEffect(() => {
    if (role !== SUPER_ADMIN || !analyticsToken || bizTab !== "uploads") return undefined;
    loadHyperGmailStatus();
    return undefined;
  }, [role, analyticsToken, bizTab, loadHyperGmailStatus]);

  useEffect(() => {
    if (role !== SUPER_ADMIN || !analyticsToken || bizTab !== "uploads") return undefined;
    const active = hyperGmailStatus?.backfillActive || hyperGmailStatus?.backfillRunning;
    if (!active) return undefined;
    const id = window.setInterval(() => loadHyperGmailStatus(), 10000);
    return () => window.clearInterval(id);
  }, [role, analyticsToken, bizTab, hyperGmailStatus?.backfillActive, hyperGmailStatus?.backfillRunning, loadHyperGmailStatus]);

  /** Daily manual expense rows when Expenses tab or month changes. */
  useEffect(() => {
    if (role !== SUPER_ADMIN || !analyticsToken || bizTab !== "expenses") return undefined;
    loadDailyExpensesForMonthPick();
    return undefined;
  }, [role, analyticsToken, bizTab, expensePickMonth, loadDailyExpensesForMonthPick]);

  useEffect(() => {
    if (role !== SUPER_ADMIN || !analyticsToken || bizTab !== "profit") return undefined;
    loadProfit();
    return undefined;
  }, [role, analyticsToken, bizTab, loadProfit]);

  useEffect(() => {
    if (role !== SUPER_ADMIN || !analyticsToken || bizTab !== "overview") return undefined;
    loadExecutiveSummary();
    return undefined;
  }, [role, analyticsToken, bizTab, loadExecutiveSummary]);

  const salaryPaidTotal = useMemo(
    () => sumSalaryPaidRupee(salaryRows, monthlyOverview),
    [salaryRows, monthlyOverview]
  );

  const salaryBankTotal = useMemo(
    () =>
      (salaryRows || []).reduce(
        (s, r) => s + (r.paymentChannel === "BANK" ? Number(r.paidSalaryRupee) || 0 : 0),
        0
      ),
    [salaryRows]
  );

  const salaryCashTotal = useMemo(
    () =>
      (salaryRows || []).reduce(
        (s, r) => s + (r.paymentChannel === "CASH" ? Number(r.paidSalaryRupee) || 0 : 0),
        0
      ),
    [salaryRows]
  );

  const bankOutFiltered = useMemo(
    () =>
      filterFinanceRows(monthlyOverview?.expenseCategories, {
        categoryField: "category",
        categoryValue: bankOutFilterCat,
        minRupee: bankOutFilterMin,
        searchText: bankOutSearch,
      }),
    [monthlyOverview?.expenseCategories, bankOutFilterCat, bankOutFilterMin, bankOutSearch]
  );

  const bankOutFilteredTotal = useMemo(
    () => bankOutFiltered.reduce((s, c) => s + rowAmountRupee(c), 0),
    [bankOutFiltered]
  );

  const bankInFiltered = useMemo(
    () =>
      filterFinanceRows(monthlyOverview?.creditRows, {
        categoryField: "vendorLabel",
        categoryValue: creditFilterVendor,
        minRupee: creditFilterMin,
        searchText: creditSearch,
      }),
    [monthlyOverview?.creditRows, creditFilterVendor, creditFilterMin, creditSearch]
  );

  const bankInFilteredTotal = useMemo(
    () => bankInFiltered.reduce((s, r) => s + rowAmountRupee(r), 0),
    [bankInFiltered]
  );

  const saveAdvanceEntry = useCallback(async () => {
    if (role !== SUPER_ADMIN || !analyticsToken || !maintPickMonth) return;
    const name = String(advEntryName || "").trim();
    if (!name) {
      setFinanceErr("Employee name is required for advance entry.");
      return;
    }
    const advance = Number(advEntryAmount) || 0;
    const recovery = Number(advRecoveryAmount) || 0;
    if (advance <= 0 && recovery <= 0) {
      setFinanceErr("Enter a new advance amount and/or a recovery amount.");
      return;
    }
    setAdvEntryBusy(true);
    setFinanceErr("");
    try {
      await axios.post(
        Constants.ADMIN_FINANCE_EMP_SALARY_ADVANCE_ENTRY,
        {
          periodKey: maintPickMonth,
          empName: name,
          advanceRupee: advance,
          recoveryRupee: recovery,
        },
        { headers: analyticsHeaders }
      );
      setFinanceMsg(recovery > 0 ? "Advance / recovery saved." : "Advance appended for employee.");
      setAdvEntryName("");
      setAdvEntryAmount("");
      setAdvRecoveryAmount("");
      await loadSalaryRows(maintPickMonth);
      await loadSalaryAdvanceSummary(maintPickMonth);
    } catch (e) {
      if (e?.response?.status === 401) handleFinanceUnauthorized();
      else setFinanceErr(e?.response?.data?.message || e?.message || "Could not save advance entry.");
    } finally {
      setAdvEntryBusy(false);
    }
  }, [
    role,
    analyticsToken,
    analyticsHeaders,
    maintPickMonth,
    advEntryName,
    advEntryAmount,
    advRecoveryAmount,
    loadSalaryRows,
    loadSalaryAdvanceSummary,
    handleFinanceUnauthorized,
  ]);

  const bizMainNavItems = useMemo(
    () => [
      { group: "Bookings", key: "partyboss", label: "Partyboss" },
      { key: "banquet", label: "Banquet hall" },
      { group: "Finance", key: "profit", label: "Profit" },
      { key: "overview", label: "Business position" },
      { key: "business-trend", label: "Business trend" },
      { key: "expenses", label: "Monthly costs" },
      { key: "uploads", label: "Uploads & Recon" },
      { key: "partner", label: "Partner share" },
      { group: "Admin", key: "register-otp", label: "Register email for OTP" },
    ],
    []
  );

  const partybossNavItems = useMemo(
    () => [
      { key: "overview", label: "Overview & charts" },
      { key: "tables", label: "Monthly tables" },
      { key: "cancelled-events", label: "Cancelled Events" },
      { key: "theatres", label: "Theatres" },
    ],
    []
  );

  const banquetNavItems = useMemo(
    () => [
      { key: "rollup", label: "Upload & roll-up" },
      { key: "lines", label: "Line bookings" },
    ],
    []
  );

  const monthlyCostsNavItems = useMemo(
    () => [
      { group: "Property", key: "rent", label: "Rent", badge: formatInr(sumRentMaint(monthlyOverview)) },
      {
        key: "digital",
        label: "Digital media",
        badge: formatInr(monthlyOverview?.digitalMediaRupee),
      },
      { group: "Payroll", key: "salaries", label: "Salaries", badge: formatInr(salaryPaidTotal) },
      { key: "advances", label: "Salary advances" },
      {
        group: "Suppliers",
        key: "electricity",
        label: "Electricity",
        badge: formatInr(monthlyOverview?.electricityRupee),
      },
      { key: "gas", label: "Gas", badge: formatInr(monthlyOverview?.gasRupee) },
      { key: "hyper", label: "Hyper", badge: formatInr(monthlyOverview?.hyperRupee) },
      {
        key: "coal",
        label: "Coal",
        badge: formatInr(
          bankPlusCash(monthlyOverview?.coalRupee ?? coalLedger?.monthSpendRupee, cashBySlot.coal)
        ),
      },
      {
        key: "dairy",
        label: "Dairy",
        badge: formatInr(
          bankPlusCash(monthlyOverview?.dairyRupee ?? dairyLedger?.monthSpendRupee, cashBySlot.dairy)
        ),
      },
      {
        key: "ice-cream",
        label: "Ice Cream",
        badge: formatInr(
          bankPlusCash(
            monthlyOverview?.iceCreamRupee ?? iceCreamLedger?.monthSpendRupee,
            cashBySlot["ice-cream"]
          )
        ),
      },
      {
        key: "meat",
        label: "Meat",
        badge: formatInr(monthlyOverview?.meatTotalRupee),
      },
      {
        key: "disposal",
        label: "Disposal",
        badge: formatInr(plasticLedger?.monthSpendRupee ?? monthlyOverview?.plasticSpendRupee),
      },
      { key: "vegetables", label: "Vegetables", badge: formatInr(monthlyOverview?.vegetableTotalRupee) },
      {
        key: "daily",
        label: "Daily expense",
        badge: formatInr(monthlyOverview?.dailyManualExpenseRupee),
      },
      {
        group: "Dine-out credits",
        key: "easydiner",
        label: "EasyDiner",
        badge: formatInr(monthlyOverview?.easydinerCreditRupee),
      },
      { key: "swiggy-dineout", label: "Swiggy", badge: formatInr(monthlyOverview?.swiggyBundlCreditRupee) },
      { key: "zomato-dineout", label: "Zomato", badge: formatInr(monthlyOverview?.zomatoEternalCreditRupee) },
      { key: "sbi-banquet", label: "SBI Banquet", badge: formatInr(monthlyOverview?.sbiBanquetCreditRupee) },
      {
        group: "Bank",
        key: "bank-out",
        label: "Bank spends",
        badge: formatInr(monthlyOverview?.bankDebitTotalRupee),
      },
      {
        key: "bank-in",
        label: "Bank credits",
        badge: formatInr(
          (Number(monthlyOverview?.bankCreditIciciRupee) || 0) +
            (Number(monthlyOverview?.bankCreditIdfcRupee) || 0)
        ),
      },
      {
        group: "Summary",
        key: "cash-income",
        label: "Cash income",
        badge: formatInr(cashIncome?.totalCashIncomeRupee),
      },
      {
        key: "cash-total",
        label: "Monthly cash",
        badge: formatInr(monthlyOverview?.monthlyCashTotalRupee ?? monthlyCash?.totalCashRupee),
      },
      {
        key: "debt",
        label: "Vendor debt",
        badge: formatInr(vendorDebtSummary?.totalNetPayableRupee),
      },
    ],
    [
      monthlyOverview,
      salaryPaidTotal,
      coalLedger,
      dairyLedger,
      iceCreamLedger,
      cashBySlot,
      plasticLedger,
      vendorDebtSummary,
      monthlyCash,
      cashIncome,
    ]
  );

  const uploadReconNavItems = useMemo(
    () => [
      { group: "Uploads", key: "all-uploads", label: "All uploads" },
      { group: "Bank", key: "ledger", label: "Ledger", badge: String(reconLedger.length) },
      { key: "rules", label: "Rules", badge: String(reconRules.length) },
      { key: "stock", label: "Stock report" },
      { group: "Imports", key: "hyper", label: "Hyper", badge: String(hyperItems.length) },
      { key: "hyper-lines", label: "Hyper line items", badge: String(hyperOrders.length) },
      { key: "plastic", label: "Plastic", badge: String(plasticLedger?.orders?.length ?? 0) },
      { key: "vendors", label: "Vendors", badge: String(vendorIncomeRows.length) },
      { key: "dayend", label: "Day-end", badge: String(dayendMatchRows.length) },
      { key: "petpooja", label: "Petpooja", badge: String(petpoojaMonthly.length) },
      { key: "salary", label: "Salary" },
    ],
    [
      reconLedger.length,
      reconRules.length,
      hyperItems.length,
      hyperOrders.length,
      plasticLedger,
      vendorIncomeRows.length,
      dayendMatchRows.length,
      petpoojaMonthly.length,
    ]
  );

  const clearAnalyticsSession = () => {
    sessionStorage.removeItem(ANALYTICS_SESSION_STORAGE_TOKEN);
    sessionStorage.removeItem(ANALYTICS_SESSION_STORAGE_EMAIL);
    setAnalyticsToken("");
    setVerifiedEmailLabel("");
    setOtpCodeInput("");
    setOtpTargetEmail("");
    setPayload(null);
    setError("");
  };

  if (role === SUPER_ADMIN && !analyticsToken) {
    return (
      <AdminLayout title="Booking analytics">
        <div className="admin-console-content py-4 text-start analytics-privacy-scope">
          <h2 className="h4 text-white mb-3">Verify your inbox (OTP)</h2>
          <p className="text-secondary small mb-2">
            Tap <strong>Send OTP</strong> — the same six-digit code is emailed to every registered analytics inbox.
            Enter the code below. Code expires in 5 minutes. Analytics session lasts 30 minutes. After 3 wrong tries,
            wait 30 minutes or send a new code.
          </p>
          <Alert variant="secondary" className="small py-2">
            Analytics unlock is stored in <strong>sessionStorage</strong> for this site — it is cleared when{" "}
            <strong>all browser windows for this site are closed</strong>. Use{" "}
            <em>Clear OTP session</em> inside analytics after verifying to revoke early.
          </Alert>
          {otpFlash ? <Alert variant="info">{otpFlash}</Alert> : null}
          {error ? <Alert variant="danger">{error}</Alert> : null}
          <div className="admin-console-card p-3 mb-4 rounded border border-secondary">
            <div className="d-flex flex-wrap gap-2 mb-3">
              <Button
                variant="primary"
                size="sm"
                disabled={otpBusy}
                type="button"
                onClick={() => sendAnalyticsOtp()}
              >
                Send OTP
              </Button>
            </div>
            <Form.Group className="mb-2">
              <Form.Label className="text-white-50 small">6-digit code</Form.Label>
              <Form.Control
                type="text"
                inputMode="numeric"
                maxLength={6}
                className="text-bg-dark border-secondary"
                style={{ maxWidth: 160 }}
                value={otpCodeInput}
                onChange={(e) => setOtpCodeInput(e.target.value.replace(/\D/g, "").slice(0, 6))}
              />
            </Form.Group>
            <Button
              variant="success"
              size="sm"
              disabled={otpBusy || otpCodeInput.length !== 6}
              type="button"
              onClick={async () => {
                setOtpBusy(true);
                setError("");
                try {
                  const verifyEmail = resolveOtpEmail();
                  const { data } = await axios.post(
                    Constants.ADMIN_BOOKING_ANALYTICS_OTP_VERIFY,
                    {
                      ...(verifyEmail && otpRegisteredEmails.length === 1 ? { email: verifyEmail } : {}),
                      otpCode: otpCodeInput,
                    },
                    { headers: adminOnlyHeaders }
                  );
                  const tok = data?.sessionToken;
                  if (!tok) throw new Error("No session returned");
                  const verifiedEmail = data?.email || verifyEmail;
                  sessionStorage.setItem(ANALYTICS_SESSION_STORAGE_TOKEN, tok);
                  if (verifiedEmail) {
                    sessionStorage.setItem(ANALYTICS_SESSION_STORAGE_EMAIL, verifiedEmail);
                    setVerifiedEmailLabel(verifiedEmail);
                  }
                  setAnalyticsToken(tok);
                  setOtpFlash("");
                  setOtpCodeInput("");
                  setOtpTargetEmail("");
                } catch (e) {
                  setError(axiosErrorMessage(e, "Verification failed."));
                } finally {
                  setOtpBusy(false);
                }
              }}
            >
              Verify and continue
            </Button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Booking analytics">
      <div
        className={`admin-console-content py-4 text-start booking-analytics-page${themeId === "premium" ? " booking-analytics-page--lounge" : ""} ${analyticsPrivacyClass(privacyBlur)}`}
      >
        <div className="analytics-theme-toolbar">
          <h2 className="h4 mb-0">Booking analytics</h2>
          <div className="ms-auto">
            <SiteThemePicker label="Site look &amp; feel" hint="Switch to Lounge for the new glass UI" />
          </div>
        </div>
        {themeId !== "premium" ? (
          <Alert variant="warning" className="py-2 px-3 small mb-3 border border-warning border-opacity-25 d-flex flex-wrap align-items-center gap-2">
            <span className="mb-0">
              You are on <strong>Classic</strong>. Switch to <strong>Lounge</strong> for the glass Dark Mode UI (mockups).
            </span>
            <Button size="sm" variant="light" type="button" onClick={() => setThemeId("premium")}>
              Enable Lounge now
            </Button>
          </Alert>
        ) : null}
        <p className="text-secondary small mb-1">
          Logged-in analytics inbox:&nbsp;
          <strong className="text-white">{verifiedEmailLabel || "(unknown)"}</strong>
          {" "}
          <button type="button" className="btn btn-link btn-sm text-warning align-baseline px-1 py-0" onClick={clearAnalyticsSession}>
            Clear OTP session
          </button>
        </p>
        <p className="text-secondary small mb-4">
          Buckets use each booking&apos;s <strong>event date</strong> (IST). Revenue sums{" "}
          <strong>final invoice</strong> for non-cancelled rows only. Compare blocks use calendar months.
        </p>

        {error ? <Alert variant="danger">{error}</Alert> : null}
        {financeErr ? <Alert variant="danger">{financeErr}</Alert> : null}
        {financeMsg ? (
          <Alert variant="secondary" dismissible onClose={() => setFinanceMsg("")}>
            <pre className="mb-0 small text-secondary" style={{ whiteSpace: "pre-wrap" }}>
              {financeMsg}
            </pre>
          </Alert>
        ) : null}

        <AnalyticsSubsectionLayout
          className="analytics-biz-root mb-3"
          sidebarTitle="Booking analytics"
          items={bizMainNavItems}
          activeKey={bizTab}
          onSelect={(k) => {
            if (typeof k === "string") {
              setBizTab(k);
              setFinanceErr("");
            }
          }}
        >
          <AnalyticsSubsectionPanel activeKey={bizTab} panelKey="partyboss">
            <AnalyticsLoungeDashboard
              title="Partyboss"
              subtitle="Bookings and revenue by event date (IST)."
              pillItems={partybossNavItems}
              activePill={partybossView}
              onPillSelect={setPartybossView}
              showCharts={partybossView === "overview" && labels.length > 0}
              charts={
                partybossView === "overview" && labels.length > 0 ? (
                  <>
                    <RichLineChart
                      title="Revenue trends"
                      subtitle="Non-cancelled booking revenue"
                      badge="Partyboss"
                      height={200}
                      labels={labels}
                      series={[
                        { key: "bookings", values: bookingCounts },
                        { key: "revenue", values: revenues },
                      ]}
                      cancelledCounts={cancelledCounts}
                    />
                    <RichGroupedBarChart labels={labels} bookings={bookingCounts} cancelled={cancelledCounts} />
                  </>
                ) : null
              }
              toolbar={
                <>
                  <Form.Label className="text-white-50 small mb-0">Chart period</Form.Label>
                  <Form.Select
                    size="sm"
                    style={{ maxWidth: 220 }}
                    value={granularity}
                    onChange={(e) => setGranularity(e.target.value)}
                  >
                    <option value="daily">Daily — last 30 days</option>
                    <option value="monthly">Monthly — last 12 months</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="halfyearly">Half-yearly</option>
                  </Form.Select>
                  <span className="text-secondary small">Tables lookback:</span>
                  <Form.Select
                    size="sm"
                    style={{ maxWidth: 140 }}
                    value={tableMonths}
                    onChange={(e) => setTableMonths(Number(e.target.value))}
                  >
                    <option value={12}>12 mo</option>
                    <option value={24}>24 mo</option>
                    <option value={36}>36 mo</option>
                    <option value={48}>48 mo</option>
                    <option value={60}>60 mo</option>
                  </Form.Select>
                  <button type="button" className="btn btn-sm btn-outline-light" onClick={load} disabled={loading}>
                    Refresh
                  </button>
                  {loading ? <Spinner animation="border" size="sm" className="text-light" /> : null}
                </>
              }
            >
              <AnalyticsSubsectionPanel activeKey={partybossView} panelKey="overview">
        {mom ? (
          <div className="row g-3 mb-4">
            <div className="col-md-6">
              <div className="admin-console-card p-3 h-100 rounded border border-secondary">
                <h3 className="h6 text-info mb-3">This month vs last — bookings</h3>
                <p className="mb-1 text-white">
                  <strong>{mom.currentLabel}</strong>: {mom.currentBookings} active,&nbsp;
                  {mom.currentCancelled} cancelled
                </p>
                <p className="mb-1 text-secondary small">
                  <strong>{mom.previousLabel}</strong>: {mom.previousBookings} active,&nbsp;
                  {mom.previousCancelled} cancelled
                </p>
                <p
                  className={`mb-0 mt-2 fw-bold ${mom.bookingsDelta >= 0 ? "text-success" : "text-warning"}`}
                >
                  Δ Active bookings: {mom.bookingsDelta >= 0 ? "+" : ""}
                  {mom.bookingsDelta}
                  {mom.bookingsDelta < 0 ? " — fewer than last month" : " — more than last month"}
                </p>
              </div>
            </div>
            <div className="col-md-6">
              <div className="admin-console-card p-3 h-100 rounded border border-secondary">
                <h3 className="h6 text-info mb-3">This month vs last — revenue</h3>
                <p className="mb-1 text-white">{formatInr(mom.currentRevenue)} vs {formatInr(mom.previousRevenue)}</p>
                <p
                  className={`mb-0 fw-bold ${mom.revenueDelta >= 0 ? "text-success" : "text-danger"}`}
                >
                  Δ {formatInr(mom.revenueDelta)}
                  {mom.revenueDelta < 0 ? " — lower revenue than last month" : " — higher revenue than last month"}
                </p>
              </div>
            </div>
          </div>
        ) : null}
              </AnalyticsSubsectionPanel>

              <AnalyticsSubsectionPanel activeKey={partybossView} panelKey="tables">
        <div className="admin-console-card p-3 mb-4 rounded border border-secondary">
          <h3 className="h6 text-info mb-3">Calendar month tables (filter)</h3>
          <p className="small text-secondary mb-3">
            Tables use IST calendar months based on booking <strong>event date</strong> — same basis as charts.
            Default filter is the last 12 months through the current month; extend &quot;To month&quot; to include
            scheduled future events. “Δ vs prior month” compares each month to the immediate prior calendar month.
          </p>
          <div className="d-flex flex-wrap align-items-end gap-3 mb-4">
            <div>
              <Form.Label className="text-white-50 small d-block mb-1">From month</Form.Label>
              <AdminDateInput
                inputType="month"
                size="sm"
                style={{ width: 180 }}
                min={minPeriodKey || undefined}
                max={(tableToKey && tableToKey < maxPeriodKey ? tableToKey : maxPeriodKey) || undefined}
                disabled={!minPeriodKey}
                value={tableFromKey || ""}
                onChange={(e) => setTableFromKey(e.target.value)}
              />
            </div>
            <div>
              <Form.Label className="text-white-50 small d-block mb-1">To month</Form.Label>
              <AdminDateInput
                inputType="month"
                size="sm"
                style={{ width: 180 }}
                min={(tableFromKey && tableFromKey > minPeriodKey ? tableFromKey : minPeriodKey) || undefined}
                max={maxPeriodKey || undefined}
                disabled={!maxPeriodKey}
                value={tableToKey || ""}
                onChange={(e) => setTableToKey(e.target.value)}
              />
            </div>
          </div>

          {filteredActiveFutureDesc.length > 0 ? (
            <details open className="mb-3 border border-info rounded p-3">
              <summary className="text-info small fw-semibold user-select-none">
                Active bookings — current &amp; future months (by event date)
              </summary>
              <p className="small text-secondary mt-2 mb-2">
                Counts non-cancelled active bookings per calendar month from this month forward.
                Current month also shows today&apos;s event-day totals (IST).
              </p>
              <Table striped bordered hover size="sm" variant="dark" responsive className="mb-0">
                <thead>
                  <tr>
                    <th>Calendar month</th>
                    <th className="text-end">Active bookings</th>
                    <th className="text-end">Today&apos;s event-day bookings</th>
                    <th className="text-end">Today&apos;s active slots</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredActiveFutureDesc.map((r) => (
                    <tr key={`af-${r.periodKey}`}>
                      <td>{r.displayLabel}</td>
                      <td className="text-end">{r.activeBookings}</td>
                      <td className="text-end text-secondary">
                        {r.periodKey === todayPeriodKey
                          ? r.todayEventDayBookings ?? "—"
                          : "—"}
                      </td>
                      <td className="text-end text-secondary">
                        {r.periodKey === todayPeriodKey
                          ? r.todayActiveBookings ?? "—"
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </details>
          ) : null}

          {filteredMonthlyDesc.length === 0 ? (
            <p className="text-secondary small mb-0">Nothing in this month range.</p>
          ) : (
            <>
              <details open className="mb-3 border border-secondary rounded p-3">
                <summary className="text-white-50 small fw-semibold user-select-none">
                  Monthly — active bookings (&amp; change vs prior month)
                </summary>
                <Table striped bordered hover size="sm" variant="dark" responsive className="mb-0 mt-3">
                  <thead>
                    <tr>
                      <th>Calendar month</th>
                      <th className="text-end">Active bookings</th>
                      <th className="text-end">Cancelled count</th>
                      <th className="text-end">Δ vs prior month (active)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMonthlyDesc.map((r) => (
                      <tr key={`b-${r.periodKey}`}>
                        <td>{r.displayLabel}</td>
                        <td className="text-end">{r.bookings}</td>
                        <td className="text-end">{r.cancelled}</td>
                        <td
                          className={`text-end ${
                            r.bookingsDeltaVsPriorMonth === null || r.bookingsDeltaVsPriorMonth === undefined
                              ? ""
                              : r.bookingsDeltaVsPriorMonth >= 0
                                ? "text-success"
                                : "text-warning"
                          }`}
                        >
                          {formatSignedBookingDelta(r.bookingsDeltaVsPriorMonth)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </details>

              <details className="mb-0 border border-secondary rounded p-3">
                <summary className="text-white-50 small fw-semibold user-select-none">
                  Monthly — revenue (&amp; change vs prior month)
                </summary>
                <Table striped bordered hover size="sm" variant="dark" responsive className="mb-0 mt-3">
                  <thead>
                    <tr>
                      <th>Calendar month</th>
                      <th className="text-end">Revenue (non-cancelled)</th>
                      <th className="text-end">Δ vs prior month</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMonthlyDesc.map((r) => (
                      <tr key={`r-${r.periodKey}`}>
                        <td>{r.displayLabel}</td>
                        <td className="text-end">{formatInr(r.revenue)}</td>
                        <td
                          className={`text-end ${
                            r.revenueDeltaVsPriorMonth === null || r.revenueDeltaVsPriorMonth === undefined
                              ? ""
                              : r.revenueDeltaVsPriorMonth >= 0
                                ? "text-success"
                                : "text-danger"
                          }`}
                        >
                          {formatSignedRevenueDelta(r.revenueDeltaVsPriorMonth)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </details>
            </>
          )}
        </div>
              </AnalyticsSubsectionPanel>

              <AnalyticsSubsectionPanel activeKey={partybossView} panelKey="cancelled-events">
        <div className="admin-console-card p-3 mb-4 rounded border border-secondary">
          <h3 className="h6 text-info mb-3">Cancelled Events</h3>
          <p className="small text-secondary mb-3">
            Audit log when admins cancel bookings (person name and message). Months follow cancellation time (IST).
            Filter by calendar month below (up to ~500 newest in the tables lookback window).
          </p>
          <div className="d-flex flex-wrap align-items-end gap-3 mb-4">
            <div>
              <Form.Label className="text-white-50 small d-block mb-1">From month</Form.Label>
              <AdminDateInput
                inputType="month"
                size="sm"
                style={{ width: 180 }}
                min={minPeriodKey || undefined}
                max={(tableToKey && tableToKey < maxPeriodKey ? tableToKey : maxPeriodKey) || undefined}
                disabled={!minPeriodKey}
                value={tableFromKey || ""}
                onChange={(e) => setTableFromKey(e.target.value)}
              />
            </div>
            <div>
              <Form.Label className="text-white-50 small d-block mb-1">To month</Form.Label>
              <AdminDateInput
                inputType="month"
                size="sm"
                style={{ width: 180 }}
                min={(tableFromKey && tableFromKey > minPeriodKey ? tableFromKey : minPeriodKey) || undefined}
                max={maxPeriodKey || undefined}
                disabled={!maxPeriodKey}
                value={tableToKey || ""}
                onChange={(e) => setTableToKey(e.target.value)}
              />
            </div>
          </div>
          {filteredCancellationDetails.length === 0 ? (
            <p className="text-secondary small mb-0">Nothing in range or none recorded yet.</p>
          ) : (
            <Table striped bordered hover size="sm" variant="dark" responsive className="mb-0">
              <thead>
                <tr>
                  <th>Cancelled (IST)</th>
                  <th>Month bucket</th>
                  <th>Booking ref</th>
                  <th>Theatre</th>
                  <th>Customer</th>
                  <th>Cancelled by</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                {filteredCancellationDetails.map((r) => (
                  <tr key={`${r.bookingReferenceId}-${r.cancellationTimeMillis}`}>
                    <td>{formatCancelledAtIst(r.cancellationTimeMillis)}</td>
                    <td>{r.calendarMonthDisplay}</td>
                    <td>{r.bookingReferenceId}</td>
                    <td>{r.theatreName}</td>
                    <td>{r.customerName}</td>
                    <td>{r.cancelledByPersonName}</td>
                    <td>{r.cancellationMessage}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
              </AnalyticsSubsectionPanel>

              <AnalyticsSubsectionPanel activeKey={partybossView} panelKey="theatres">
        <div className="admin-console-card p-3 rounded border border-secondary">
          <h3 className="h6 text-info mb-3">Theatres by calendar month (non-cancelled)</h3>
          <p className="small text-secondary mb-3">
            Up to twenty theatres per month by bookings. Empty months skipped. Filter by calendar month below.
          </p>
          <div className="d-flex flex-wrap align-items-end gap-3 mb-4">
            <div>
              <Form.Label className="text-white-50 small d-block mb-1">From month</Form.Label>
              <AdminDateInput
                inputType="month"
                size="sm"
                style={{ width: 180 }}
                min={minPeriodKey || undefined}
                max={(tableToKey && tableToKey < maxPeriodKey ? tableToKey : maxPeriodKey) || undefined}
                disabled={!minPeriodKey}
                value={tableFromKey || ""}
                onChange={(e) => setTableFromKey(e.target.value)}
              />
            </div>
            <div>
              <Form.Label className="text-white-50 small d-block mb-1">To month</Form.Label>
              <AdminDateInput
                inputType="month"
                size="sm"
                style={{ width: 180 }}
                min={(tableFromKey && tableFromKey > minPeriodKey ? tableFromKey : minPeriodKey) || undefined}
                max={maxPeriodKey || undefined}
                disabled={!maxPeriodKey}
                value={tableToKey || ""}
                onChange={(e) => setTableToKey(e.target.value)}
              />
            </div>
          </div>
          {!tableFromKey || !tableToKey ? (
            <p className="text-secondary small mb-0">Set the month filters above.</p>
          ) : filteredTheatreBundlesNewestFirst.length === 0 ? (
            <p className="text-secondary small mb-0">Nothing in this month range.</p>
          ) : (
            filteredTheatreBundlesNewestFirst.map((bundle) => (
              <div key={bundle.periodKey} className="mb-4">
                <h4 className="h6 text-white-50">{bundle.displayLabel}</h4>
                {bundle.theatres?.length ? (
                  <Table striped bordered hover size="sm" variant="dark" responsive className="mb-0">
                    <thead>
                      <tr>
                        <th>Theatre</th>
                        <th className="text-end">Bookings</th>
                        <th className="text-end">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bundle.theatres.map((row) => (
                        <tr key={`${bundle.periodKey}-${row.theatreName}`}>
                          <td>{row.theatreName}</td>
                          <td className="text-end">{row.bookings}</td>
                          <td className="text-end">{formatInr(row.revenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <p className="text-secondary small mb-0">No active theatre bookings recorded this month.</p>
                )}
              </div>
            ))
          )}
        </div>
              </AnalyticsSubsectionPanel>
            </AnalyticsLoungeDashboard>
          </AnalyticsSubsectionPanel>

          <AnalyticsSubsectionPanel activeKey={bizTab} panelKey="banquet">
            <AnalyticsLoungeDashboard
              title="Banquet hall"
              subtitle="Workbook upload, monthly roll-up, and line bookings."
              pillItems={banquetNavItems}
              activePill={banquetView}
              onPillSelect={setBanquetView}
              showCharts={banquetView === "rollup" && (banquetRows?.length || 0) > 0}
              charts={
                banquetView === "rollup" && (banquetRows?.length || 0) > 0 ? (
                  <RichLineChart
                    title="Banquet revenue trends"
                    subtitle="Roll-up totals by month"
                    badge="Banquet"
                    height={200}
                    labels={[...(banquetRows || [])]
                      .slice()
                      .sort((a, b) => String(a.periodKey).localeCompare(String(b.periodKey)))
                      .map((r) => r.periodKey)}
                    series={[
                      {
                        key: "revenue",
                        values: [...(banquetRows || [])]
                          .slice()
                          .sort((a, b) => String(a.periodKey).localeCompare(String(b.periodKey)))
                          .map((r) => Number(r.banquetRevenueRupee) || 0),
                      },
                    ]}
                  />
                ) : null
              }
            >
              <AnalyticsSubsectionPanel activeKey={banquetView} panelKey="rollup">
            <div className="admin-console-card p-3 rounded border border-secondary mb-4">
              <h3 className="h6 text-info mb-2">Excel upload — banquet workbook</h3>
              <p className="small text-secondary mb-3">
                Name sheet tabs like <code>Aug24</code>, <code>March25</code>, etc. The importer maps headers such as{" "}
                <strong>Date</strong>, <strong>Amount</strong> or <strong>Total Amount</strong>, <strong>No.Pax</strong> +{" "}
                <strong>Price</strong> (incl. typo <code>Pice</code>) and persists <strong>line bookings</strong>. If a
                row has pax + price but no amount, total is <strong>pax × price</strong>. If row-by-row parsing fails, the
                legacy column sum is used for that tab.
              </p>
              <div className="d-flex flex-wrap gap-3 align-items-center mb-3">
                <Form.Control
                  type="file"
                  accept=".xlsx,.xls"
                  className="text-bg-dark border-secondary"
                  disabled={banquetUploadBusy}
                  style={{ maxWidth: 340 }}
                  onChange={(ev) => {
                    const file = ev.target.files?.[0];
                    ev.target.value = "";
                    uploadBanquetWorkbookFile(file || null);
                  }}
                />
                <Button
                  variant="outline-light"
                  size="sm"
                  disabled={banquetUploadBusy || banquetLinesBusy}
                  onClick={async () => {
                    await loadBanquetMonthly();
                    await loadBanquetBookingLinesForMonth(banquetLinesMonth);
                  }}
                >
                  Reload roll-up table
                </Button>
              </div>
              {banquetUploadBusy ? <Spinner animation="border" size="sm" className="text-light mb-3" /> : null}
              <p className="small text-secondary mb-2">
                <strong>Tip:</strong> Click a month in the table below to load that month&apos;s lines in the next card.
              </p>
              <Table striped bordered hover size="sm" variant="dark" responsive className="mb-0">
                <thead>
                  <tr>
                    <th>Month key</th>
                    <th className="text-end">Banquet ₹ (roll-up)</th>
                    <th>Last upload</th>
                  </tr>
                </thead>
                <tbody>
                  {[...(banquetRows || [])]
                    .slice()
                    .sort((a, b) => String(b.periodKey).localeCompare(String(a.periodKey)))
                    .map((r) => (
                      <tr
                        key={r.periodKey}
                        tabIndex={0}
                        role="button"
                        title="Click to load line bookings for this month"
                        style={{ cursor: "pointer" }}
                        className={
                          String(banquetLinesMonth) === String(r.periodKey) ? "table-active" : ""
                        }
                        onClick={() => setBanquetLinesMonth(r.periodKey)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setBanquetLinesMonth(r.periodKey);
                          }
                        }}
                      >
                        <td>{r.periodKey}</td>
                        <td className="text-end">{formatInr(r.banquetRevenueRupee)}</td>
                        <td className="small text-secondary text-truncate" style={{ maxWidth: 220 }} title={r.lastImportedFile || ""}>
                          {r.lastImportedFile || "—"}
                        </td>
                      </tr>
                    ))}
                  {banquetRows?.length ? null : (
                    <tr>
                      <td colSpan={3} className="text-secondary small">
                        Upload the workbook to hydrate this table — it stays server-side until the next migration reset.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
              </AnalyticsSubsectionPanel>

              <AnalyticsSubsectionPanel activeKey={banquetView} panelKey="lines">
            <div className="admin-console-card p-3 rounded border border-secondary">
              <h3 className="h6 text-info mb-2">Month detail — line bookings (spreadsheet columns)</h3>
              <p className="small text-secondary mb-3">
                The <strong>calendar month</strong> here must match a <code>yyyy-mm</code> key from the roll-up table above
                (from each sheet tab, e.g. <code>Aug24</code> → <code>2024-08</code>), not necessarily the current month.
                Active rows in view:{" "}
                <strong>{(banquetBookingLines || []).filter((x) => !x.cancelled).length}</strong> (cancelled excluded from
                count).
              </p>
              <div className="d-flex flex-wrap gap-3 align-items-center mb-3 pb-2 border-bottom border-secondary">
                <span className="text-secondary small mb-0 align-self-center">
                  Or upload the same Excel here (same as above):
                </span>
                <Form.Control
                  type="file"
                  accept=".xlsx,.xls"
                  className="text-bg-dark border-secondary"
                  disabled={banquetUploadBusy}
                  style={{ maxWidth: 340 }}
                  onChange={(ev) => {
                    const file = ev.target.files?.[0];
                    ev.target.value = "";
                    uploadBanquetWorkbookFile(file || null);
                  }}
                />
              </div>
              <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
                <div>
                  <Form.Label className="text-secondary small mb-1">Calendar month</Form.Label>
                  <AdminDateInput
                    inputType="month"
                    size="sm"
                    style={{ width: 180 }}
                    value={banquetLinesMonth}
                    onChange={(e) => setBanquetLinesMonth(e.target.value)}
                  />
                </div>
                {latestBanquetMonthKey ? (
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    type="button"
                    disabled={banquetLinesBusy}
                    onClick={() => setBanquetLinesMonth(latestBanquetMonthKey)}
                  >
                    Latest month in roll-up ({latestBanquetMonthKey})
                  </Button>
                ) : null}
                <Button
                  size="sm"
                  variant="outline-light"
                  type="button"
                  disabled={banquetLinesBusy}
                  onClick={() => loadBanquetBookingLinesForMonth(banquetLinesMonth)}
                >
                  Refresh lines
                </Button>
                {banquetLinesBusy ? <Spinner animation="border" size="sm" className="text-light" /> : null}
              </div>
              <div
                className="admin-console-grid-wrap admin-console-grid-wrap--light ag-theme-quartz"
                style={{ height: Math.min(640, Math.max(280, 56 + (banquetBookingLines?.length || 1) * 42)) }}
              >
                <AgGridReact
                  rowData={banquetBookingLines || []}
                  columnDefs={banquetAnalyticsLineColumnDefs}
                  defaultColDef={banquetAnalyticsLineDefaultColDef}
                  getRowId={(p) => String(p.data?.id ?? `${p.data?.eventDate}-${p.data?.serialNo}`)}
                  rowClassRules={{
                    "opacity-50": (p) => Boolean(p.data?.cancelled),
                  }}
                  overlayNoRowsTemplate="No line rows for this month — upload a workbook or pick another month."
                  suppressCellFocus
                  animateRows={false}
                />
              </div>
              <Modal
                show={banquetEditOpen}
                onHide={() => !banquetEditBusy && setBanquetEditOpen(false)}
                centered
                size="lg"
                backdrop={banquetEditBusy ? "static" : true}
              >
                <Modal.Header closeButton closeVariant="white" className="bg-secondary border-secondary">
                  <Modal.Title className="h6">
                    Edit banquet booking line {banquetEditDraft?.id != null ? `#${banquetEditDraft.id}` : ""}
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-bg-dark">
                  {banquetEditDraft ? (
                    <Form id="pb-banquet-edit" className="row g-2 small">
                      <div className="col-md-4">
                        <Form.Label className="text-secondary">Event date</Form.Label>
                        <AdminDateInput
                          size="sm"
                          required
                          value={banquetEditDraft.eventDate || ""}
                          onChange={(e) => setBanquetEditDraft({ ...banquetEditDraft, eventDate: e.target.value })}
                        />
                      </div>
                      <div className="col-md-4">
                        <Form.Label className="text-secondary">S.No</Form.Label>
                        <Form.Control
                          type="number"
                          size="sm"
                          className="text-bg-dark border-secondary"
                          value={banquetEditDraft.serialNo}
                          onChange={(e) => setBanquetEditDraft({ ...banquetEditDraft, serialNo: e.target.value })}
                        />
                      </div>
                      <div className="col-md-4 d-flex align-items-end">
                        <Form.Check
                          type="switch"
                          id="pb-banquet-cancelled"
                          className="text-white-50"
                          label="Cancelled"
                          checked={banquetEditDraft.cancelled}
                          onChange={(e) =>
                            setBanquetEditDraft({ ...banquetEditDraft, cancelled: Boolean(e.target.checked) })
                          }
                        />
                      </div>
                      <div className="col-md-6">
                        <Form.Label className="text-secondary">Package / function</Form.Label>
                        <Form.Control
                          size="sm"
                          className="text-bg-dark border-secondary"
                          value={banquetEditDraft.packageName}
                          onChange={(e) => setBanquetEditDraft({ ...banquetEditDraft, packageName: e.target.value })}
                        />
                      </div>
                      <div className="col-md-6">
                        <Form.Label className="text-secondary">Slot</Form.Label>
                        <Form.Control
                          size="sm"
                          className="text-bg-dark border-secondary"
                          value={banquetEditDraft.slotDesc}
                          onChange={(e) => setBanquetEditDraft({ ...banquetEditDraft, slotDesc: e.target.value })}
                        />
                      </div>
                      <div className="col-md-3">
                        <Form.Label className="text-secondary">Pax</Form.Label>
                        <Form.Control
                          type="number"
                          size="sm"
                          className="text-bg-dark border-secondary"
                          value={banquetEditDraft.numPax}
                          onChange={(e) => setBanquetEditDraft({ ...banquetEditDraft, numPax: e.target.value })}
                        />
                      </div>
                      <div className="col-md-3">
                        <Form.Label className="text-secondary">Advance ₹</Form.Label>
                        <Form.Control
                          type="number"
                          size="sm"
                          className="text-bg-dark border-secondary"
                          value={banquetEditDraft.advanceRupee}
                          onChange={(e) =>
                            setBanquetEditDraft({ ...banquetEditDraft, advanceRupee: e.target.value })
                          }
                        />
                      </div>
                      <div className="col-md-3">
                        <Form.Label className="text-secondary">Balance ₹</Form.Label>
                        <Form.Control
                          type="number"
                          size="sm"
                          className="text-bg-dark border-secondary"
                          value={banquetEditDraft.balanceRupee}
                          onChange={(e) =>
                            setBanquetEditDraft({ ...banquetEditDraft, balanceRupee: e.target.value })
                          }
                        />
                      </div>
                      <div className="col-md-3">
                        <Form.Label className="text-secondary">Total ₹</Form.Label>
                        <Form.Control
                          type="number"
                          size="sm"
                          className="text-bg-dark border-secondary"
                          value={banquetEditDraft.totalAmountRupee}
                          onChange={(e) =>
                            setBanquetEditDraft({ ...banquetEditDraft, totalAmountRupee: e.target.value })
                          }
                        />
                      </div>
                      <div className="col-md-6">
                        <Form.Label className="text-secondary">Bank ₹</Form.Label>
                        <Form.Control
                          type="number"
                          size="sm"
                          className="text-bg-dark border-secondary"
                          value={banquetEditDraft.bankPaymentRupee}
                          onChange={(e) =>
                            setBanquetEditDraft({ ...banquetEditDraft, bankPaymentRupee: e.target.value })
                          }
                        />
                      </div>
                      <div className="col-md-6">
                        <Form.Label className="text-secondary">Cash ₹</Form.Label>
                        <Form.Control
                          type="number"
                          size="sm"
                          className="text-bg-dark border-secondary"
                          value={banquetEditDraft.cashPaymentRupee}
                          onChange={(e) =>
                            setBanquetEditDraft({ ...banquetEditDraft, cashPaymentRupee: e.target.value })
                          }
                        />
                      </div>
                      <div className="col-md-4">
                        <Form.Label className="text-secondary">Pre-plate / unit ₹</Form.Label>
                        <Form.Control
                          type="number"
                          size="sm"
                          className="text-bg-dark border-secondary"
                          value={banquetEditDraft.prePlateCostRupee}
                          onChange={(e) =>
                            setBanquetEditDraft({ ...banquetEditDraft, prePlateCostRupee: e.target.value })
                          }
                        />
                      </div>
                      <div className="col-md-4">
                        <Form.Label className="text-secondary">Décor exp ₹</Form.Label>
                        <Form.Control
                          type="number"
                          size="sm"
                          className="text-bg-dark border-secondary"
                          value={banquetEditDraft.decorExpensesRupee}
                          onChange={(e) =>
                            setBanquetEditDraft({ ...banquetEditDraft, decorExpensesRupee: e.target.value })
                          }
                        />
                      </div>
                      <div className="col-md-4">
                        <Form.Label className="text-secondary">Décor cost ₹</Form.Label>
                        <Form.Control
                          type="number"
                          size="sm"
                          className="text-bg-dark border-secondary"
                          value={banquetEditDraft.decorCostRupee}
                          onChange={(e) =>
                            setBanquetEditDraft({ ...banquetEditDraft, decorCostRupee: e.target.value })
                          }
                        />
                      </div>
                      <div className="col-md-12">
                        <Form.Label className="text-secondary">Status</Form.Label>
                        <Form.Control
                          size="sm"
                          className="text-bg-dark border-secondary"
                          value={banquetEditDraft.status}
                          onChange={(e) => setBanquetEditDraft({ ...banquetEditDraft, status: e.target.value })}
                        />
                      </div>
                      <div className="col-12">
                        <Form.Label className="text-secondary">Remarks</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          size="sm"
                          className="text-bg-dark border-secondary"
                          value={banquetEditDraft.remarks}
                          onChange={(e) => setBanquetEditDraft({ ...banquetEditDraft, remarks: e.target.value })}
                        />
                      </div>
                      <div className="col-md-4">
                        <Form.Label className="text-secondary">Customer name</Form.Label>
                        <Form.Control
                          size="sm"
                          className="text-bg-dark border-secondary"
                          value={banquetEditDraft.customerName}
                          onChange={(e) =>
                            setBanquetEditDraft({ ...banquetEditDraft, customerName: e.target.value })
                          }
                        />
                      </div>
                      <div className="col-md-4">
                        <Form.Label className="text-secondary">Customer email</Form.Label>
                        <Form.Control
                          type="email"
                          size="sm"
                          className="text-bg-dark border-secondary"
                          value={banquetEditDraft.customerEmail}
                          onChange={(e) =>
                            setBanquetEditDraft({ ...banquetEditDraft, customerEmail: e.target.value })
                          }
                        />
                      </div>
                      <div className="col-md-4">
                        <Form.Label className="text-secondary">Customer phone</Form.Label>
                        <Form.Control
                          size="sm"
                          className="text-bg-dark border-secondary"
                          value={banquetEditDraft.customerPhone}
                          onChange={(e) =>
                            setBanquetEditDraft({ ...banquetEditDraft, customerPhone: e.target.value })
                          }
                        />
                      </div>
                    </Form>
                  ) : (
                    <p className="text-secondary small mb-0">No row loaded.</p>
                  )}
                </Modal.Body>
                <Modal.Footer className="text-bg-dark border-secondary">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={banquetEditBusy}
                    onClick={() => setBanquetEditOpen(false)}
                  >
                    Close
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={
                      banquetEditBusy ||
                      banquetEditDraft == null ||
                      !banquetEditDraft.id ||
                      !banquetEditDraft.eventDate
                    }
                    onClick={async () => {
                      const d = banquetEditDraft;
                      const num = (s) => {
                        if (s == null || s === "") {
                          return null;
                        }
                        const n = Number(s);
                        return Number.isFinite(n) ? Math.round(n) : null;
                      };
                      const sint = (s) => {
                        const n = num(s);
                        return n === null ? null : n;
                      };
                      try {
                        setBanquetEditBusy(true);
                        setFinanceErr("");
                        await axios.put(
                          Constants.adminFinanceBanquetBookingPutUrl(d.id),
                          {
                            eventDate: d.eventDate || null,
                            serialNo:
                              d.serialNo === ""
                                ? null
                                : (() => {
                                    const v = Number.parseInt(String(d.serialNo), 10);
                                    return Number.isNaN(v) ? null : v;
                                  })(),
                            packageName: d.packageName || null,
                            slotDesc: d.slotDesc || null,
                            numPax: sint(d.numPax),
                            advanceRupee: num(d.advanceRupee),
                            balanceRupee: num(d.balanceRupee),
                            totalAmountRupee: num(d.totalAmountRupee),
                            bankPaymentRupee: num(d.bankPaymentRupee),
                            cashPaymentRupee: num(d.cashPaymentRupee),
                            status: d.status || null,
                            remarks: d.remarks || null,
                            decorExpensesRupee: num(d.decorExpensesRupee),
                            decorCostRupee: num(d.decorCostRupee),
                            prePlateCostRupee: num(d.prePlateCostRupee),
                            customerName: d.customerName || null,
                            customerEmail: d.customerEmail || null,
                            customerPhone: d.customerPhone || null,
                            cancelled: d.cancelled,
                          },
                          { headers: { ...analyticsHeaders } }
                        );
                        setFinanceMsg("Banquet row updated.");
                        setBanquetEditOpen(false);
                        await loadBanquetMonthly();
                        await loadBanquetBookingLinesForMonth(banquetLinesMonth);
                      } catch (e) {
                        if (e?.response?.status === 401) {
                          handleFinanceUnauthorized();
                        } else {
                          setFinanceErr(e?.response?.data?.message || e?.message || "Update failed.");
                        }
                      } finally {
                        setBanquetEditBusy(false);
                      }
                    }}
                  >
                    {banquetEditBusy ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-1 align-middle" /> Saving…
                      </>
                    ) : (
                      "Save changes"
                    )}
                  </Button>
                </Modal.Footer>
              </Modal>
            </div>
              </AnalyticsSubsectionPanel>
            </AnalyticsLoungeDashboard>
          </AnalyticsSubsectionPanel>

          <AnalyticsSubsectionPanel activeKey={bizTab} panelKey="business-trend">
            <BusinessTrendPanel
              endPeriodKey={maintPickMonth}
              onEndPeriodKeyChange={(v) => setMaintPickMonth(v || yyyyMmToday())}
              comparisonMonths={comparisonMonths}
              onComparisonMonthsChange={setComparisonMonths}
              monthlyComparison={monthlyComparison}
              comparisonBusy={comparisonBusy}
              onRefresh={() => loadMonthlyComparison(maintPickMonth, comparisonMonths)}
            />
          </AnalyticsSubsectionPanel>

          <AnalyticsSubsectionPanel activeKey={bizTab} panelKey="expenses">
            <AnalyticsLoungeDashboard
              title="Monthly costs"
              subtitle="Rent, salaries, suppliers, bank — from uploads. Multi-month trend is under Business trend tab."
              pillItems={monthlyCostsNavItems}
              activePill={monthlyCostsView}
              onPillSelect={setMonthlyCostsView}
              showCharts={false}
              toolbar={
                <>
                <div>
                  <Form.Label className="text-secondary small mb-1">Calendar month</Form.Label>
                  <AdminDateInput
                    inputType="month"
                    size="sm"
                    style={{ width: 160 }}
                    value={maintPickMonth}
                    onChange={(e) => setMaintPickMonth(e.target.value || yyyyMmToday())}
                  />
                </div>
                <Button size="sm" variant="outline-light" disabled={monthlyOverviewBusy} onClick={() => {
                  loadMonthlyOverview(maintPickMonth);
                  loadMonthlyComparison(maintPickMonth, comparisonMonths);
                  loadMonthlyCash(maintPickMonth);
                  loadCashIncome(maintPickMonth);
                  loadSalaryRows(maintPickMonth);
                  loadSalaryAdvanceSummary(maintPickMonth);
                  if (monthlyCostsView === "hyper") loadHyperItems();
                  if (monthlyCostsView === "coal") loadCoalLedger();
                  if (monthlyCostsView === "dairy") loadDairyLedger();
                  if (monthlyCostsView === "ice-cream") loadIceCreamLedger();
                  if (monthlyCostsView === "debt") loadVendorDebts();
                }}>
                  Refresh month
                </Button>
                </>
              }
            >
                <AnalyticsSubsectionPanel activeKey={monthlyCostsView} panelKey="rent">
                  <MonthlyMetricTrend
                    metricLabel="Rent + maintenance (bank)"
                    valueFn={(m) => (Number(m.rent1Rupee) || 0) + (Number(m.rent2Rupee) || 0) + (Number(m.maintenanceRupee) || 0)}
                    comparison={monthlyComparison}
                  />
                  <MonthlyBankCashHero
                    label="Rent & maintenance"
                    periodMonth={maintPickMonth}
                    bankRupee={sumRentMaint(monthlyOverview)}
                    cashRupee={cashBySlot.rent}
                    extraMeta={
                      <>
                        <div>Expected ~{formatInr(500000)} / month</div>
                        <div>
                          Bank line items:{" "}
                          <strong className="text-light">{(monthlyOverview?.rentDebitRows || []).length}</strong>
                        </div>
                      </>
                    }
                  />
                  <Table striped bordered hover size="sm" variant="dark" responsive className="mb-3">
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th className="text-end">Expected ₹</th>
                        <th className="text-end">Paid (bank) ₹</th>
                        <th className="text-end">Δ ₹</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { label: "Rent 1 — Builder (cash / RentWithDrawal)", expected: 276000, actual: monthlyOverview?.rent1Rupee },
                        { label: "Rent 2 — Land owner (NEFT ~₹1.79L)", expected: 179000, actual: monthlyOverview?.rent2Rupee },
                        { label: "Maintenance (school / upkeep)", expected: 45000, actual: monthlyOverview?.maintenanceRupee },
                      ].map((r) => {
                        const act = Number(r.actual) || 0;
                        const delta = act - r.expected;
                        return (
                          <tr key={r.label}>
                            <td>{r.label}</td>
                            <td className="text-end">{formatInr(r.expected)}</td>
                            <td className="text-end fw-semibold">{formatInr(act)}</td>
                            <td className={`text-end ${Math.abs(delta) <= 2000 ? "text-success" : "text-warning"}`}>
                              {formatInr(delta)}
                            </td>
                          </tr>
                        );
                      })}
                      <tr className="fw-semibold">
                        <td>Total rent & maintenance</td>
                        <td className="text-end">{formatInr(500000)}</td>
                        <td className="text-end">
                          {formatInr((Number(monthlyOverview?.rent1Rupee) || 0) + (Number(monthlyOverview?.rent2Rupee) || 0) + (Number(monthlyOverview?.maintenanceRupee) || 0))}
                        </td>
                        <td />
                      </tr>
                    </tbody>
                  </Table>
                  <p className="text-secondary small mb-2">
                    Upload ICICI statement and run Auto-classify in Uploads & Recon so narrations like CASH WDL and land-owner NEFT are tagged.
                  </p>
                  <div className="finance-recon-scroll">
                    <Table striped bordered hover size="sm" variant="dark" responsive className="mb-0">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Bank</th>
                          <th>Category</th>
                          <th>Particulars</th>
                          <th className="text-end">Debit ₹</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(monthlyOverview?.rentDebitRows || []).length === 0 ? (
                          <tr><td colSpan={5} className="text-secondary small">No rent/maintenance bank debits this month.</td></tr>
                        ) : (
                          monthlyOverview.rentDebitRows.map((r, idx) => (
                            <tr key={`rent-dr-${r.txnDateIso}-${idx}`}>
                              <td>{r.txnDateIso}</td>
                              <td>{r.bank}</td>
                              <td>{r.category}</td>
                              <td className="small">{r.particulars}</td>
                              <td className="text-end">{formatInr(r.debitRupee)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                  <MonthlyCostCashPanel
                    slotId="rent"
                    label="Rent — cash payment"
                    cashBySlot={cashBySlot}
                    cashLinesBySlot={cashLinesBySlot}
                    monthlyCashBusy={monthlyCashBusy}
                    onAddCashLine={addCashPaymentLine}
                    onDeleteCashLine={deleteCashPaymentLine}
                  />
                </AnalyticsSubsectionPanel>

                <AnalyticsSubsectionPanel activeKey={monthlyCostsView} panelKey="digital">
                  <MonthlyBankCashHero
                    label="Digital media"
                    periodMonth={maintPickMonth}
                    bankRupee={monthlyOverview?.digitalMediaRupee}
                    cashRupee={cashBySlot.digital}
                    extraMeta={
                      <div>
                        Transfers:{" "}
                        <strong className="text-light">{(monthlyOverview?.digitalMediaRows || []).length}</strong>
                      </div>
                    }
                  />
                  <p className="text-secondary small mb-2">
                    Monthly burn for digital media — separate from Rent 2. NEFT debits to gongatinarendra.
                  </p>
                  <div className="finance-recon-scroll">
                    <Table striped bordered hover size="sm" variant="dark" responsive className="mb-0">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Bank</th>
                          <th>Category</th>
                          <th>Particulars / beneficiary</th>
                          <th className="text-end">Amount ₹</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(monthlyOverview?.digitalMediaRows || []).length === 0 ? (
                          <tr>
                            <td colSpan={5} className="text-secondary small">
                              No gongatinarendra debits this month — upload ICICI statement and run Auto-classify.
                            </td>
                          </tr>
                        ) : (
                          <>
                            {monthlyOverview.digitalMediaRows.map((r, idx) => (
                              <tr key={`dm-${r.txnDateIso}-${idx}`}>
                                <td>{r.txnDateIso}</td>
                                <td>{r.bank}</td>
                                <td>{r.category || "Digital media"}</td>
                                <td className="small">{r.particulars}</td>
                                <td className="text-end text-warning">{formatInr(r.debitRupee)}</td>
                              </tr>
                            ))}
                            <tr className={ANALYTICS_TABLE_TOTAL_ROW_CLASS}>
                              <td colSpan={4}>
                                Total ({monthlyOverview.digitalMediaRows.length}{" "}
                                {monthlyOverview.digitalMediaRows.length === 1 ? "transfer" : "transfers"})
                              </td>
                              <td className="text-end fs-6">
                                <TableTotalAmount amount={monthlyOverview?.digitalMediaRupee} toneClass="text-warning" />
                              </td>
                            </tr>
                          </>
                        )}
                      </tbody>
                    </Table>
                  </div>
                  <MonthlyCostCashPanel
                    slotId="digital"
                    label="Digital media — cash payment"
                    cashBySlot={cashBySlot}
                    cashLinesBySlot={cashLinesBySlot}
                    monthlyCashBusy={monthlyCashBusy}
                    onAddCashLine={addCashPaymentLine}
                    onDeleteCashLine={deleteCashPaymentLine}
                  />
                </AnalyticsSubsectionPanel>

                <AnalyticsSubsectionPanel activeKey={monthlyCostsView} panelKey="salaries">
                  <MonthlyMetricTrend
                    metricLabel="Salaries paid (Excel)"
                    valueFn={(m) => m.salaryUploadRupee}
                    comparison={monthlyComparison}
                  />
                  <MonthlyBankCashHero
                    label="Salaries paid"
                    periodMonth={maintPickMonth}
                    bankRupee={salaryBankTotal}
                    cashRupee={salaryCashTotal}
                    extraMeta={
                      <>
                        <div>
                          Employees: <strong className="text-light">{salaryRows.length}</strong>
                        </div>
                        <div>Fixed total: {formatInr(monthlyOverview?.salaryUploadFixedRupee)}</div>
                        <div>Grand total paid: {formatInr(salaryPaidTotal)}</div>
                      </>
                    }
                  />
                  <p className="text-secondary small mb-2">
                    From uploaded salary Excel for {maintPickMonth}. Bank match vs ICICI/IDFC — unmatched rows are{" "}
                    <span className="text-warning">Cash</span>.
                  </p>
                  <Table striped bordered hover size="sm" variant="dark" responsive className="mb-0 finance-recon-scroll">
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th className="text-end">Fixed ₹</th>
                        <th className="text-end">Paid ₹</th>
                        <th>Payment</th>
                        <th>Bank txn</th>
                        <th className="text-end">Sal advance</th>
                        <th className="text-end">Adv deduct</th>
                        <th className="text-end">Advance</th>
                        <th>Adv bal</th>
                        <th>Status</th>
                        <th>Paid by</th>
                        <th>Leaves</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salaryRows.length === 0 ? (
                        <tr>
                          <td colSpan={12} className="text-secondary small">
                            Upload EMPSalaryList.xlsx (S.No, Emp Name, Fixed Salary, Paid Salary …) under Uploads & Recon.
                          </td>
                        </tr>
                      ) : (
                        salaryRows.map((row) => (
                          <tr key={`mc-sal-${row.id}`}>
                            <td>{row.empName}</td>
                            <td className="text-end">{formatInr(row.fixedSalaryRupee)}</td>
                            <td className="text-end fw-semibold">{formatInr(row.paidSalaryRupee)}</td>
                            <td>
                              {row.paymentChannel === "BANK" ? (
                                <span className="text-success">Bank</span>
                              ) : row.paymentChannel === "CASH" ? (
                                <span className="text-warning">Cash</span>
                              ) : "—"}
                            </td>
                            <td className="small text-secondary">
                              {row.paymentChannel === "BANK" ? (
                                <>
                                  {row.bankMatchTxnDateIso || "—"}
                                  {row.bankMatchParticulars ? (
                                    <div className="text-white-50" title={row.bankMatchParticulars}>
                                      {String(row.bankMatchParticulars).slice(0, 48)}…
                                    </div>
                                  ) : null}
                                </>
                              ) : "—"}
                            </td>
                            <td className="text-end">{formatInr(row.salAdvanceRupee)}</td>
                            <td className="text-end">{formatInr(row.advanceDeductionRupee)}</td>
                            <td className="text-end">{formatInr(row.advanceRupee)}</td>
                            <td>{row.advanceBal || "—"}</td>
                            <td>{row.status || "—"}</td>
                            <td>{row.paidBy || "—"}</td>
                            <td>{row.leaves || "—"}</td>
                          </tr>
                        ))
                      )}
                      {salaryRows.length ? (
                        <>
                          <tr className={ANALYTICS_TABLE_TOTAL_ROW_CLASS}>
                            <td>Bank paid</td>
                            <td />
                            <td className="text-end text-success">{formatInr(salaryBankTotal)}</td>
                            <td colSpan={9} />
                          </tr>
                          <tr className={ANALYTICS_TABLE_TOTAL_ROW_CLASS}>
                            <td>Cash paid</td>
                            <td />
                            <td className="text-end text-warning">{formatInr(salaryCashTotal)}</td>
                            <td colSpan={9} />
                          </tr>
                          <tr className={ANALYTICS_TABLE_TOTAL_ROW_CLASS}>
                            <td>Total paid ({salaryRows.length} employees)</td>
                            <td className="text-end">{formatInr(monthlyOverview?.salaryUploadFixedRupee)}</td>
                            <td className="text-end fs-6">
                              <TableTotalAmount amount={salaryPaidTotal} toneClass="text-success" />
                            </td>
                            <td colSpan={9} />
                          </tr>
                        </>
                      ) : null}
                    </tbody>
                  </Table>
                </AnalyticsSubsectionPanel>

                <AnalyticsSubsectionPanel activeKey={monthlyCostsView} panelKey="advances">
                  {salaryAdvanceSummary ? (
                    <>
                      <AnalyticsStatHero
                        label={`Net advance debt — ${maintPickMonth}`}
                        amountRupee={
                          salaryAdvanceSummary.netAdvanceDebtRupee ??
                          salaryAdvanceSummary.totalAdvanceBalanceRupee
                        }
                        valueClassName="text-info"
                        meta={
                          <>
                            <div>Advance given: {formatInr(salaryAdvanceSummary.totalSalAdvanceRupee)}</div>
                            <div>+ Advance column: {formatInr(salaryAdvanceSummary.totalAdvanceRupee)}</div>
                          </>
                        }
                      />
                      <AnalyticsStatHero
                        label={`Recovery income (collected) — ${maintPickMonth}`}
                        amountRupee={salaryAdvanceSummary.totalRecoveryIncomeRupee}
                        valueClassName="text-success"
                        meta={
                          <div>
                            Recoveries reduce employee debt and count as income when collected.
                          </div>
                        }
                      />
                      <div className="border border-secondary rounded p-3 mb-3">
                        <h5 className="h6 text-white mb-2">Add advance or record recovery</h5>
                        <p className="text-secondary small mb-2">
                          New employee: creates a row. Existing employee: new advance amounts are appended (not replaced).
                        </p>
                        <div className="row g-2 align-items-end">
                          <div className="col-md-4">
                            <Form.Label className="text-secondary small">Employee name</Form.Label>
                            <Form.Control
                              size="sm"
                              className="text-bg-dark border-secondary"
                              value={advEntryName}
                              onChange={(e) => setAdvEntryName(e.target.value)}
                              placeholder="Exact name"
                            />
                          </div>
                          <div className="col-md-3">
                            <Form.Label className="text-secondary small">New advance ₹</Form.Label>
                            <Form.Control
                              size="sm"
                              type="number"
                              min={0}
                              className="text-bg-dark border-secondary"
                              value={advEntryAmount}
                              onChange={(e) => setAdvEntryAmount(e.target.value)}
                            />
                          </div>
                          <div className="col-md-3">
                            <Form.Label className="text-secondary small">Recovery ₹ (income)</Form.Label>
                            <Form.Control
                              size="sm"
                              type="number"
                              min={0}
                              className="text-bg-dark border-secondary"
                              value={advRecoveryAmount}
                              onChange={(e) => setAdvRecoveryAmount(e.target.value)}
                            />
                          </div>
                          <div className="col-md-2">
                            <Button size="sm" variant="success" disabled={advEntryBusy} onClick={saveAdvanceEntry}>
                              {advEntryBusy ? "Saving…" : "Save"}
                            </Button>
                          </div>
                        </div>
                      </div>
                      <p className="text-secondary small mb-2">
                        Partner review — advance given, recoveries (income), and balance outstanding per employee.
                      </p>
                      <Table striped bordered hover size="sm" variant="dark" responsive className="mb-0 finance-recon-scroll">
                        <thead>
                          <tr>
                            <th>Employee</th>
                            <th className="text-end">Sal advance</th>
                            <th className="text-end">Advance</th>
                            <th className="text-end">Deducted</th>
                            <th className="text-end">Balance ₹</th>
                            <th>Bal (raw)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(salaryAdvanceSummary.rows || []).length === 0 ? (
                            <tr><td colSpan={6} className="text-secondary small">No advance rows this month.</td></tr>
                          ) : (
                            salaryAdvanceSummary.rows.map((r, i) => (
                              <tr key={`adv-${i}-${r.empName}`}>
                                <td>{r.empName}</td>
                                <td className="text-end">{formatInr(r.salAdvanceRupee)}</td>
                                <td className="text-end">{formatInr(r.advanceRupee)}</td>
                                <td className="text-end">{formatInr(r.advanceDeductionRupee)}</td>
                                <td className="text-end fw-semibold">{formatInr(r.advanceBalanceRupee)}</td>
                                <td className="small">{r.advanceBalRaw || "—"}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </>
                  ) : (
                    <p className="text-secondary small">Upload salary Excel and refresh.</p>
                  )}
                </AnalyticsSubsectionPanel>

                <AnalyticsSubsectionPanel activeKey={monthlyCostsView} panelKey="electricity">
                  <MonthlyCostTabShell
                    comparison={monthlyComparison}
                    trendLabel="Electricity"
                    valueFn={(m) => m.electricityRupee}
                    totalLabel="ICICI / IDFC electricity debits"
                    totalRupee={monthlyOverview?.electricityRupee}
                    cashRupee={cashBySlot.electricity}
                    periodMonth={maintPickMonth}
                    lineHint="Each bank debit tagged as electricity / power bill."
                    lines={monthlyOverview?.electricityDebitRows}
                    emptyMessage="No electricity debits this month."
                  >
                    <MonthlyCostCashPanel
                      slotId="electricity"
                      label="Electricity — cash payment"
                      cashBySlot={cashBySlot}
                      cashLinesBySlot={cashLinesBySlot}
                      monthlyCashBusy={monthlyCashBusy}
                      onAddCashLine={addCashPaymentLine}
                      onDeleteCashLine={deleteCashPaymentLine}
                    />
                  </MonthlyCostTabShell>
                </AnalyticsSubsectionPanel>

                <AnalyticsSubsectionPanel activeKey={monthlyCostsView} panelKey="gas">
                  <MonthlyCostTabShell
                    comparison={monthlyComparison}
                    trendLabel="Gas (HP Gas)"
                    valueFn={(m) => m.gasRupee}
                    totalLabel="HP Gas bank debits"
                    totalRupee={monthlyOverview?.gasRupee}
                    cashRupee={cashBySlot.gas}
                    periodMonth={maintPickMonth}
                    lineHint="Debits with HPGas in narration."
                    lines={(monthlyOverview?.expenseCategories || [])
                      .filter((c) => /gas/i.test(c.category || ""))
                      .map((c) => ({ txnDateIso: "—", bank: "—", category: c.category, particulars: `${c.txnCount} txns`, debitRupee: c.amountRupee }))}
                    emptyMessage="No gas debits this month."
                  >
                    <MonthlyCostCashPanel
                      slotId="gas"
                      label="Gas — cash payment"
                      cashBySlot={cashBySlot}
                      cashLinesBySlot={cashLinesBySlot}
                      monthlyCashBusy={monthlyCashBusy}
                      onAddCashLine={addCashPaymentLine}
                      onDeleteCashLine={deleteCashPaymentLine}
                    />
                  </MonthlyCostTabShell>
                </AnalyticsSubsectionPanel>

                <AnalyticsSubsectionPanel activeKey={monthlyCostsView} panelKey="hyper">
                  <MonthlyCostTabShell
                    comparison={monthlyComparison}
                    trendLabel="Hyper supplier"
                    valueFn={(m) => m.hyperRupee}
                    totalLabel="Hyper Gmail orders"
                    totalRupee={monthlyOverview?.hyperRupee}
                    cashRupee={cashBySlot.hyper}
                    periodMonth={maintPickMonth}
                    lineHint="Line items from Hyper supplier import. Pull Gmail under Uploads & Recon for history."
                    lines={(hyperItems || []).map((it) => ({
                      txnDateIso: maintPickMonth,
                      bank: "Hyper",
                      category: it.category || "Hyper",
                      particulars: it.itemName,
                      debitRupee: it.amountRupee,
                      qtyTotal: it.qtyTotal,
                      qtyUnit: it.qtyUnit,
                      unitPriceRupee: it.avgUnitPriceRupee,
                      gstAmountRupee: it.totalGstAmountRupee,
                    }))}
                    hyperPricingColumns
                    emptyMessage="No Hyper line items this month — poll Gmail or import orders."
                  >
                    <MonthlyCostCashPanel
                      slotId="hyper"
                      label="Hyper — cash payment"
                      cashBySlot={cashBySlot}
                      cashLinesBySlot={cashLinesBySlot}
                      monthlyCashBusy={monthlyCashBusy}
                      onAddCashLine={addCashPaymentLine}
                      onDeleteCashLine={deleteCashPaymentLine}
                    />
                  </MonthlyCostTabShell>
                </AnalyticsSubsectionPanel>

                <AnalyticsSubsectionPanel activeKey={monthlyCostsView} panelKey="coal">
                  <VendorPayableMonthlyTab
                    vendorLabel="Coal"
                    bankKeywordHint="machenderj-6"
                    periodMonth={maintPickMonth}
                    comparison={monthlyComparison}
                    trendLabel="Coal"
                    valueFn={(m) => m.coalRupee}
                    totalRupee={monthlyOverview?.coalRupee}
                    debitRows={monthlyOverview?.coalDebitRows}
                    ledger={coalLedger}
                    ledgerBusy={coalLedgerBusy}
                    settings={coalSettings}
                    onSettingsChange={setCoalSettings}
                    onSaveSettings={saveCoalSettings}
                    settingsBusy={coalSettingsBusy}
                    onRefreshLedger={loadCoalLedger}
                    lineHint="ICICI / IDFC debits with machenderj-6 in narration."
                    emptyMessage="No coal vendor debits this month — upload bank statements."
                    cashSlotId="coal"
                    cashAmountRupee={cashBySlot.coal}
                    cashLines={cashLinesBySlot.coal || []}
                    cashSaving={monthlyCashBusy}
                    onAddCashLine={addCashPaymentLine}
                    onDeleteCashLine={deleteCashPaymentLine}
                  />
                </AnalyticsSubsectionPanel>

                <AnalyticsSubsectionPanel activeKey={monthlyCostsView} panelKey="dairy">
                  <VendorPayableMonthlyTab
                    vendorLabel="Dairy products"
                    bankKeywordHint="9866040155"
                    periodMonth={maintPickMonth}
                    comparison={monthlyComparison}
                    trendLabel="Dairy products"
                    valueFn={(m) => m.dairyRupee}
                    totalRupee={monthlyOverview?.dairyRupee}
                    debitRows={monthlyOverview?.dairyDebitRows}
                    ledger={dairyLedger}
                    ledgerBusy={dairyLedgerBusy}
                    settings={dairySettings}
                    onSettingsChange={setDairySettings}
                    onSaveSettings={saveDairySettings}
                    settingsBusy={dairySettingsBusy}
                    onRefreshLedger={loadDairyLedger}
                    lineHint="ICICI / IDFC debits with 9866040155 in narration."
                    emptyMessage="No dairy vendor debits this month — upload bank statements."
                    cashSlotId="dairy"
                    cashAmountRupee={cashBySlot.dairy}
                    cashLines={cashLinesBySlot.dairy || []}
                    cashSaving={monthlyCashBusy}
                    onAddCashLine={addCashPaymentLine}
                    onDeleteCashLine={deleteCashPaymentLine}
                  />
                </AnalyticsSubsectionPanel>

                <AnalyticsSubsectionPanel activeKey={monthlyCostsView} panelKey="ice-cream">
                  <VendorPayableMonthlyTab
                    vendorLabel="Ice Cream"
                    bankKeywordHint="238386000686"
                    periodMonth={maintPickMonth}
                    comparison={monthlyComparison}
                    trendLabel="Ice Cream"
                    valueFn={(m) => m.iceCreamRupee}
                    totalRupee={monthlyOverview?.iceCreamRupee}
                    debitRows={monthlyOverview?.iceCreamDebitRows}
                    ledger={iceCreamLedger}
                    ledgerBusy={iceCreamLedgerBusy}
                    settings={iceCreamSettings}
                    onSettingsChange={setIceCreamSettings}
                    onSaveSettings={saveIceCreamSettings}
                    settingsBusy={iceCreamSettingsBusy}
                    onRefreshLedger={loadIceCreamLedger}
                    lineHint="ICICI / IDFC debits with 238386000686 in narration."
                    emptyMessage="No Ice Cream vendor debits this month — upload bank statements."
                    cashSlotId="ice-cream"
                    cashAmountRupee={cashBySlot["ice-cream"]}
                    cashLines={cashLinesBySlot["ice-cream"] || []}
                    cashSaving={monthlyCashBusy}
                    onAddCashLine={addCashPaymentLine}
                    onDeleteCashLine={deleteCashPaymentLine}
                  />
                </AnalyticsSubsectionPanel>

                <AnalyticsSubsectionPanel activeKey={monthlyCostsView} panelKey="meat">
                  <MeatMonthlyTab
                    periodMonth={maintPickMonth}
                    comparison={monthlyComparison}
                    overview={monthlyOverview}
                    cashBySlot={cashBySlot}
                    cashLinesBySlot={cashLinesBySlot}
                    cashSaving={monthlyCashBusy}
                    onAddCashLine={addCashPaymentLine}
                    onDeleteCashLine={deleteCashPaymentLine}
                  />
                </AnalyticsSubsectionPanel>

                <AnalyticsSubsectionPanel activeKey={monthlyCostsView} panelKey="cash-income">
                  <MonthlyCashIncomeTab
                    periodMonth={maintPickMonth}
                    incomeDto={cashIncome}
                    busy={cashIncomeBusy}
                    onRefresh={() => loadCashIncome(maintPickMonth)}
                    onAddLine={addCashIncomeLine}
                    onDeleteLine={deleteCashIncomeLine}
                  />
                </AnalyticsSubsectionPanel>

                <AnalyticsSubsectionPanel activeKey={monthlyCostsView} panelKey="cash-total">
                  <MonthlyCashSummaryTab
                    periodMonth={maintPickMonth}
                    cashDto={monthlyCash}
                    busy={monthlyCashBusy}
                    onRefresh={() => loadMonthlyCash(maintPickMonth)}
                  />
                </AnalyticsSubsectionPanel>

                <AnalyticsSubsectionPanel activeKey={monthlyCostsView} panelKey="debt">
                  <AnalyticsStatHero
                    label={`Total vendor debt — ${maintPickMonth}`}
                    amountRupee={vendorDebtSummary?.totalNetPayableRupee}
                    valueClassName="text-info"
                    meta={
                      <div>
                        Vendors:{" "}
                        <strong className="text-light">{(vendorDebtSummary?.vendors || []).length}</strong>
                      </div>
                    }
                  />
                  <p className="text-secondary small mb-2">
                    Outstanding payable across disposables (WhatsApp + plastic payments), coal, dairy, and ice cream.
                    Configure opening balance on each vendor tab.
                  </p>
                  {vendorDebtBusy ? <Spinner size="sm" animation="border" className="text-light mb-2" /> : null}
                  <Table striped bordered hover size="sm" variant="dark" responsive className="mb-3">
                    <thead>
                      <tr>
                        <th>Vendor</th>
                        <th className="text-end">This month</th>
                        <th className="text-end">Opening</th>
                        <th className="text-end">Paid (after cutoff)</th>
                        <th className="text-end">Net debt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(vendorDebtSummary?.vendors || []).length === 0 ? (
                        <tr>
                          <td colSpan={5} className="text-secondary small">
                            No vendor debt data — open Coal or Dairy tab to configure opening balance.
                          </td>
                        </tr>
                      ) : (
                        vendorDebtSummary.vendors.map((v) => (
                          <tr key={`debt-${v.vendorKey}`}>
                            <td>
                              <strong>{v.vendorLabel}</strong>
                              <div className="text-secondary small">
                                Bank: <code>{v.bankKeywordHint}</code>
                              </div>
                            </td>
                            <td className="text-end text-warning">{formatInr(v.monthSpendRupee)}</td>
                            <td className="text-end">{formatInr(v.openingBalanceRupee)}</td>
                            <td className="text-end text-success">{formatInr(v.bankPaymentsAfterOpeningRupee)}</td>
                            <td className="text-end text-info fw-semibold fs-6">{formatInrWithWords(v.netPayableRupee)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                  <Button size="sm" variant="outline-light" className="mb-3" disabled={vendorDebtBusy} onClick={loadVendorDebts}>
                    Refresh debt summary
                  </Button>
                  <h5 className="h6 text-info mb-2">Bank payments (where debt is tracked)</h5>
                  <div className="finance-recon-scroll">
                    <Table striped bordered hover size="sm" variant="dark" responsive className="mb-0">
                      <thead>
                        <tr>
                          <th>Vendor</th>
                          <th>Date</th>
                          <th>Bank</th>
                          <th>Particulars</th>
                          <th className="text-end">Amount ₹</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(vendorDebtSummary?.vendors || []).every((v) => !(v.recentPayments || []).length) ? (
                          <tr>
                            <td colSpan={5} className="text-secondary small">No payment rows loaded.</td>
                          </tr>
                        ) : (
                          vendorDebtSummary.vendors.flatMap((v) =>
                            (v.recentPayments || []).map((r, idx) => (
                              <tr key={`debt-pay-${v.vendorKey}-${r.txnDateIso}-${idx}`}>
                                <td className="small">{v.vendorLabel}</td>
                                <td>{r.txnDateIso}</td>
                                <td>{r.bank}</td>
                                <td className="small">{r.particulars}</td>
                                <td className="text-end">{formatInr(r.debitRupee)}</td>
                              </tr>
                            ))
                          )
                        )}
                      </tbody>
                    </Table>
                  </div>
                </AnalyticsSubsectionPanel>

                <AnalyticsSubsectionPanel activeKey={monthlyCostsView} panelKey="easydiner">
                  <BankCreditChannelTabBody
                    title="EasyDiner Dine out"
                    trendLabel="EasyDiner dine-out (bank credits)"
                    narrationHint="ICICI/IDFC credits with EAZYDINER in narration"
                    totalRupee={monthlyOverview?.easydinerCreditRupee}
                    rows={monthlyOverview?.easydinerCreditRows}
                    comparison={monthlyComparison}
                    valueFn={(m) => m.easydinerCreditRupee}
                    periodMonth={maintPickMonth}
                  />
                </AnalyticsSubsectionPanel>

                <AnalyticsSubsectionPanel activeKey={monthlyCostsView} panelKey="swiggy-dineout">
                  <BankCreditChannelTabBody
                    title="Swiggy Dine out"
                    trendLabel="Swiggy dine-out (Bundl Technologies)"
                    narrationHint="Bank credits with BUNDL TECHNOLOGIES in narration"
                    totalRupee={monthlyOverview?.swiggyBundlCreditRupee}
                    rows={monthlyOverview?.swiggyBundlCreditRows}
                    comparison={monthlyComparison}
                    valueFn={(m) => m.swiggyBundlCreditRupee}
                    periodMonth={maintPickMonth}
                  />
                </AnalyticsSubsectionPanel>

                <AnalyticsSubsectionPanel activeKey={monthlyCostsView} panelKey="zomato-dineout">
                  <BankCreditChannelTabBody
                    title="Zomato Dine out"
                    trendLabel="Zomato dine-out (Eternal Limited)"
                    narrationHint="Bank credits with ETERNAL LIMITED in narration"
                    totalRupee={monthlyOverview?.zomatoEternalCreditRupee}
                    rows={monthlyOverview?.zomatoEternalCreditRows}
                    comparison={monthlyComparison}
                    valueFn={(m) => m.zomatoEternalCreditRupee}
                    periodMonth={maintPickMonth}
                  />
                </AnalyticsSubsectionPanel>

                <AnalyticsSubsectionPanel activeKey={monthlyCostsView} panelKey="sbi-banquet">
                  <BankCreditChannelTabBody
                    title="SBI Banquet Payment"
                    trendLabel="SBI Life banquet payment"
                    narrationHint="Bank credits with SBI LIFE INSURANCE CO LTD in narration"
                    totalRupee={monthlyOverview?.sbiBanquetCreditRupee}
                    rows={monthlyOverview?.sbiBanquetCreditRows}
                    comparison={monthlyComparison}
                    valueFn={(m) => m.sbiBanquetCreditRupee}
                    periodMonth={maintPickMonth}
                  />
                </AnalyticsSubsectionPanel>

                <AnalyticsSubsectionPanel activeKey={monthlyCostsView} panelKey="bank-out">
                  <AnalyticsStatHero
                    label={`Total bank debits (all DR) — ${maintPickMonth}`}
                    amountRupee={monthlyOverview?.bankDebitTotalRupee}
                    valueClassName="text-warning"
                    meta={
                      <>
                        <div>Bank fees: {formatInr(monthlyOverview?.bankFeesRupee)}</div>
                        <div>
                          Categories:{" "}
                          <strong className="text-light">{(monthlyOverview?.expenseCategories || []).length}</strong>
                        </div>
                        <div>Filtered view total: {formatInr(bankOutFilteredTotal)}</div>
                      </>
                    }
                  />
                  <div className="d-flex flex-wrap gap-2 mb-2 align-items-end">
                    <div>
                      <Form.Label className="text-secondary small mb-0">Category</Form.Label>
                      <Form.Select size="sm" className="text-bg-dark border-secondary" style={{ minWidth: 160 }}
                        value={bankOutFilterCat} onChange={(e) => setBankOutFilterCat(e.target.value)}>
                        <option value="ALL">All categories</option>
                        {(monthlyOverview?.expenseCategories || []).map((c) => (
                          <option key={c.category} value={c.category}>{c.category}</option>
                        ))}
                      </Form.Select>
                    </div>
                    <div>
                      <Form.Label className="text-secondary small mb-0">Min ₹</Form.Label>
                      <Form.Control size="sm" type="number" min={0} className="text-bg-dark border-secondary" style={{ width: 100 }}
                        value={bankOutFilterMin} onChange={(e) => setBankOutFilterMin(e.target.value)} placeholder="0" />
                    </div>
                    <div className="flex-grow-1" style={{ minWidth: 180 }}>
                      <Form.Label className="text-secondary small mb-0">Search</Form.Label>
                      <Form.Control size="sm" className="text-bg-dark border-secondary"
                        value={bankOutSearch} onChange={(e) => setBankOutSearch(e.target.value)} placeholder="Filter category name…" />
                    </div>
                  </div>
                  <div className="finance-recon-scroll">
                    <Table striped bordered hover size="sm" variant="dark" responsive className="mb-0">
                      <thead>
                        <tr>
                          <th>Category</th>
                          <th className="text-end"># Txns</th>
                          <th className="text-end">Total ₹</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bankOutFiltered.length === 0 ? (
                          <tr>
                            <td colSpan={3} className="text-secondary small">Upload bank statements to see where money goes.</td>
                          </tr>
                        ) : (
                          <>
                            {bankOutFiltered.map((c) => (
                              <tr key={`exp-cat-${c.category}`}>
                                <td>{c.category}</td>
                                <td className="text-end">{c.txnCount}</td>
                                <td className="text-end text-warning fw-semibold">{formatInr(rowAmountRupee(c))}</td>
                              </tr>
                            ))}
                            <tr className={ANALYTICS_TABLE_TOTAL_ROW_CLASS}>
                              <td>Total ({bankOutFiltered.length} categories)</td>
                              <td className="text-end">
                                {bankOutFiltered.reduce((s, c) => s + (Number(c.txnCount) || 0), 0)}
                              </td>
                              <td className="text-end fs-6">
                                <TableTotalAmount amount={bankOutFilteredTotal} toneClass="text-warning" />
                              </td>
                            </tr>
                          </>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </AnalyticsSubsectionPanel>

                <AnalyticsSubsectionPanel activeKey={monthlyCostsView} panelKey="bank-in">
                  <AnalyticsStatHero
                    label={`Total bank credits — ${maintPickMonth}`}
                    amountRupee={
                      (Number(monthlyOverview?.bankCreditIciciRupee) || 0) +
                      (Number(monthlyOverview?.bankCreditIdfcRupee) || 0)
                    }
                    valueClassName="text-info"
                    meta={
                      <>
                        <div>ICICI: {formatInr(monthlyOverview?.bankCreditIciciRupee)}</div>
                        <div>IDFC: {formatInr(monthlyOverview?.bankCreditIdfcRupee)}</div>
                        <div>
                          Lines:{" "}
                          <strong className="text-light">{(monthlyOverview?.creditRows || []).length}</strong>
                        </div>
                      </>
                    }
                  />
                  {(monthlyOverview?.creditVendorTotals || []).length ? (
                    <Table striped bordered hover size="sm" variant="dark" responsive className="mb-3">
                      <thead>
                        <tr>
                          <th>Vendor / source</th>
                          <th className="text-end"># Txns</th>
                          <th className="text-end">Total ₹</th>
                        </tr>
                      </thead>
                      <tbody>
                        {monthlyOverview.creditVendorTotals.map((c) => (
                          <tr key={`cr-v-${c.category}`}>
                            <td>{c.category}</td>
                            <td className="text-end">{c.txnCount}</td>
                            <td className="text-end fw-semibold text-success">{formatInr(rowAmountRupee(c))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : null}
                  <div className="d-flex flex-wrap gap-2 mb-2 align-items-end">
                    <div>
                      <Form.Label className="text-secondary small mb-0">Vendor</Form.Label>
                      <Form.Select size="sm" className="text-bg-dark border-secondary" style={{ minWidth: 200 }}
                        value={creditFilterVendor} onChange={(e) => setCreditFilterVendor(e.target.value)}>
                        <option value="ALL">All vendors</option>
                        {(monthlyOverview?.creditVendorTotals || []).map((c) => (
                          <option key={c.category} value={c.category}>{c.category}</option>
                        ))}
                      </Form.Select>
                    </div>
                    <div>
                      <Form.Label className="text-secondary small mb-0">Min ₹</Form.Label>
                      <Form.Control size="sm" type="number" min={0} className="text-bg-dark border-secondary" style={{ width: 100 }}
                        value={creditFilterMin} onChange={(e) => setCreditFilterMin(e.target.value)} />
                    </div>
                    <div className="flex-grow-1" style={{ minWidth: 180 }}>
                      <Form.Label className="text-secondary small mb-0">Search narration</Form.Label>
                      <Form.Control size="sm" className="text-bg-dark border-secondary"
                        value={creditSearch} onChange={(e) => setCreditSearch(e.target.value)} placeholder="Swiggy, Eternal, SBI Life…" />
                    </div>
                  </div>
                  <div className="finance-recon-scroll">
                    <Table striped bordered hover size="sm" variant="dark" responsive className="mb-0">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Bank</th>
                          <th>Vendor</th>
                          <th>Particulars</th>
                          <th className="text-end">Credit ₹</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bankInFiltered.length === 0 ? (
                          <tr><td colSpan={5} className="text-secondary small">No credits this month — upload ICICI/IDFC statements.</td></tr>
                        ) : (
                          <>
                            {bankInFiltered.map((r, idx) => (
                              <tr key={`cr-${r.txnDateIso}-${idx}`}>
                                <td>{r.txnDateIso}</td>
                                <td>{r.bank}</td>
                                <td>{r.vendorLabel}</td>
                                <td className="small">{r.particulars}</td>
                                <td className="text-end text-success fw-semibold">{formatInr(rowAmountRupee(r))}</td>
                              </tr>
                            ))}
                            <tr className={ANALYTICS_TABLE_TOTAL_ROW_CLASS}>
                              <td colSpan={4}>Total ({bankInFiltered.length} credits)</td>
                              <td className="text-end fs-6">
                                <TableTotalAmount amount={bankInFilteredTotal} toneClass="text-success" />
                              </td>
                            </tr>
                          </>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </AnalyticsSubsectionPanel>

                <AnalyticsSubsectionPanel activeKey={monthlyCostsView} panelKey="disposal">
                  <MonthlyMetricTrend
                    metricLabel="Disposables (WhatsApp vendor)"
                    valueFn={(m) => m.plasticSpendRupee}
                    comparison={monthlyComparison}
                  />
                  <AnalyticsStatHero
                    label={`Disposables spent — ${maintPickMonth}`}
                    amountRupee={plasticLedger?.monthSpendRupee ?? 0}
                    valueClassName="text-warning"
                    meta={
                      <>
                        <div>
                          Orders:{" "}
                          <strong className="text-light">{(plasticLedger?.orders || []).length}</strong>
                        </div>
                        <div>Net payable to vendor: {formatInr(plasticLedger?.netPayableRupee)}</div>
                      </>
                    }
                  />
                  <p className="text-secondary small mb-2">
                    Ramdev plastic vendor — <strong>this month spent {formatInr(plasticLedger?.monthSpendRupee ?? 0)}</strong> on disposables ({maintPickMonth}).
                    Vendor debt (net payable) = opening + orders after cutoff − bank payments to <code>ashvinpatel9920</code>.
                    Total-only WhatsApp messages are skipped; mismatched follow-up totals are ignored.
                  </p>
                  {plasticLedgerBusy ? <Spinner size="sm" animation="border" className="text-light mb-2" /> : null}
                  <Table striped bordered hover size="sm" variant="dark" responsive className="mb-3">
                    <thead>
                      <tr>
                        <th>Metric</th>
                        <th className="text-end">₹</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="fw-bold"><td>This month spent ({maintPickMonth})</td><td className="text-end text-warning fs-6">{formatInrWithWords(plasticLedger?.monthSpendRupee ?? 0)}</td></tr>
                      <tr><td>Opening balance (as of {formatIsoDateIn(plasticLedger?.openingBalanceDateIso) || "—"})</td><td className="text-end">{formatInr(plasticLedger?.openingBalanceRupee)}</td></tr>
                      <tr><td>+ Line items after cutoff date (all months)</td><td className="text-end">{formatInr(plasticLedger?.ordersAfterOpeningRupee)}</td></tr>
                      {(plasticLedger?.ordersStatedTotalRupee ?? 0) > 0 &&
                      (plasticLedger?.ordersStatedTotalRupee ?? 0) !== (plasticLedger?.ordersAfterOpeningRupee ?? 0) ? (
                        <tr><td className="text-secondary small ps-3">Vendor stated in WhatsApp (reference)</td><td className="text-end text-secondary">{formatInr(plasticLedger?.ordersStatedTotalRupee)}</td></tr>
                      ) : null}
                      {(plasticLedger?.totalMismatchCount ?? 0) > 0 ? (
                        <tr><td className="text-danger small">Orders where stated ≠ line sum</td><td className="text-end text-danger">{plasticLedger.totalMismatchCount}</td></tr>
                      ) : null}
                      <tr><td>− Paid via bank</td><td className="text-end text-success">{formatInr(plasticLedger?.bankPaymentsAfterOpeningRupee)}</td></tr>
                      <tr className="fw-bold"><td>Net payable to vendor (debt)</td><td className="text-end text-info">{formatInr(plasticLedger?.netPayableRupee)}</td></tr>
                    </tbody>
                  </Table>
                  <Button size="sm" variant="outline-light" className="mb-3" onClick={() => loadPlasticLedger(maintPickMonth)} disabled={plasticLedgerBusy}>
                    Refresh disposal ledger
                  </Button>
                  {(plasticLedger?.aggregatedItems || []).length > 0 ? (
                    <>
                      <h5 className="h6 text-info mb-2">What we bought — {maintPickMonth}</h5>
                      <p className="text-secondary small mb-2">Same item across orders: quantities and amounts are summed for this month.</p>
                      <Table striped bordered hover size="sm" variant="dark" responsive className="mb-3">
                        <thead>
                          <tr>
                            <th>Item</th>
                            <th className="text-end">Total qty</th>
                            <th className="text-end">Line count</th>
                            <th className="text-end">Total ₹</th>
                          </tr>
                        </thead>
                        <tbody>
                          {plasticLedger.aggregatedItems.map((it, idx) => (
                            <tr key={`disp-agg-${idx}-${it.itemName}`}>
                              <td>{it.itemName}</td>
                              <td className="text-end">{it.qtyTotal != null ? `${Number(it.qtyTotal)} ${it.qtyUnit || ""}`.trim() : "—"}</td>
                              <td className="text-end">{it.orderCount ?? "—"}</td>
                              <td className="text-end fw-semibold">{formatInr(it.amountRupee)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </>
                  ) : null}
                  <Table striped bordered hover size="sm" variant="dark" responsive className="mb-0">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th className="text-end">Vendor stated ₹</th>
                        <th className="text-end">Lines sum ₹</th>
                        <th>Check</th>
                        <th className="text-end">Items</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(plasticLedger?.orders || []).length === 0 ? (
                        <tr><td colSpan={5} className="text-secondary small">No plastic orders in {maintPickMonth}. Upload WhatsApp .zip in Uploads & Recon.</td></tr>
                      ) : (
                        plasticLedger.orders.map((o) => {
                          const oid = String(o.id ?? `${o.orderDateIso}-${o.totalRupee}`);
                          const expanded = plasticExpandedOrderIds.has(oid);
                          const lineRows = o.lines || [];
                          const hasLines = lineRows.length > 0 && !lineRows.every((ln) => (ln.itemName || "").toLowerCase().includes("total only"));
                          return (
                            <React.Fragment key={`disp-${oid}`}>
                              <tr className={o.totalMismatch ? "table-warning" : undefined}>
                                <td>
                                  {hasLines ? (
                                    <button type="button" className="btn btn-link btn-sm text-info p-0 me-1" onClick={() => togglePlasticOrderExpand(oid)} aria-label={expanded ? "Collapse" : "Expand"}>
                                      {expanded ? "▼" : "▶"}
                                    </button>
                                  ) : null}
                                  {formatIsoDateIn(o.orderDateIso)}
                                </td>
                                <td className="text-end text-secondary">{formatInr(o.statedTotalRupee ?? o.totalRupee)}</td>
                                <td className="text-end fw-semibold">{formatInr(o.calculatedTotalRupee ?? o.totalRupee)}</td>
                                <td>{o.totalMismatch ? <span className="text-danger small">Mismatch</span> : hasLines ? <span className="text-success small">OK</span> : <span className="text-secondary small">Total only</span>}</td>
                                <td className="text-end">{o.lineCount ?? lineRows.length}</td>
                              </tr>
                              {expanded && hasLines ? (
                                <tr>
                                  <td colSpan={5} className="bg-dark border-0 pt-0">
                                    <Table size="sm" variant="dark" className="mb-0 ms-3 border-secondary">
                                      <thead><tr><th>Item</th><th className="text-end">Qty</th><th className="text-end">Amount ₹</th></tr></thead>
                                      <tbody>
                                        {lineRows.filter((ln) => !(ln.itemName || "").toLowerCase().includes("total only")).map((ln, li) => (
                                          <tr key={`disp-ln-${oid}-${li}`}>
                                            <td className="small">{ln.itemName}</td>
                                            <td className="text-end small">{ln.qtyLabel || "—"}</td>
                                            <td className="text-end small">{formatInr(ln.amountRupee)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </Table>
                                  </td>
                                </tr>
                              ) : null}
                            </React.Fragment>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                  <MonthlyCostCashPanel
                    slotId="disposal"
                    label="Disposables — cash payment"
                    cashBySlot={cashBySlot}
                    cashLinesBySlot={cashLinesBySlot}
                    monthlyCashBusy={monthlyCashBusy}
                    onAddCashLine={addCashPaymentLine}
                    onDeleteCashLine={deleteCashPaymentLine}
                  />
                </AnalyticsSubsectionPanel>

                <AnalyticsSubsectionPanel activeKey={monthlyCostsView} panelKey="vegetables">
                  <AnalyticsStatHero
                    label={`Vegetable spend (bank) — ${maintPickMonth}`}
                    amountRupee={monthlyOverview?.vegetableTotalRupee}
                    meta={
                      <div>
                        Categories:{" "}
                        <strong className="text-light">{(monthlyOverview?.vegetableSpend || []).length}</strong>
                      </div>
                    }
                  />
                  <p className="text-secondary small mb-2">
                    Bank debits tagged OnionAloo, Tamoto, Gobi, keera, etc.
                  </p>
                  <Table striped bordered hover size="sm" variant="dark" responsive className="mb-0">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th className="text-end"># Txns</th>
                        <th className="text-end">Total ₹</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(monthlyOverview?.vegetableSpend || []).length === 0 ? (
                        <tr><td colSpan={3} className="text-secondary small">No vegetable-tagged debits this month.</td></tr>
                      ) : (
                        <>
                          {monthlyOverview.vegetableSpend.map((v) => (
                            <tr key={`veg-${v.vegCategory}`}>
                              <td>{v.vegCategory}</td>
                              <td className="text-end">{v.txnCount}</td>
                              <td className="text-end">{formatInr(v.amountRupee)}</td>
                            </tr>
                          ))}
                          <tr className={ANALYTICS_TABLE_TOTAL_ROW_CLASS}>
                            <td>Total</td>
                            <td />
                            <td className="text-end fs-6">
                              <TableTotalAmount amount={monthlyOverview?.vegetableTotalRupee} toneClass="text-warning" />
                            </td>
                          </tr>
                        </>
                      )}
                    </tbody>
                  </Table>
                  <MonthlyCostCashPanel
                    slotId="vegetables"
                    label="Vegetables — cash payment"
                    cashBySlot={cashBySlot}
                    cashLinesBySlot={cashLinesBySlot}
                    monthlyCashBusy={monthlyCashBusy}
                    onAddCashLine={addCashPaymentLine}
                    onDeleteCashLine={deleteCashPaymentLine}
                  />
                </AnalyticsSubsectionPanel>

                <AnalyticsSubsectionPanel activeKey={monthlyCostsView} panelKey="daily">
                  <MonthlyMetricTrend
                    metricLabel="Daily expense (manual)"
                    valueFn={(m) => m.dailyManualExpenseRupee}
                    comparison={monthlyComparison}
                  />
                  <AnalyticsStatHero
                    label={`Daily expense (manual entries) — ${expensePickMonth}`}
                    amountRupee={monthlyOverview?.dailyManualExpenseRupee}
                    meta={
                      <div>
                        Entries this month:{" "}
                        <strong className="text-light">{(dailyRows || []).length}</strong>
                      </div>
                    }
                  />
              <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
                <div>
                  <Form.Label className="text-secondary small mb-1">Month window</Form.Label>
                  <AdminDateInput
                    inputType="month"
                    size="sm"
                    value={expensePickMonth}
                    onChange={(e) => setExpensePickMonth(e.target.value)}
                  />
                </div>
              </div>
              <Form
                className="row g-2 align-items-end mb-4 pb-3 border-bottom border-secondary"
                onSubmit={(e) => e.preventDefault()}
              >
                <div className="col-md-3">
                  <Form.Label className="text-secondary small">Date</Form.Label>
                  <AdminDateInput
                    size="sm"
                    value={dexDate}
                    onChange={(e) => setDexDate(e.target.value)}
                  />
                </div>
                <div className="col-md-4">
                  <Form.Label className="text-secondary small">Category (pick preset or free text)</Form.Label>
                  <Form.Control
                    size="sm"
                    className="text-bg-dark border-secondary"
                    list="pb-expense-presets-list"
                    value={dexCategory}
                    onChange={(e) => setDexCategory(e.target.value)}
                  />
                  <datalist id="pb-expense-presets-list">
                    {(expensePresets || []).map((p) => (
                      <option key={p} value={p} />
                    ))}
                  </datalist>
                </div>
                <div className="col-md-2">
                  <Form.Label className="text-secondary small">Amount ₹</Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    min={1}
                    className="text-bg-dark border-secondary"
                    value={dexAmount}
                    onChange={(e) => setDexAmount(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <Form.Label className="text-secondary small">Entered by</Form.Label>
                  <Form.Control
                    size="sm"
                    className="text-bg-dark border-secondary"
                    value={dexEnteredBy}
                    onChange={(e) => setDexEnteredBy(e.target.value)}
                  />
                </div>
                <div className="col-12">
                  <Form.Label className="text-secondary small">Notes</Form.Label>
                  <Form.Control
                    size="sm"
                    className="text-bg-dark border-secondary"
                    value={dexNotes}
                    onChange={(e) => setDexNotes(e.target.value)}
                  />
                </div>
                <div className="col-12 mt-2 d-flex gap-2 flex-wrap align-items-center">
                  <Button
                    size="sm"
                    variant="success"
                    disabled={dexSaveBusy || dailyBusy}
                    onClick={async () => {
                      setFinanceErr("");
                      const amt = Number(dexAmount);
                      if (!(amt >= 1)) {
                        setFinanceErr("Daily spend needs a positive ₹ amount.");
                        return;
                      }
                      const cat = String(dexCategory || "").trim();
                      if (!cat) {
                        setFinanceErr("Category is required.");
                        return;
                      }
                      setDexSaveBusy(true);
                      try {
                        await axios.post(
                          Constants.ADMIN_FINANCE_DAILY_EXPENSES,
                          {
                            expenseDateIso: dexDate,
                            category: cat,
                            amountRupee: Math.round(amt),
                            enteredBy: dexEnteredBy,
                            notes: dexNotes,
                          },
                          { headers: analyticsHeaders }
                        );
                        setFinanceMsg("Captured daily expense row.");
                        setDexNotes("");
                        setDexAmount("");
                        await loadDailyExpensesForMonthPick();
                      } catch (e) {
                        setFinanceErr(e?.response?.data?.message || e?.message || "Save failed.");
                      } finally {
                        setDexSaveBusy(false);
                      }
                    }}
                  >
                    Save expense
                  </Button>
                  {dailyBusy ? <Spinner size="sm" animation="border" className="text-light" /> : null}
                </div>
              </Form>

              <Table striped bordered hover size="sm" variant="dark" responsive className="mb-0">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Category</th>
                    <th className="text-end">₹</th>
                    <th>Who</th>
                    <th className="text-end">Del</th>
                  </tr>
                </thead>
                <tbody>
                  {(dailyRows || []).map((r) => (
                    <tr key={r.id}>
                      <td>{r.expenseDateIso}</td>
                      <td>{r.category}</td>
                      <td className="text-end">{formatInr(r.amountRupee)}</td>
                      <td>{r.enteredBy || "—"}</td>
                      <td className="text-end">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={async () => {
                            if (!window.confirm("Remove this ledger row?")) return;
                            try {
                              await axios.delete(Constants.adminFinanceDailyExpenseDelete(r.id), {
                                headers: analyticsHeaders,
                              });
                              await loadDailyExpensesForMonthPick();
                            } catch (e) {
                              setFinanceErr(e?.response?.data?.message || e?.message || "Delete failed.");
                            }
                          }}
                        >
                          ×
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {dailyRows?.length ? null : (
                    <tr>
                      <td colSpan={5} className="text-secondary small">
                        No ledger rows captured for this IST month yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
                </AnalyticsSubsectionPanel>
            </AnalyticsLoungeDashboard>
          </AnalyticsSubsectionPanel>

          <AnalyticsSubsectionPanel activeKey={bizTab} panelKey="uploads">
            <AnalyticsLoungeDashboard
              title="Uploads & Recon"
              subtitle="Upload files under All uploads; use other pills for ledger, rules, and import review."
              pillItems={uploadReconNavItems}
              activePill={uploadReconView}
              onPillSelect={setUploadReconView}
              showCharts={(petpoojaMonthly?.length || 0) > 0}
              charts={
                (petpoojaMonthly?.length || 0) > 0 ? (
                  <RichLineChart
                    title="Petpooja monthly"
                    subtitle="Imported order totals"
                    badge="Petpooja"
                    height={200}
                    labels={[...(petpoojaMonthly || [])]
                      .slice()
                      .sort((a, b) => String(a.periodKey).localeCompare(String(b.periodKey)))
                      .map((r) => r.periodKey)}
                    series={[
                      {
                        key: "revenue",
                        values: [...(petpoojaMonthly || [])]
                          .slice()
                          .sort((a, b) => String(a.periodKey).localeCompare(String(b.periodKey)))
                          .map((r) => Number(r.totalRupee) || 0),
                      },
                    ]}
                  />
                ) : null
              }
              mainHeader={null}
              toolbar={
                <>
                  <div>
                    <Form.Label className="text-secondary small mb-1">Recon month</Form.Label>
                    <AdminDateInput
                      inputType="month"
                      size="sm"
                      style={{ width: 160 }}
                      value={reconPeriod}
                      onChange={(e) => setReconPeriod(e.target.value || yyyyMmToday())}
                    />
                  </div>
                  <div>
                    <Form.Label className="text-secondary small mb-1">Bank</Form.Label>
                    <Form.Select
                      size="sm"
                      className="text-bg-dark border-secondary"
                      style={{ width: 140 }}
                      value={reconBank}
                      onChange={(e) => setReconBank(e.target.value)}
                    >
                      <option value="">Both</option>
                      <option value="ICICI">ICICI</option>
                      <option value="IDFC">IDFC</option>
                    </Form.Select>
                  </div>
                  <Button size="sm" variant="outline-light" onClick={loadReconLedger} disabled={reconLedgerBusy}>
                    Refresh ledger
                  </Button>
                  <Button size="sm" variant="outline-info" onClick={runReconciliation}>
                    Auto-classify
                  </Button>
                  <Button size="sm" variant="outline-light" onClick={loadStockBundle} disabled={stockBusy}>
                    Stock report
                  </Button>
                </>
              }
            >
                <AnalyticsSubsectionPanel activeKey={uploadReconView} panelKey="all-uploads">
                  {uploadLastResult ? (
                    <Alert variant="info" dismissible onClose={() => setUploadLastResult(null)} className="mb-3">
                      <div>
                        <strong>Last upload ({uploadLastResult.key}):</strong>{" "}
                        inserted {uploadLastResult.insertedCount ?? 0}, skipped {uploadLastResult.skippedCount ?? 0}.
                        {uploadLastResult.periodStart
                          ? ` Window ${uploadLastResult.periodStart} → ${uploadLastResult.periodEnd}.`
                          : ""}
                      </div>
                      {Array.isArray(uploadLastResult.messages) && uploadLastResult.messages.length ? (
                        <details className="mt-2">
                          <summary className="small">Diagnostic trace ({uploadLastResult.messages.length} lines)</summary>
                          <pre className="small mb-0 mt-2" style={{ whiteSpace: "pre-wrap" }}>
                            {uploadLastResult.messages.join("\n")}
                          </pre>
                        </details>
                      ) : null}
                    </Alert>
                  ) : null}

                  <p className="text-secondary small mb-3">
                    Bank statements, Petpooja, salary, plastic WhatsApp, and Hyper Gmail. Duplicates skipped by content hash.
                  </p>

                  <details open className="mb-3 border border-secondary rounded p-3">
                    <summary className="text-white-50 small">ICICI Bank statement (.xls / .xlsx)</summary>
                    <div className="mt-2 d-flex flex-wrap gap-2 align-items-end">
                      <input
                        type="file"
                        accept=".xls,.xlsx"
                        className="form-control form-control-sm bg-dark text-light border-secondary"
                        style={{ maxWidth: 360 }}
                        id="upload-icici"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) uploadFinanceFile("icici", Constants.adminFinanceUploadBankUrl("icici"), f, "ICICI statement");
                        }}
                      />
                      {uploadBusyKey === "icici" ? <Spinner size="sm" animation="border" className="text-light" /> : null}
                    </div>
                  </details>

                  <details open className="mb-3 border border-secondary rounded p-3">
                    <summary className="text-white-50 small">IDFC FIRST Bank statement (.xlsx)</summary>
                    <div className="mt-2 d-flex flex-wrap gap-2 align-items-end">
                      <input
                        type="file"
                        accept=".xls,.xlsx"
                        className="form-control form-control-sm bg-dark text-light border-secondary"
                        style={{ maxWidth: 360 }}
                        id="upload-idfc"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) uploadFinanceFile("idfc", Constants.adminFinanceUploadBankUrl("idfc"), f, "IDFC statement");
                        }}
                      />
                      {uploadBusyKey === "idfc" ? <Spinner size="sm" animation="border" className="text-light" /> : null}
                    </div>
                  </details>

                  <details className="mb-3 border border-secondary rounded p-3">
                    <summary className="text-white-50 small">Petpooja Day-End Summary (daily totals)</summary>
                    <div className="mt-2 d-flex flex-wrap gap-2 align-items-end">
                      <input
                        type="file"
                        accept=".xls,.xlsx"
                        className="form-control form-control-sm bg-dark text-light border-secondary"
                        style={{ maxWidth: 360 }}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) uploadFinanceFile("petpooja-dayend", Constants.ADMIN_FINANCE_UPLOAD_PETPOOJA_DAYEND, f, "Day-End report");
                        }}
                      />
                      {uploadBusyKey === "petpooja-dayend" ? <Spinner size="sm" animation="border" className="text-light" /> : null}
                    </div>
                  </details>

                  <details className="mb-3 border border-secondary rounded p-3">
                    <summary className="text-white-50 small">Petpooja Orders Master (Dine-In + Delivery / Zomato / Swiggy)</summary>
                    <div className="mt-2 d-flex flex-wrap gap-2 align-items-end">
                      <input
                        type="file"
                        accept=".xls,.xlsx"
                        className="form-control form-control-sm bg-dark text-light border-secondary"
                        style={{ maxWidth: 360 }}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) uploadFinanceFile("petpooja-orders", Constants.ADMIN_FINANCE_UPLOAD_PETPOOJA_ORDERS, f, "Orders Master");
                        }}
                      />
                      {uploadBusyKey === "petpooja-orders" ? <Spinner size="sm" animation="border" className="text-light" /> : null}
                    </div>
                  </details>

                  <details className="mb-3 border border-secondary rounded p-3">
                    <summary className="text-white-50 small">Monthly EMP salary list (.xlsx — one tab per month)</summary>
                    <div className="mt-2 d-flex flex-wrap gap-2 align-items-end">
                      <input
                        type="file"
                        accept=".xls,.xlsx"
                        className="form-control form-control-sm bg-dark text-light border-secondary"
                        style={{ maxWidth: 360 }}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) uploadFinanceFile("emp-salary", Constants.ADMIN_FINANCE_UPLOAD_EMP_SALARY, f, "Salary workbook");
                        }}
                      />
                      {uploadBusyKey === "emp-salary" ? <Spinner size="sm" animation="border" className="text-light" /> : null}
                    </div>
                  </details>

                  <details className="mb-3 border border-secondary rounded p-3">
                    <summary className="text-white-50 small">Plastic / Disposables — WhatsApp chat export (.zip)</summary>
                    <p className="text-secondary small mt-2 mb-2">
                      Export chat with Ramdev plastic vendor from WhatsApp → attach .zip. Parses
                      <strong> Date dd/mm/yyyy</strong>, item lines (<code>520 pcs = 480/-</code>), and
                      <strong> Total amount</strong> into orders. Messages with only a total (no line items) are skipped.
                      Opening balance and cutoff date are configurable in the Plastic tab; bank payments to{" "}
                      <code>ashvinpatel9920</code> reduce net payable.
                    </p>
                    <div className="mt-2 d-flex flex-wrap gap-2 align-items-center">
                      <input
                        type="file"
                        accept=".zip"
                        className="form-control form-control-sm bg-dark text-light border-secondary"
                        style={{ maxWidth: 360 }}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) uploadPlasticZip(f);
                          e.target.value = "";
                        }}
                      />
                      <Form.Check
                        type="checkbox"
                        id="plastic-replace-all"
                        className="text-secondary small mb-0"
                        label="Clear all plastic data before import"
                        checked={plasticReplaceAll}
                        onChange={(e) => setPlasticReplaceAll(e.target.checked)}
                      />
                      {uploadBusyKey === "whatsapp-plastic" ? <Spinner size="sm" animation="border" className="text-light" /> : null}
                    </div>
                    <p className="text-secondary small mt-2 mb-0">
                      Re-uploading the same file updates matching orders automatically. Existing orders with the same content are refreshed, not skipped.
                    </p>
                  </details>

                  <details className="mb-3 border border-secondary rounded p-3">
                    <summary className="text-white-50 small">Hyper supplier orders — Gmail poller</summary>
                    <p className="text-secondary small mt-2 mb-2">
                      <strong>First pull:</strong> choose months — the server imports <strong>one calendar month per batch</strong>
                      (30 min pause between batches, down to {hyperGmailStatus?.backfillFloorMonth || "2024-03"}).
                      <strong className="ms-1">After backfill:</strong> automatic delta pulls every{" "}
                      {hyperGmailStatus?.deltaIntervalHours ?? 6} hour(s).
                      Configure <code className="ms-1">partyboss.finance.gmail.app-password</code> (16-char Gmail App Password).
                    </p>
                    {hyperGmailStatus ? (
                      <div className="text-secondary small mb-2 border border-secondary rounded p-2">
                        <div>
                          <strong>Phase:</strong>{" "}
                          {hyperGmailStatus.schedulerPhase || (hyperGmailStatus.firstPullPending ? "FIRST_PULL_PENDING" : "DELTA_READY")}
                          {hyperGmailStatus.backfillRunning ? (
                            <span className="ms-2 text-warning">
                              <Spinner animation="border" size="sm" className="me-1 align-middle" />
                              Running…
                            </span>
                          ) : null}
                        </div>
                        {hyperGmailStatus.backfillActive ? (
                          <div className="mt-1">
                            <strong>Backfill:</strong> {hyperGmailStatus.monthsBackfillCompleted ?? 0} /{" "}
                            {hyperGmailStatus.backfillTotalMonths ?? "?"} months
                            {hyperGmailStatus.backfillNextMonth ? (
                              <span className="ms-1">— next: {hyperGmailStatus.backfillNextMonth}</span>
                            ) : null}
                            {hyperGmailStatus.nextScheduledRunAtMillis ? (
                              <span className="d-block mt-1 text-white-50">
                                Next batch after: {formatCancelledAtIst(hyperGmailStatus.nextScheduledRunAtMillis)}
                              </span>
                            ) : null}
                          </div>
                        ) : hyperGmailStatus.firstPullPending ? (
                          <span className="text-warning d-block mt-1">First pull pending — pick months below, then start.</span>
                        ) : (
                          <span className="text-success d-block mt-1">Delta mode — new Hyper emails only.</span>
                        )}
                        {hyperGmailStatus.lastPolledAtMillis ? (
                          <span className="d-block mt-1">Last polled: {formatCancelledAtIst(hyperGmailStatus.lastPolledAtMillis)}</span>
                        ) : null}
                        {hyperGmailStatus.lastStatus ? (
                          <span className="d-block mt-1 text-white-50">{hyperGmailStatus.lastStatus}</span>
                        ) : null}
                      </div>
                    ) : null}
                    <div className="d-flex flex-wrap gap-2 align-items-end mb-2">
                      <div>
                        <Form.Label className="text-secondary small mb-0">First-pull window</Form.Label>
                        <Form.Select
                          size="sm"
                          className="text-bg-dark border-secondary"
                          style={{ minWidth: 160 }}
                          value={hyperLookbackMonths}
                          onChange={(e) => setHyperLookbackMonths(Number(e.target.value))}
                          disabled={hyperGmailStatus && !hyperGmailStatus.firstPullPending && !hyperGmailStatus.backfillActive}
                          title={
                            hyperGmailStatus && !hyperGmailStatus.firstPullPending && !hyperGmailStatus.backfillActive
                              ? "Used only on first pull or after Reset cursor"
                              : "Months of Hyper emails to import (one month per scheduled batch)"
                          }
                        >
                          <option value={3}>3 months</option>
                          <option value={6}>6 months</option>
                          <option value={12}>12 months</option>
                          <option value={24}>24 months</option>
                          <option value={36}>36 months</option>
                          <option value={0}>All (to {hyperGmailStatus?.backfillFloorMonth || "2024-03"})</option>
                        </Form.Select>
                      </div>
                      <Button
                        size="sm"
                        variant="outline-light"
                        onClick={pollHyperGmail}
                        disabled={uploadBusyKey === "hyper-gmail" || hyperGmailStatus?.backfillRunning}
                      >
                        {uploadBusyKey === "hyper-gmail" || hyperGmailStatus?.backfillRunning
                          ? "Working…"
                          : hyperGmailStatus?.backfillActive
                            ? "Run next month now"
                            : hyperGmailStatus?.firstPullPending
                              ? "Start monthly backfill"
                              : "Get latest Hyper orders (delta)"}
                      </Button>
                      <Button size="sm" variant="outline-warning" onClick={resetHyperCursor} disabled={uploadBusyKey === "hyper-reset"}>
                        Reset delta cursor
                      </Button>
                    </div>
                  </details>
                </AnalyticsSubsectionPanel>

                <AnalyticsSubsectionPanel activeKey={uploadReconView} panelKey="ledger">
                  {reconSummary ? (
                    <p className="text-secondary small mb-2">
                      Run summary: total {reconSummary.totalRows}, already classified {reconSummary.alreadyClassified},
                      newly classified {reconSummary.newlyClassified}, unmatched {reconSummary.unmatched}.
                    </p>
                  ) : null}
                  <div className="finance-recon-scroll">
                    <Table striped bordered hover size="sm" variant="dark" responsive className="mb-0">
                      <thead>
                        <tr>
                          <th>Bank</th>
                          <th>Date</th>
                          <th>Particulars</th>
                          <th className="text-end">Debit ₹</th>
                          <th className="text-end">Credit ₹</th>
                          <th>Category</th>
                          <th>Matched as</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reconLedger.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="text-secondary small">
                              No bank rows in this window. Upload an ICICI / IDFC statement first.
                            </td>
                          </tr>
                        ) : (
                          reconLedger.map((r) => (
                            <tr key={r.id}>
                              <td>{r.bank}</td>
                              <td>{formatIsoDateIn(r.txnDateIso)}</td>
                              <td className="small">{r.particulars}</td>
                              <td className="text-end">{r.debitRupee ? formatInr(r.debitRupee) : "—"}</td>
                              <td className="text-end">{r.creditRupee ? formatInr(r.creditRupee) : "—"}</td>
                              <td className="small">{r.category || <span className="text-warning">unmatched</span>}</td>
                              <td className="small">{r.matchedEntityType || "—"}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </AnalyticsSubsectionPanel>

                <AnalyticsSubsectionPanel activeKey={uploadReconView} panelKey="rules">
                  <div className="border border-secondary rounded p-3">
                    <h4 className="h6 text-white-50 mb-3">Reconciliation rules</h4>
                <Table striped bordered hover size="sm" variant="dark" responsive className="mt-3 mb-2">
                  <thead>
                    <tr>
                      <th>Kind</th>
                      <th>Bank</th>
                      <th>Dir</th>
                      <th className="text-end">Expected ₹</th>
                      <th className="text-end">± ₹</th>
                      <th>Regex</th>
                      <th>Category</th>
                      <th className="text-end">Pri</th>
                      <th>On</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {reconRules.map((r) => (
                      <tr key={r.id}>
                        <td className="small">{r.ruleKind}</td>
                        <td className="small">{r.bank || "—"}</td>
                        <td className="small">{r.direction}</td>
                        <td className="text-end small">{r.expectedAmountRupee != null ? formatInr(r.expectedAmountRupee) : "—"}</td>
                        <td className="text-end small">{r.amountToleranceRupee != null ? formatInr(r.amountToleranceRupee) : "—"}</td>
                        <td className="small text-truncate" style={{ maxWidth: 180 }}>{r.particularsRegex || "—"}</td>
                        <td className="small">{r.category || "—"}</td>
                        <td className="text-end small">{r.priority}</td>
                        <td>{r.enabled ? "✓" : "✗"}</td>
                        <td>
                          <button type="button" className="btn btn-link btn-sm text-warning p-0" onClick={() => deleteReconRule(r.id)}>
                            delete
                          </button>
                        </td>
                      </tr>
                    ))}
                    {reconRules.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="text-secondary small">
                          No rules configured. Add one below.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </Table>
                <div className="row g-2">
                  <div className="col-md-3">
                    <Form.Label className="text-secondary small mb-1">Kind</Form.Label>
                    <Form.Select size="sm" className="text-bg-dark border-secondary"
                      value={reconRuleDraft.ruleKind}
                      onChange={(e) => setReconRuleDraft({ ...reconRuleDraft, ruleKind: e.target.value })}>
                      {[
                        "LAND_OWNER_RENT", "BUILDER_RENT", "MAINTENANCE_RENT", "ELECTRICITY",
                        "SALARY_NAME", "VENDOR_HYPER", "VENDOR_GENERIC", "INCOME_PETPOOJA", "OTHER_CATEGORY",
                      ].map((k) => (
                        <option key={k} value={k}>{k}</option>
                      ))}
                    </Form.Select>
                  </div>
                  <div className="col-md-2">
                    <Form.Label className="text-secondary small mb-1">Bank</Form.Label>
                    <Form.Select size="sm" className="text-bg-dark border-secondary"
                      value={reconRuleDraft.bank || ""}
                      onChange={(e) => setReconRuleDraft({ ...reconRuleDraft, bank: e.target.value })}>
                      <option value="">Any</option>
                      <option value="ICICI">ICICI</option>
                      <option value="IDFC">IDFC</option>
                    </Form.Select>
                  </div>
                  <div className="col-md-2">
                    <Form.Label className="text-secondary small mb-1">Direction</Form.Label>
                    <Form.Select size="sm" className="text-bg-dark border-secondary"
                      value={reconRuleDraft.direction}
                      onChange={(e) => setReconRuleDraft({ ...reconRuleDraft, direction: e.target.value })}>
                      <option value="DR">DR</option>
                      <option value="CR">CR</option>
                    </Form.Select>
                  </div>
                  <div className="col-md-2">
                    <Form.Label className="text-secondary small mb-1">Expected ₹</Form.Label>
                    <Form.Control size="sm" type="number" className="text-bg-dark border-secondary"
                      value={reconRuleDraft.expectedAmountRupee}
                      onChange={(e) => setReconRuleDraft({ ...reconRuleDraft, expectedAmountRupee: e.target.value })} />
                  </div>
                  <div className="col-md-2">
                    <Form.Label className="text-secondary small mb-1">Tolerance ₹</Form.Label>
                    <Form.Control size="sm" type="number" className="text-bg-dark border-secondary"
                      value={reconRuleDraft.amountToleranceRupee}
                      onChange={(e) => setReconRuleDraft({ ...reconRuleDraft, amountToleranceRupee: e.target.value })} />
                  </div>
                  <div className="col-md-1">
                    <Form.Label className="text-secondary small mb-1">Pri</Form.Label>
                    <Form.Control size="sm" type="number" className="text-bg-dark border-secondary"
                      value={reconRuleDraft.priority}
                      onChange={(e) => setReconRuleDraft({ ...reconRuleDraft, priority: e.target.value })} />
                  </div>
                  <div className="col-md-6">
                    <Form.Label className="text-secondary small mb-1">Particulars regex</Form.Label>
                    <Form.Control size="sm" type="text" className="text-bg-dark border-secondary"
                      placeholder="(?i)NEFT.*BENEFICIARY"
                      value={reconRuleDraft.particularsRegex}
                      onChange={(e) => setReconRuleDraft({ ...reconRuleDraft, particularsRegex: e.target.value })} />
                  </div>
                  <div className="col-md-4">
                    <Form.Label className="text-secondary small mb-1">Category label</Form.Label>
                    <Form.Control size="sm" type="text" className="text-bg-dark border-secondary"
                      value={reconRuleDraft.category}
                      onChange={(e) => setReconRuleDraft({ ...reconRuleDraft, category: e.target.value })} />
                  </div>
                  <div className="col-md-2 d-flex align-items-end">
                    <Form.Check type="checkbox" label="enabled" className="text-secondary"
                      checked={!!reconRuleDraft.enabled}
                      onChange={(e) => setReconRuleDraft({ ...reconRuleDraft, enabled: e.target.checked })} />
                  </div>
                  <div className="col-12 mt-2">
                    <Button size="sm" variant="outline-light" disabled={reconRuleBusy} onClick={saveReconRule}>
                      {reconRuleBusy ? "Saving…" : "Add / update rule"}
                    </Button>
                  </div>
                </div>
                  </div>
                </AnalyticsSubsectionPanel>

                <AnalyticsSubsectionPanel activeKey={uploadReconView} panelKey="stock">
              <h4 className="h6 text-info mb-2">Stock report — {stockBundle?.periodKey || reconPeriod}</h4>
              <p className="text-secondary small mb-2">
                Compares each category's line-item ₹ for the chosen month vs the previous month. Click a category to drill down.
              </p>
              <Table striped bordered hover size="sm" variant="dark" responsive className="mb-3">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th className="text-end">This month ₹</th>
                    <th className="text-end">Previous ₹</th>
                    <th className="text-end">Δ ₹</th>
                  </tr>
                </thead>
                <tbody>
                  {(stockBundle?.rows || []).map((row) => (
                    <tr key={row.category}>
                      <td>
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 finance-stock-cat-link"
                          onClick={() => { setStockDrillCategory(row.category); loadStockDrill(row.category); }}
                        >
                          {row.category}
                        </button>
                      </td>
                      <td className="text-end">{formatInr(row.currentMonthRupee)}</td>
                      <td className="text-end">{formatInr(row.previousMonthRupee)}</td>
                      <td className={`text-end ${Number(row.deltaRupee) > 0 ? "text-warning" : "text-success"}`}>
                        {formatInr(row.deltaRupee)}
                      </td>
                    </tr>
                  ))}
                  {(stockBundle?.rows || []).length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-secondary small">
                        No line items in this window. Pull Hyper Gmail orders or import bank-classified DR rows.
                      </td>
                    </tr>
                  ) : (
                    <tr className="fw-semibold">
                      <td>Total</td>
                      <td className="text-end">{formatInr(stockBundle?.totalCurrentMonthRupee)}</td>
                      <td className="text-end">{formatInr(stockBundle?.totalPreviousMonthRupee)}</td>
                      <td className="text-end">{formatInr(stockBundle?.totalDeltaRupee)}</td>
                    </tr>
                  )}
                </tbody>
              </Table>

              {stockDrillCategory ? (
                <details open className="mb-3 border border-secondary rounded p-3">
                  <summary className="text-white-50 small">Drill-down — {stockDrillCategory}</summary>
                  <Table striped bordered hover size="sm" variant="dark" responsive className="mt-2 mb-0">
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th className="text-end">Qty</th>
                        <th>Unit</th>
                        <th className="text-end">Price unit</th>
                        <th className="text-end">GST amt</th>
                        <th className="text-end">₹</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stockDrillRows.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-secondary small">No line items recorded for this category.</td>
                        </tr>
                      ) : (
                        stockDrillRows.map((r, i) => (
                          <tr key={`${r.itemName}-${i}`}>
                            <td>{r.itemName}</td>
                            <td className="text-end">{r.qtyTotal != null ? r.qtyTotal : "—"}</td>
                            <td>{r.qtyUnit || "—"}</td>
                            <td className="text-end">{formatPaiseAsInr(r.avgUnitPricePaise)}</td>
                            <td className="text-end">{formatPaiseAsInr(r.totalGstAmountPaise)}</td>
                            <td className="text-end">{formatInr(r.amountRupee)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </details>
              ) : null}
                </AnalyticsSubsectionPanel>

                <AnalyticsSubsectionPanel activeKey={uploadReconView} panelKey="hyper">
              <h4 className="h6 text-info mb-2">
                Hyper supplier — kitchen stock month-wise
              </h4>
              <p className="text-secondary small mb-2">
                Compare what you order from Hyperpure by category and item across months. Trend: Up / Down / Stable vs oldest month in window.
              </p>
              <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
                <div>
                  <Form.Label className="text-secondary small mb-0">End month</Form.Label>
                  <AdminDateInput
                    inputType="month"
                    size="sm"
                    style={{ width: 160 }}
                    value={reconPeriod}
                    onChange={(e) => setReconPeriod(e.target.value || yyyyMmToday())}
                  />
                </div>
                <div>
                  <Form.Label className="text-secondary small mb-0">Compare</Form.Label>
                  <Form.Select size="sm" className="text-bg-dark border-secondary" style={{ width: 100 }}
                    value={hyperCompareMonths} onChange={(e) => setHyperCompareMonths(Number(e.target.value))}>
                    {[3, 4, 6, 9, 12].map((n) => <option key={n} value={n}>{n} mo</option>)}
                  </Form.Select>
                </div>
                <Button size="sm" variant="outline-light" disabled={hyperCompareBusy} onClick={() => { loadHyperCompare(); loadHyperItems(); }}>
                  Refresh
                </Button>
              </div>
              {hyperCompareBusy ? <Spinner size="sm" animation="border" className="text-light mb-2" /> : null}
              {hyperCompare?.periodLabels?.length ? (
                <>
                  <p className="text-secondary small mb-2">
                    Total Hyper spend: {formatInr(hyperCompare.totalOldestMonthRupee)} → {formatInr(hyperCompare.totalLatestMonthRupee)} (oldest → latest month).
                  </p>
                  <h5 className="h6 text-info mt-3">By kitchen category</h5>
                  <div className="finance-recon-scroll mb-4">
                    <Table striped bordered hover size="sm" variant="dark" responsive className="mb-0 finance-comparison-table">
                      <thead>
                        <tr>
                          <th>Category</th>
                          {(hyperCompare.periodLabels || []).map((lbl) => (
                            <th key={`hc-${lbl}`} className="text-end">{lbl}</th>
                          ))}
                          <th className="text-end">Δ</th>
                          <th>Trend</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(hyperCompare.categories || []).map((c) => (
                          <tr key={`hcc-${c.category}`}>
                            <td>{c.category}</td>
                            {(c.amountsRupee || []).map((a, i) => (
                              <td key={`hcc-a-${c.category}-${i}`} className="text-end">{formatInr(a)}</td>
                            ))}
                            <td className={`text-end ${Number(c.deltaLatestVsOldestRupee) >= 0 ? "text-warning" : "text-success"}`}>
                              {formatSignedRevenueDelta(c.deltaLatestVsOldestRupee)}
                            </td>
                            <td>{c.trend}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  <h5 className="h6 text-info">Top items (by total spend)</h5>
                  <div className="finance-recon-scroll mb-4">
                    <Table striped bordered hover size="sm" variant="dark" responsive className="mb-0 finance-comparison-table">
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th>Category</th>
                          {(hyperCompare.periodLabels || []).map((lbl) => (
                            <th key={`hi-${lbl}`} className="text-end">{lbl}</th>
                          ))}
                          <th className="text-end">Δ</th>
                          <th>Trend</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(hyperCompare.topItems || []).map((it) => (
                          <tr key={`hit-${it.itemName}`}>
                            <td className="small">{it.itemName}</td>
                            <td>{it.category}</td>
                            {(it.amountsRupee || []).map((a, i) => (
                              <td key={`hit-a-${it.itemName}-${i}`} className="text-end">{formatInr(a)}</td>
                            ))}
                            <td className="text-end">{formatSignedRevenueDelta(it.deltaLatestVsOldestRupee)}</td>
                            <td>{it.trend}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </>
              ) : null}
              <h5 className="h6 text-info mb-2">This month detail ({reconPeriod})</h5>
              <p className="text-secondary small mb-2">
                Line items from Hyper Gmail for {reconPeriod}. Run &quot;Get latest orders&quot; above to refresh.
              </p>
              {hyperItemsBusy ? <Spinner size="sm" animation="border" className="text-light mb-2" /> : null}
              <Table striped bordered hover size="sm" variant="dark" responsive className="mb-3">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Category</th>
                    <th className="text-end">Orders</th>
                    <th className="text-end">Qty</th>
                    <th>Unit</th>
                    <th className="text-end">Price unit</th>
                    <th className="text-end">GST amt</th>
                    <th className="text-end">Amount ₹</th>
                  </tr>
                </thead>
                <tbody>
                  {hyperItems.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-secondary small">
                        No Hyper line items in {reconPeriod}. Pull Gmail to populate.
                      </td>
                    </tr>
                  ) : (
                    hyperItems.map((row, i) => (
                      <tr key={`hi-${row.itemName}-${i}`}>
                        <td>{row.itemName}</td>
                        <td>{row.category || "—"}</td>
                        <td className="text-end">{row.orderCount}</td>
                        <td className="text-end">{row.qtyTotal != null ? row.qtyTotal : "—"}</td>
                        <td>{row.qtyUnit || "—"}</td>
                        <td className="text-end">{formatInrDecimal(row.avgUnitPriceRupee)}</td>
                        <td className="text-end">{formatInrDecimal(row.totalGstAmountRupee)}</td>
                        <td className="text-end">{formatInr(row.amountRupee)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
                </AnalyticsSubsectionPanel>

                <AnalyticsSubsectionPanel activeKey={uploadReconView} panelKey="hyper-lines">
              <h4 className="h6 text-info mb-2">Hyper line items — orders &amp; drill-down</h4>
              <p className="text-secondary small mb-2">
                One row per Hyper order number for {reconPeriod}. Click the order number to load all line items below.
              </p>
              <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
                <div>
                  <Form.Label className="text-secondary small mb-0">Month</Form.Label>
                  <AdminDateInput
                    inputType="month"
                    size="sm"
                    style={{ width: 160 }}
                    value={reconPeriod}
                    onChange={(e) => {
                      setReconPeriod(e.target.value || yyyyMmToday());
                      setSelectedHyperOrderId("");
                      setHyperOrderLines([]);
                      setHyperOrdersTableOpen(true);
                      setHyperLinesTableOpen(false);
                    }}
                  />
                </div>
                <Button size="sm" variant="outline-light" disabled={hyperOrdersBusy} onClick={loadHyperOrders}>
                  Refresh orders
                </Button>
              </div>
              <details
                open={hyperOrdersTableOpen}
                className="mb-3 border border-secondary rounded p-3"
                onToggle={(e) => setHyperOrdersTableOpen(e.currentTarget.open)}
              >
                <summary className="text-white-50 small fw-semibold user-select-none">
                  Hyper orders ({reconPeriod})
                </summary>
                {hyperOrdersBusy ? (
                  <Spinner size="sm" animation="border" className="text-light mt-3" />
                ) : (
                  <Table striped bordered hover size="sm" variant="dark" responsive className="mb-0 mt-3">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Order number (Hyper)</th>
                        <th className="text-end">Line items</th>
                        <th className="text-end">Total amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hyperOrders.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="text-secondary small">
                            No Hyper orders in {reconPeriod}. Pull Gmail on All uploads tab first.
                          </td>
                        </tr>
                      ) : (
                        hyperOrders.map((row) => {
                          const oid = row.orderId || "";
                          const selected = selectedHyperOrderId === oid;
                          return (
                            <tr key={`ho-${row.expenseDate}-${oid}`} className={selected ? "table-active" : ""}>
                              <td>{formatIsoDateIn(row.expenseDate)}</td>
                              <td>
                                <button
                                  type="button"
                                  className="hyper-order-number-link"
                                  onClick={() => {
                                    setSelectedHyperOrderId(oid);
                                    loadHyperOrderLines(oid);
                                    setHyperOrdersTableOpen(false);
                                    setHyperLinesTableOpen(true);
                                  }}
                                >
                                  {oid || "—"}
                                </button>
                              </td>
                              <td className="text-end">{row.lineItemCount ?? "—"}</td>
                              <td className="text-end">{formatInr(row.totalAmountRupee)}</td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                )}
              </details>
              {selectedHyperOrderId ? (
                <details
                  open={hyperLinesTableOpen}
                  className="mb-3 border border-secondary rounded p-3"
                  onToggle={(e) => setHyperLinesTableOpen(e.currentTarget.open)}
                >
                  <summary className="text-white-50 small fw-semibold user-select-none">
                    Line items — order {selectedHyperOrderId}
                    {hyperOrderLinesBusy ? null : (
                      <span className="ms-2 text-warning">
                        Total{" "}
                        {formatInr(
                          hyperOrderLines.length > 0
                            ? hyperOrderLinesTotalRupee
                            : selectedHyperOrderSummary?.totalAmountRupee
                        )}
                      </span>
                    )}
                  </summary>
                  {hyperOrderLinesBusy ? (
                    <Spinner size="sm" animation="border" className="text-light mt-3" />
                  ) : (
                    <Table striped bordered hover size="sm" variant="dark" responsive className="mb-0 mt-3">
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th>Category</th>
                          <th className="text-end">Qty</th>
                          <th>Unit</th>
                          <th className="text-end">Price unit</th>
                          <th className="text-end">GST amt</th>
                          <th className="text-end">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {hyperOrderLines.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="text-secondary small">
                              No line items for this order.
                            </td>
                          </tr>
                        ) : (
                          <>
                            {hyperOrderLines.map((ln, i) => (
                              <tr key={`hol-${selectedHyperOrderId}-${i}`}>
                                <td>{ln.itemName || "—"}</td>
                                <td>{ln.category || "—"}</td>
                                <td className="text-end">{ln.qtyValue != null ? ln.qtyValue : "—"}</td>
                                <td>{ln.qtyUnit || "—"}</td>
                                <td className="text-end">{formatInrDecimal(ln.unitPriceRupee)}</td>
                                <td className="text-end">{formatInrDecimal(ln.gstAmountRupee)}</td>
                                <td className="text-end">{formatInrDecimal(ln.amountRupee)}</td>
                              </tr>
                            ))}
                            <tr className={ANALYTICS_TABLE_TOTAL_ROW_CLASS}>
                              <td colSpan={5} className="text-end">
                                Order total
                              </td>
                              <td className="text-end">
                                <TableTotalAmount
                                  amount={hyperOrderLinesTotalGstRupee}
                                  toneClass="text-secondary"
                                />
                              </td>
                              <td className="text-end">
                                <TableTotalAmount amount={hyperOrderLinesTotalRupee} toneClass="text-warning" />
                              </td>
                            </tr>
                          </>
                        )}
                      </tbody>
                    </Table>
                  )}
                </details>
              ) : (
                <p className="text-secondary small mb-0">Select an order number above to view its line items.</p>
              )}
                </AnalyticsSubsectionPanel>

                <AnalyticsSubsectionPanel activeKey={uploadReconView} panelKey="plastic">
              <h4 className="h6 text-info mb-2">Ramdev plastic vendor — WhatsApp orders</h4>
              <p className="text-secondary small mb-2">
                Messages with only a total and no line items are ignored. Net payable = opening balance (as of cutoff date)
                + line-item amounts after that date − bank payments to ashvinpatel9920.
              </p>
              <div className="border border-secondary rounded p-3 mb-3">
                <h5 className="h6 text-white mb-2">Opening balance &amp; cutoff date</h5>
                <p className="text-secondary small mb-2">Configure once for production — used for net payable calculation.</p>
                <div className="row g-2 align-items-end">
                  <div className="col-md-4">
                    <Form.Label className="text-secondary small">Opening balance ₹</Form.Label>
                    <Form.Control
                      size="sm"
                      type="number"
                      min={0}
                      value={plasticSettings.openingBalanceRupee ?? 0}
                      onChange={(e) => setPlasticSettings((s) => ({ ...s, openingBalanceRupee: Number(e.target.value) || 0 }))}
                    />
                  </div>
                  <div className="col-md-4">
                    <Form.Label className="text-secondary small">Cutoff date (orders after this date count)</Form.Label>
                    <AdminDateInput
                      size="sm"
                      value={plasticSettings.openingBalanceDateIso || ""}
                      onChange={(e) => setPlasticSettings((s) => ({ ...s, openingBalanceDateIso: e.target.value }))}
                    />
                  </div>
                  <div className="col-md-4 d-flex flex-wrap gap-2">
                    <Button size="sm" variant="success" disabled={plasticSettingsBusy} onClick={() => savePlasticSettings()}>
                      {plasticSettingsBusy ? "Saving…" : "Save opening settings"}
                    </Button>
                    <Button size="sm" variant="outline-danger" disabled={plasticSettingsBusy} onClick={() => resetPlasticData()}>
                      Clear all plastic data
                    </Button>
                  </div>
                </div>
              </div>
              {plasticLedgerBusy ? <Spinner size="sm" animation="border" className="text-light mb-2" /> : null}
              <Table striped bordered hover size="sm" variant="dark" responsive className="mb-3">
                <thead>
                  <tr>
                    <th>Metric</th>
                    <th className="text-end">Amount ₹</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>Opening balance (as of {formatIsoDateIn(plasticLedger?.openingBalanceDateIso) || "—"})</td><td className="text-end">{formatInr(plasticLedger?.openingBalanceRupee)}</td></tr>
                  <tr><td>+ Line items after cutoff date</td><td className="text-end text-warning">{formatInr(plasticLedger?.ordersAfterOpeningRupee)}</td></tr>
                  {(plasticLedger?.ordersStatedTotalRupee ?? 0) > 0 ? (
                    <tr><td className="text-secondary small ps-3">Vendor stated totals (WhatsApp)</td><td className="text-end text-secondary">{formatInr(plasticLedger?.ordersStatedTotalRupee)}</td></tr>
                  ) : null}
                  {(plasticLedger?.totalMismatchCount ?? 0) > 0 ? (
                    <tr><td className="text-danger small">Orders with stated ≠ line sum</td><td className="text-end text-danger">{plasticLedger.totalMismatchCount}</td></tr>
                  ) : null}
                  <tr><td>− Bank payments (ashvinpatel9920)</td><td className="text-end text-success">{formatInr(plasticLedger?.bankPaymentsAfterOpeningRupee)}</td></tr>
                  <tr className="fw-bold"><td>Net payable now</td><td className="text-end text-info">{formatInr(plasticLedger?.netPayableRupee)}</td></tr>
                </tbody>
              </Table>
              <div className="d-flex gap-2 mb-3">
                <Button size="sm" variant="outline-light" onClick={() => loadPlasticLedger()} disabled={plasticLedgerBusy}>Refresh</Button>
              </div>
              {(plasticLedger?.aggregatedItems || []).length > 0 ? (
                <>
                  <h5 className="h6 text-info mb-2">What we bought — {reconPeriod}</h5>
                  <p className="text-secondary small mb-2">Same item bought multiple times: qty and amount add up across all orders in this month.</p>
                  <Table striped bordered hover size="sm" variant="dark" responsive className="mb-3">
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th className="text-end">Total qty</th>
                        <th className="text-end">Line count</th>
                        <th className="text-end">Total ₹</th>
                      </tr>
                    </thead>
                    <tbody>
                      {plasticLedger.aggregatedItems.map((it, idx) => (
                        <tr key={`pl-agg-${idx}-${it.itemName}`}>
                          <td>{it.itemName}</td>
                          <td className="text-end">{it.qtyTotal != null ? `${Number(it.qtyTotal)} ${it.qtyUnit || ""}`.trim() : "—"}</td>
                          <td className="text-end">{it.orderCount ?? "—"}</td>
                          <td className="text-end fw-semibold">{formatInr(it.amountRupee)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              ) : null}
              <Table striped bordered hover size="sm" variant="dark" responsive className="mb-3">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th className="text-end">Vendor stated ₹</th>
                    <th className="text-end">Lines sum ₹</th>
                    <th>Check</th>
                    <th className="text-end">Lines</th>
                    <th>Vendor</th>
                  </tr>
                </thead>
                <tbody>
                  {(plasticLedger?.orders || []).length === 0 ? (
                    <tr><td colSpan={6} className="text-secondary small">No orders in {reconPeriod}. Upload WhatsApp .zip above.</td></tr>
                  ) : (
                    plasticLedger.orders.map((o) => {
                      const oid = String(o.id ?? `${o.orderDateIso}-${o.totalRupee}`);
                      const expanded = plasticExpandedOrderIds.has(oid);
                      const lineRows = o.lines || [];
                      const hasLines = lineRows.length > 0 && !lineRows.every((ln) => (ln.itemName || "").toLowerCase().includes("total only"));
                      return (
                        <React.Fragment key={`pl-${oid}`}>
                          <tr className={o.totalMismatch ? "table-warning" : undefined}>
                            <td>
                              {hasLines ? (
                                <button type="button" className="btn btn-link btn-sm text-info p-0 me-1" onClick={() => togglePlasticOrderExpand(oid)} aria-label={expanded ? "Collapse" : "Expand"}>
                                  {expanded ? "▼" : "▶"}
                                </button>
                              ) : null}
                              {formatIsoDateIn(o.orderDateIso)}
                            </td>
                            <td className="text-end text-secondary">{formatInr(o.statedTotalRupee ?? o.totalRupee)}</td>
                            <td className="text-end fw-semibold">{formatInr(o.calculatedTotalRupee ?? o.totalRupee)}</td>
                            <td>{o.totalMismatch ? <span className="text-danger small">Mismatch</span> : hasLines ? <span className="text-success small">OK</span> : <span className="text-secondary small">Total only</span>}</td>
                            <td className="text-end">{o.lineCount ?? lineRows.length}</td>
                            <td className="small">{o.vendorLabel || "—"}</td>
                          </tr>
                          {expanded && hasLines ? (
                            <tr>
                              <td colSpan={6} className="bg-dark border-0 pt-0">
                                <Table size="sm" variant="dark" className="mb-0 ms-3 border-secondary">
                                  <thead><tr><th>Item</th><th className="text-end">Qty</th><th className="text-end">Amount ₹</th></tr></thead>
                                  <tbody>
                                    {lineRows.filter((ln) => !(ln.itemName || "").toLowerCase().includes("total only")).map((ln, li) => (
                                      <tr key={`pl-ln-${oid}-${li}`}>
                                        <td className="small">{ln.itemName}</td>
                                        <td className="text-end small">{ln.qtyLabel || "—"}</td>
                                        <td className="text-end small">{formatInr(ln.amountRupee)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              </td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </Table>
              {(plasticLedger?.payments || []).length ? (
                <>
                  <h5 className="h6 text-info mb-2">Matched bank payments</h5>
                  <Table striped bordered hover size="sm" variant="dark" responsive className="mb-0">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Bank</th>
                        <th>Particulars</th>
                        <th className="text-end">Debit ₹</th>
                      </tr>
                    </thead>
                    <tbody>
                      {plasticLedger.payments.map((p) => (
                        <tr key={`pp-${p.ledgerEntryId}`}>
                          <td>{formatIsoDateIn(p.txnDateIso)}</td>
                          <td>{p.bank}</td>
                          <td className="small">{p.particulars}</td>
                          <td className="text-end text-success">{formatInr(p.debitRupee)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              ) : null}
                </AnalyticsSubsectionPanel>

                <AnalyticsSubsectionPanel activeKey={uploadReconView} panelKey="vendors">
              <h4 className="h6 text-info mb-2">
                Vendor income from bank statement ({vendorIncomeRows.length})
              </h4>
              <p className="text-secondary small mb-2">
                Bank narration scanned for Swiggy / Zomato / EasyDiner; CR rows are vendor payouts, DR rows are
                refunds or commission deductions. Window: {reconRangeIso.fromIso} → {reconRangeIso.toIso}.
              </p>
              {vendorIncomeBusy ? <Spinner size="sm" animation="border" className="text-light mb-2" /> : null}
              <Table striped bordered hover size="sm" variant="dark" responsive className="mb-3">
                <thead>
                  <tr>
                    <th>Vendor</th>
                    <th className="text-end">Credits ₹</th>
                    <th className="text-end"># CR</th>
                    <th className="text-end">Debits ₹</th>
                    <th className="text-end"># DR</th>
                    <th className="text-end">Net ₹</th>
                  </tr>
                </thead>
                <tbody>
                  {vendorIncomeRows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-secondary small">
                        No matching Swiggy / Zomato / EasyDiner rows. Upload an ICICI / IDFC statement first.
                      </td>
                    </tr>
                  ) : (
                    vendorIncomeRows.map((row) => (
                      <tr key={`vi-${row.vendor}`}>
                        <td>{row.displayLabel}</td>
                        <td className="text-end">{formatInr(row.creditRupee)}</td>
                        <td className="text-end">{row.creditCount}</td>
                        <td className="text-end">{formatInr(row.debitRupee)}</td>
                        <td className="text-end">{row.debitCount}</td>
                        <td className={`text-end ${Number(row.netRupee) < 0 ? "text-warning" : "text-success"}`}>
                          {formatInr(row.netRupee)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
                </AnalyticsSubsectionPanel>

                <AnalyticsSubsectionPanel activeKey={uploadReconView} panelKey="dayend">
              <h4 className="h6 text-info mb-2">
                Petpooja day-end vs ICICI settlement ({dayendMatchRows.length})
              </h4>
              <p className="text-secondary small mb-2">
                Each day&apos;s Petpooja total is paired to the closest ICICI CR whose narration matches{" "}
                <code>FT-MESPOS SET …</code> (embedded sale date in narration, or approx amount within ±5% / ₹150
                on the same day or up to 3 days later). One bank credit is used for at most one sale day.
              </p>
              {dayendMatchBusy ? <Spinner size="sm" animation="border" className="text-light mb-2" /> : null}
              <div className="finance-recon-scroll">
                <Table striped bordered hover size="sm" variant="dark" responsive className="mb-0">
                  <thead>
                    <tr>
                      <th>Sale date</th>
                      <th className="text-end">Day-end ₹</th>
                      <th className="text-end">ICICI settled ₹</th>
                      <th>Match</th>
                      <th className="text-end">Δ ₹</th>
                      <th>Credit date(s)</th>
                      <th>Settlement narrations</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dayendMatchRows.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-secondary small">
                          Upload Petpooja Day-End + ICICI statement to see matched settlements.
                        </td>
                      </tr>
                    ) : (
                      dayendMatchRows.map((row) => {
                        const settled =
                          row.matchedCreditRupee != null
                            ? row.matchedCreditRupee
                            : (Number(row.sameDaySettlementRupee) || 0) + (Number(row.nextDaySettlementRupee) || 0);
                        const status = (row.matchStatus || "NONE").toUpperCase();
                        const statusClass =
                          status === "EXACT"
                            ? "finance-dayend-match-exact"
                            : status === "APPROX"
                              ? "finance-dayend-match-approx"
                              : "finance-dayend-match-partial";
                        return (
                          <tr key={`pm-${row.saleDateIso}`}>
                            <td>{formatIsoDateIn(row.saleDateIso)}</td>
                            <td className="text-end">{formatInr(row.dayEndTotalRupee)}</td>
                            <td className="text-end fw-semibold">{formatInr(settled)}</td>
                            <td className={`small ${statusClass}`}>{status}</td>
                            <td
                              className={`text-end ${
                                status === "EXACT" || status === "APPROX" ? "text-success" : "text-warning"
                              }`}
                            >
                              {formatInr(row.deltaRupee)}
                            </td>
                            <td className="small text-light">
                              {row.matchedSettlementRows && row.matchedSettlementRows.length
                                ? [...new Set(row.matchedSettlementRows.map((rr) => formatIsoDateIn(rr.txnDateIso)))].join(
                                    ", "
                                  )
                                : "—"}
                            </td>
                            <td className="small text-secondary" style={{ maxWidth: 280, whiteSpace: "normal" }}>
                              {row.matchedSettlementRows && row.matchedSettlementRows.length
                                ? row.matchedSettlementRows
                                    .map((rr) => `${formatIsoDateIn(rr.txnDateIso)} · ${formatInr(rr.creditRupee)} · ${(rr.particulars || "").slice(0, 48)}`)
                                    .join(" · ")
                                : "—"}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>
                </AnalyticsSubsectionPanel>

                <AnalyticsSubsectionPanel activeKey={uploadReconView} panelKey="petpooja">
              <h4 className="h6 text-info mb-2">
                Petpooja monthly summary ({petpoojaMonthly.length})
              </h4>
              <p className="text-secondary small mb-2">
                Built from the Orders Master upload — only month-totals are stored, not individual invoices.
              </p>
              {petpoojaMonthlyBusy ? <Spinner size="sm" animation="border" className="text-light mb-2" /> : null}
              <Table striped bordered hover size="sm" variant="dark" responsive className="mb-3">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th className="text-end">Dine-In ₹</th>
                    <th className="text-end">Parcel ₹</th>
                    <th className="text-end">Zomato ₹</th>
                    <th className="text-end">Swiggy ₹</th>
                    <th className="text-end">EasyDiner ₹</th>
                    <th className="text-end">Other ₹</th>
                    <th className="text-end">Invoices</th>
                    <th className="text-end">Total ₹</th>
                  </tr>
                </thead>
                <tbody>
                  {petpoojaMonthly.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-secondary small">
                        Upload an Orders Master report to populate monthly totals.
                      </td>
                    </tr>
                  ) : (
                    petpoojaMonthly.map((row) => (
                      <tr key={`pmm-${row.periodKey}`}>
                        <td>{row.periodKey}</td>
                        <td className="text-end">{formatInr(row.dineInRupee)}</td>
                        <td className="text-end">{formatInr(row.deliveryParcelRupee)}</td>
                        <td className="text-end">{formatInr(row.zomatoRupee)}</td>
                        <td className="text-end">{formatInr(row.swiggyRupee)}</td>
                        <td className="text-end">{formatInr(row.easydinerRupee)}</td>
                        <td className="text-end">{formatInr(row.otherDeliveryRupee)}</td>
                        <td className="text-end">{row.invoiceCount}</td>
                        <td className="text-end fw-semibold">{formatInr(row.totalRupee)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
                </AnalyticsSubsectionPanel>

                <AnalyticsSubsectionPanel activeKey={uploadReconView} panelKey="salary">
              <h4 className="h6 text-info mb-2">Employee salary ledger</h4>
              <div className="d-flex flex-wrap gap-2 align-items-end mb-2">
                <div>
                  <Form.Label className="text-secondary small mb-1">Month</Form.Label>
                  <Form.Select
                    size="sm"
                    className="text-bg-dark border-secondary"
                    style={{ width: 140 }}
                    value={salaryPick}
                    onChange={(e) => setSalaryPick(e.target.value)}
                    disabled={salaryMonths.length === 0}
                  >
                    {salaryMonths.length === 0 ? (
                      <option value="">No data yet</option>
                    ) : (
                      salaryMonths.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))
                    )}
                  </Form.Select>
                </div>
                <Button
                  size="sm"
                  variant="outline-light"
                  type="button"
                  disabled={!salaryPick || salaryBusy}
                  onClick={() => loadSalaryRows(salaryPick)}
                >
                  Refresh
                </Button>
              </div>
              {salaryBusy ? <Spinner size="sm" animation="border" className="text-light mb-2" /> : null}
              <Table striped bordered hover size="sm" variant="dark" responsive className="mb-3">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th className="text-end">Fixed ₹</th>
                    <th className="text-end">Paid ₹</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {salaryRows.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-secondary small">
                        Upload the EMP salary workbook to see rows here.
                      </td>
                    </tr>
                  ) : (
                    salaryRows.map((row) => (
                      <tr key={`sal-${row.id}`}>
                        <td>{row.empName}</td>
                        <td className="text-end">{formatInr(row.fixedSalaryRupee)}</td>
                        <td className="text-end">{formatInr(row.paidSalaryRupee)}</td>
                        <td>{row.status || "—"}</td>
                      </tr>
                    ))
                  )}
                  {salaryRows.length ? (
                    <tr className="fw-semibold">
                      <td>Total</td>
                      <td className="text-end">
                        {formatInr(salaryRows.reduce((s, r) => s + (Number(r.fixedSalaryRupee) || 0), 0))}
                      </td>
                      <td className="text-end">
                        {formatInr(salaryRows.reduce((s, r) => s + (Number(r.paidSalaryRupee) || 0), 0))}
                      </td>
                      <td />
                    </tr>
                  ) : null}
                </tbody>
              </Table>
                </AnalyticsSubsectionPanel>
            </AnalyticsLoungeDashboard>
          </AnalyticsSubsectionPanel>

          <AnalyticsSubsectionPanel activeKey={bizTab} panelKey="overview">
            <div className="admin-console-card p-3 rounded border border-secondary">
              <h3 className="h6 text-info mb-2">High-level position — money in vs money out</h3>
              <p className="text-secondary small mb-3">
                Taranga restaurant &amp; banquet from {formatIsoDateIn(executiveSummary?.tarangaStartedOnIso) || "14 Feb 2024"}.
                PartyBoss theaters from {formatIsoDateIn(executiveSummary?.partybossStartedOnIso) || "1 Dec 2024"}.
                Upload full bank statements, Hyper Gmail, WhatsApp plastic, salary &amp; banquet data in PROD for accurate totals.
              </p>
              <Button size="sm" variant="outline-light" className="mb-3" disabled={executiveSummaryBusy} onClick={() => loadExecutiveSummary()}>
                Refresh position
              </Button>
              {executiveSummaryBusy ? <Spinner size="sm" animation="border" className="text-light mb-2" /> : null}
              {executiveSummary ? (
                <>
                  <div className="row g-3 mb-4">
                    <div className="col-md-3">
                      <div className="p-3 rounded border border-success border-opacity-25">
                        <div className="text-secondary small">Total money in</div>
                        <AmountWithWords amount={executiveSummary.totalMoneyInRupee} valueClassName="text-success" />
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="p-3 rounded border border-warning border-opacity-25">
                        <div className="text-secondary small">Total money out</div>
                        <AmountWithWords amount={executiveSummary.totalMoneyOutRupee} valueClassName="text-warning" />
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="p-3 rounded border border-info border-opacity-25">
                        <div className="text-secondary small">Operating P&amp;L</div>
                        <AmountWithWords
                          amount={executiveSummary.operatingProfitRupee}
                          valueClassName={
                            Number(executiveSummary.operatingProfitRupee) < 0 ? "text-warning" : "text-success"
                          }
                        />
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="p-3 rounded border border-danger border-opacity-25">
                        <div className="text-secondary small">After capital invested</div>
                        <AmountWithWords amount={executiveSummary.economicPositionRupee} valueClassName="text-danger" />
                        <div className="text-secondary small">Invested {formatInr(executiveSummary.totalCapitalInvestedRupee)}</div>
                      </div>
                    </div>
                  </div>
                  <Table striped bordered hover size="sm" variant="dark" responsive className="mb-4">
                    <thead>
                      <tr>
                        <th>Section</th>
                        <th>Line item</th>
                        <th>Detail</th>
                        <th className="text-end">Money in</th>
                        <th className="text-end">Money out</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(executiveSummary.lines || []).map((ln, idx) => (
                        <tr key={`exec-${idx}-${ln.label}`} className={ln.label?.includes("Total") ? "fw-semibold" : undefined}>
                          <td>{ln.section}</td>
                          <td>{ln.label}</td>
                          <td className="small text-secondary">{ln.detail || "—"}</td>
                          <td className="text-end text-success">{ln.inflowRupee != null ? formatInr(ln.inflowRupee) : "—"}</td>
                          <td className="text-end text-warning">{ln.outflowRupee != null ? formatInr(ln.outflowRupee) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <h4 className="h6 text-info mb-2">Partner investments</h4>
                  {(executiveSummary.investments || []).map((v) => (
                    <div key={v.ventureName} className="mb-3 p-3 rounded border border-secondary">
                      <div className="fw-semibold">{v.ventureName} — {formatInr(v.totalInvestedRupee)} total</div>
                      <div className="text-secondary small mb-2">Started {formatIsoDateIn(v.startedOnIso)}</div>
                      <ul className="small mb-0">
                        {(v.partners || []).map((p) => (
                          <li key={`${v.ventureName}-${p.name}`}>
                            <strong>{p.name}</strong>: {formatInr(p.amountRupee)}
                            {p.notes ? <span className="text-secondary"> — {p.notes}</span> : null}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                  {(executiveSummary.notes || []).length ? (
                    <ul className="small text-secondary mb-0">
                      {executiveSummary.notes.map((n, i) => (
                        <li key={`exec-note-${i}`}>{n}</li>
                      ))}
                    </ul>
                  ) : null}
                </>
              ) : (
                <p className="text-secondary small">Load summary after uploading finance data.</p>
              )}
            </div>
          </AnalyticsSubsectionPanel>

          <AnalyticsSubsectionPanel activeKey={bizTab} panelKey="profit">
            <ProfitAnalyticsPanel
              profitView={profitView}
              setProfitView={setProfitView}
              profitWindowMonths={profitWindowMonths}
              setProfitWindowMonths={setProfitWindowMonths}
              profitBusy={profitBusy}
              onRecompute={loadProfit}
              cashflowChart={cashflowChart}
              profitPack={profitPack}
              partybossLabels={labels}
              bookingCounts={bookingCounts}
              revenues={revenues}
            />
          </AnalyticsSubsectionPanel>

          <AnalyticsSubsectionPanel activeKey={bizTab} panelKey="partner">
            <div className="admin-console-card p-3 rounded border border-secondary">
              <h3 className="h6 text-info mb-2">Profit share for partners</h3>
              <p className="text-secondary small mb-3">
                Split positive profit between partners and business retention. Percentages are saved in admin settings (must total 100%).
              </p>
              <div className="row g-2 align-items-end mb-4 pb-3 border-bottom border-secondary">
                <div className="col-md-2">
                  <Form.Label className="text-secondary small">{partnerSettings.partner1Label || "Partner 1"} %</Form.Label>
                  <Form.Control size="sm" type="number" min={0} max={100} className="text-bg-dark border-secondary"
                    value={partnerSettings.partner1SharePercent}
                    onChange={(e) => setPartnerSettings((s) => ({ ...s, partner1SharePercent: Number(e.target.value) }))} />
                </div>
                <div className="col-md-2">
                  <Form.Label className="text-secondary small">{partnerSettings.partner2Label || "Partner 2"} %</Form.Label>
                  <Form.Control size="sm" type="number" min={0} max={100} className="text-bg-dark border-secondary"
                    value={partnerSettings.partner2SharePercent}
                    onChange={(e) => setPartnerSettings((s) => ({ ...s, partner2SharePercent: Number(e.target.value) }))} />
                </div>
                <div className="col-md-2">
                  <Form.Label className="text-secondary small">Business %</Form.Label>
                  <Form.Control size="sm" type="number" min={0} max={100} className="text-bg-dark border-secondary"
                    value={partnerSettings.businessRetentionPercent}
                    onChange={(e) => setPartnerSettings((s) => ({ ...s, businessRetentionPercent: Number(e.target.value) }))} />
                </div>
                <div className="col-md-2">
                  <Form.Label className="text-secondary small">Partner 1 name</Form.Label>
                  <Form.Control size="sm" className="text-bg-dark border-secondary"
                    value={partnerSettings.partner1Label || ""}
                    onChange={(e) => setPartnerSettings((s) => ({ ...s, partner1Label: e.target.value }))} />
                </div>
                <div className="col-md-2">
                  <Form.Label className="text-secondary small">Partner 2 name</Form.Label>
                  <Form.Control size="sm" className="text-bg-dark border-secondary"
                    value={partnerSettings.partner2Label || ""}
                    onChange={(e) => setPartnerSettings((s) => ({ ...s, partner2Label: e.target.value }))} />
                </div>
                <div className="col-md-2">
                  <Button size="sm" variant="success" disabled={partnerSettingsBusy} onClick={() => savePartnerSettings()}>
                    Save split %
                  </Button>
                </div>
              </div>
              <div className="d-flex flex-wrap gap-3 align-items-end mb-3">
                <div>
                  <Form.Label className="text-secondary small mb-1">View by</Form.Label>
                  <Form.Select
                    size="sm"
                    className="text-bg-dark border-secondary"
                    style={{ width: 160 }}
                    value={partnerGranularity}
                    onChange={(e) => setPartnerGranularity(e.target.value)}
                  >
                    <option value="MONTHLY">Monthly</option>
                    <option value="QUARTERLY">Quarterly</option>
                    <option value="HALF_YEARLY">Half-yearly</option>
                    <option value="YEARLY">Yearly</option>
                  </Form.Select>
                </div>
                <Button size="sm" variant="outline-light" disabled={partnerShareBusy} onClick={() => loadPartnerShare()}>
                  Refresh
                </Button>
              </div>
              {partnerShareBusy ? <Spinner size="sm" animation="border" className="text-light mb-2" /> : null}
              <div className="finance-recon-scroll">
                <Table striped bordered hover size="sm" variant="dark" responsive className="mb-0 finance-comparison-table">
                  <thead>
                    <tr>
                      <th>Period</th>
                      <th className="text-end">Money in</th>
                      <th className="text-end">Money out</th>
                      <th className="text-end">Net profit</th>
                      <th className="text-end">{partnerShare?.partner1Label || partnerSettings.partner1Label || "Partner 1"} ({partnerShare?.partner1SharePercent ?? partnerSettings.partner1SharePercent}%)</th>
                      <th className="text-end">{partnerShare?.partner2Label || partnerSettings.partner2Label || "Partner 2"} ({partnerShare?.partner2SharePercent ?? partnerSettings.partner2SharePercent}%)</th>
                      <th className="text-end">Business ({partnerShare?.businessRetentionPercent ?? partnerSettings.businessRetentionPercent}%)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(partnerShare?.periods || []).length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-secondary small">
                          Upload bank, salary, Petpooja data — then refresh. Need profit rows to calculate share.
                        </td>
                      </tr>
                    ) : (
                      partnerShare.periods.map((p) => (
                        <tr key={p.periodKey}>
                          <td className="fw-semibold">{p.displayLabel || p.periodKey}</td>
                          <td className="text-end text-info">{formatInr(p.totalRevenueRupee)}</td>
                          <td className="text-end text-warning">{formatInr(p.totalExpenseRupee)}</td>
                          <td className={`text-end fw-semibold ${p.lossPeriod ? "text-warning" : "text-success"}`}>
                            {formatInr(p.netProfitRupee)}
                            {p.lossPeriod ? " (loss)" : ""}
                          </td>
                          <td className="text-end">{p.lossPeriod ? "—" : formatInr(p.partner1ShareRupee)}</td>
                          <td className="text-end">{p.lossPeriod ? "—" : formatInr(p.partner2ShareRupee)}</td>
                          <td className="text-end">{p.lossPeriod ? "—" : formatInr(p.businessRetentionRupee)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
              <p className="text-secondary small mt-3 mb-0">
                Example: Q profit ₹40,000 → Partner 1 ₹12,000 + Partner 2 ₹12,000 + Business retention ₹16,000.
              </p>
            </div>
          </AnalyticsSubsectionPanel>

          <AnalyticsSubsectionPanel activeKey={bizTab} panelKey="register-otp">
            <div className="admin-console-card p-3 rounded border border-secondary">
              <h3 className="h6 text-info mb-2">Register email for OTP</h3>
              <p className="text-secondary small mb-3">
                Only registered inboxes may unlock booking analytics. <strong>Send OTP</strong> emails the same code to
                every active registered inbox. Successful verifications are counted in the Successful column.
              </p>
              <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
                <Form.Group style={{ minWidth: 220 }}>
                  <Form.Label className="text-white-50 small">Add email</Form.Label>
                  <Form.Control
                    type="email"
                    size="sm"
                    className="text-bg-dark border-secondary"
                    value={registerEmailDraft}
                    onChange={(e) => setRegisterEmailDraft(e.target.value)}
                    placeholder="colleague@company.com"
                  />
                </Form.Group>
                <Button
                  size="sm"
                  variant="outline-light"
                  disabled={accessBusy || !registerEmailDraft.trim()}
                  type="button"
                  onClick={async () => {
                    setAccessBusy(true);
                    try {
                      await axios.post(
                        Constants.ADMIN_BOOKING_ANALYTICS_ACCESS_EMAILS,
                        { email: registerEmailDraft.trim() },
                        { headers: adminOnlyHeaders }
                      );
                      setRegisterEmailDraft("");
                      await refreshAccessEmails();
                    } catch (e) {
                      const body = e?.response?.data;
                      alert(body?.message || (typeof body === "string" ? body : null) || e?.message || "Register failed.");
                    } finally {
                      setAccessBusy(false);
                    }
                  }}
                >
                  Register
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={accessBusy}
                  type="button"
                  onClick={() => refreshAccessEmails()}
                >
                  Refresh list
                </Button>
              </div>
              <Table striped bordered hover size="sm" variant="dark" responsive className="mb-0 admin-console-data-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th className="text-end">Successful OTP logins</th>
                    <th>Active</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {accessEmails.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-secondary small">
                        No emails registered yet. Run the PROD seed SQL or add an inbox above.
                      </td>
                    </tr>
                  ) : (
                    accessEmails.map((row) => (
                      <tr key={row.id}>
                        <td>{row.email}</td>
                        <td className="text-end">{row.successfulOtpGrantCount ?? 0}</td>
                        <td>{row.enabled ? "Yes" : "No"}</td>
                        <td className="text-end">
                          <div className="d-flex flex-wrap gap-1 justify-content-end">
                            <Button
                              size="sm"
                              variant="outline-info"
                              disabled={accessBusy || !row.enabled}
                              type="button"
                              onClick={() => sendAnalyticsOtp()}
                            >
                              Send OTP
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-warning"
                              disabled={accessBusy}
                              type="button"
                              onClick={async () => {
                                try {
                                  await axios.put(
                                    `${Constants.ADMIN_BOOKING_ANALYTICS_ACCESS_EMAILS}/${row.id}?enabled=${!row.enabled}`,
                                    {},
                                    { headers: adminOnlyHeaders }
                                  );
                                  refreshAccessEmails();
                                } catch (e) {
                                  alert(e?.message || "Toggle failed.");
                                }
                              }}
                            >
                              Toggle active
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </AnalyticsSubsectionPanel>
        </AnalyticsSubsectionLayout>
      </div>
    </AdminLayout>
  );
}
